'use client';
import { useState, useRef, useEffect, useCallback, useMemo, useContext, createContext } from 'react';
import ReactDOM from 'react-dom';
import { BaseApiClient, subscribeConfig, getConfig } from '@quantabit/sdk-config';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { OAuthIconGroup } from '@quantabit/oauth-sdk';
import { QBitConnection, broadcastQBitAnchor, verifyAnchoredMemo as verifyAnchoredMemo$1, buildQBitAnchorMemo as buildQBitAnchorMemo$2, createQBitAnchorTransaction } from '@quantabit/qbit-chain-sdk';

/**
 * QuantaBit Auth API Service
 * 
 * 核心认证 API 服务，支持多种登录方式：
 * - 钱包登录 (MetaMask, WalletConnect, Coinbase)
 * - DID 密码登录
 * - 邮箱注册登录
 * 
 * 使用 BaseApiClient 基类实现统一配置
 */


/**
 * 认证 API 客户端
 * 继承 BaseApiClient 并扩展认证特性
 */
class AuthApiClient extends BaseApiClient {
  constructor(config = {}) {
    super('', config); // 禁用自动模块路径，使用显式控制
    this.onTokenRefresh = config.onTokenRefresh;
    this.onAuthError = config.onAuthError;

    // 认证相关的存储 key（可配置）
    this._tokenKey = config.tokenStorageKey || 'qbit_did_access_token';
    this._refreshTokenKey = config.refreshTokenStorageKey || 'qbit_did_refresh_token';
    this._currentSessionKey = config.currentSessionKey || 'qbit_did_current';
  }

  /**
   * 配置 API 基础 URL 和回调
   * @param {Object} config - 配置对象
   * @param {string} config.apiBaseUrl - API 基础 URL
   */
  configure(config = {}) {
    if (config.apiBaseUrl) {
      this.setBaseUrl(config.apiBaseUrl);
    }
    this.configureCallbacks(config);
  }

  /**
   * 配置回调
   * @param {Object} config - 配置对象
   */
  configureCallbacks(config) {
    if (config.onTokenRefresh) this.onTokenRefresh = config.onTokenRefresh;
    if (config.onAuthError) this.onAuthError = config.onAuthError;
  }

  // ============ 认证方法 ============

  /**
   * 获取钱包签名 nonce
   */
  async getNonce(walletAddress, chainType = 'quantabit') {
    return this.post('/auth/nonce', {
      wallet_address: walletAddress,
      chain_type: chainType
    });
  }

  /**
   * 钱包登录
   */
  async walletLogin(walletAddress, signature, message, chainType = 'quantabit') {
    const result = await this.post('/auth/wallet', {
      wallet_address: walletAddress,
      signature,
      message,
      chain_type: chainType
    });
    if (result.access_token) {
      this.setTokens(result.access_token, result.refresh_token);
    }
    return result;
  }

  /**
   * DID 密码登录
   */
  async didLogin(did, password, pin = null, totpCode = null) {
    const result = await this.post('/auth/did-login', {
      did: did.trim(),
      password,
      pin,
      totp_code: totpCode
    });
    if (result.access_token) {
      this.setTokens(result.access_token, result.refresh_token);
    }
    return result;
  }

  /**
   * 邮箱注册
   */
  async register(data) {
    const result = await this.post('/auth/register', {
      display_name: data.displayName || data.nickname,
      email: data.email || undefined,
      password: data.password
    });
    if (result.access_token) {
      this.setTokens(result.access_token, result.refresh_token);
    }
    return result;
  }

  /**
   * 刷新 Token
   */
  async refreshAuthToken(refreshToken) {
    const result = await this.post('/auth/refresh', {
      refresh_token: refreshToken
    });
    if (result.access_token) {
      this.setTokens(result.access_token, result.refresh_token);
    }
    return result;
  }

  /**
   * 登出
   */
  async logout() {
    try {
      await this.post('/auth/logout', {});
    } catch (e) {
      // 忽略登出错误
    }
    this.clearTokens();
  }

  /**
   * 获取当前用户信息
   */
  async getMe() {
    return this.get('/profile/me');
  }
  async setPin(did, password, pin) {
    return this.post('/auth/set-pin', {
      did,
      password,
      pin
    });
  }

  // ============ Token 管理（扩展） ============

  /**
   * 获取刷新 Token
   */
  getRefreshToken() {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(this._refreshTokenKey);
  }

  /**
   * 设置 Tokens
   */
  setTokens(accessToken, refreshToken) {
    if (typeof window === 'undefined') return;

    // 使用可配置的 token 存储键
    localStorage.setItem(this._tokenKey, accessToken);
    if (refreshToken) {
      localStorage.setItem(this._refreshTokenKey, refreshToken);
    }
    this.onTokenRefresh?.({
      accessToken,
      refreshToken
    });
  }

  /**
   * 清除所有 Token
   */
  clearTokens() {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(this._tokenKey);
    localStorage.removeItem(this._refreshTokenKey);
    localStorage.removeItem(this._currentSessionKey);
  }

  /**
   * 检查是否已认证
   */
  isAuthenticated() {
    return !!this.getToken();
  }

  // ============ 多账户支持 ============

  /**
   * 获取当前会话标识
   */
  getCurrentSession() {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(this._currentSessionKey);
  }

  /**
   * 设置当前会话标识
   */
  setCurrentSession(sessionId) {
    if (typeof window === 'undefined') return;
    localStorage.setItem(this._currentSessionKey, sessionId);
  }
}

// 创建默认实例
const authApi = new AuthApiClient();

/**
 * QuantaBit Auth SDK - 图标组件
 * 
 * 内置 SVG 图标，无需外部依赖
 */

const ShieldIcon = ({
  size = 20,
  className = ''
}) => /*#__PURE__*/jsxs("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: [/*#__PURE__*/jsx("path", {
    d: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
  }), /*#__PURE__*/jsx("path", {
    d: "M9 12l2 2 4-4",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })]
});

// Lock 锁图标
const LockIcon = ({
  size = 16,
  className = ''
}) => /*#__PURE__*/jsxs("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: [/*#__PURE__*/jsx("rect", {
    x: "3",
    y: "11",
    width: "18",
    height: "11",
    rx: "2",
    ry: "2"
  }), /*#__PURE__*/jsx("circle", {
    cx: "12",
    cy: "16",
    r: "1"
  }), /*#__PURE__*/jsx("path", {
    d: "M7 11V7a5 5 0 0110 0v4"
  })]
});

// Verified 验证徽章
const VerifiedIcon = ({
  size = 14,
  className = ''
}) => /*#__PURE__*/jsx("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "#22c55e",
  className: className,
  children: /*#__PURE__*/jsx("path", {
    d: "M12 2L3.5 6.5v5.9c0 5.1 3.6 9.9 8.5 11.1 4.9-1.2 8.5-6 8.5-11.1V6.5L12 2zm-1 14l-4-4 1.4-1.4 2.6 2.6 5.6-5.6L18 9l-7 7z"
  })
});

// Wallet 钱包图标
const WalletIcon = ({
  size = 20,
  className = ''
}) => /*#__PURE__*/jsxs("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: [/*#__PURE__*/jsx("path", {
    d: "M20 12V8H6a2 2 0 01-2-2c0-1.1.9-2 2-2h12v4"
  }), /*#__PURE__*/jsx("path", {
    d: "M4 6v12a2 2 0 002 2h14a2 2 0 002-2v-6"
  }), /*#__PURE__*/jsx("circle", {
    cx: "18",
    cy: "12",
    r: "2"
  })]
});

// Mail 邮箱图标
const MailIcon = ({
  size = 20,
  className = ''
}) => /*#__PURE__*/jsxs("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: [/*#__PURE__*/jsx("path", {
    d: "M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
  }), /*#__PURE__*/jsx("polyline", {
    points: "22,6 12,13 2,6"
  })]
});

// Fingerprint 指纹图标
const FingerprintIcon = ({
  size = 20,
  className = ''
}) => /*#__PURE__*/jsxs("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: [/*#__PURE__*/jsx("path", {
    d: "M2 12C2 6.5 6.5 2 12 2a10 10 0 0 1 8 4"
  }), /*#__PURE__*/jsx("path", {
    d: "M5 19.5C5.5 18 6 15 6 12c0-.7.12-1.37.34-2"
  }), /*#__PURE__*/jsx("path", {
    d: "M17.29 21.02c.12-.6.43-2.3.5-3.02"
  }), /*#__PURE__*/jsx("path", {
    d: "M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4"
  }), /*#__PURE__*/jsx("path", {
    d: "M8.65 22c.21-.66.45-1.32.57-2"
  }), /*#__PURE__*/jsx("path", {
    d: "M14 13.12c0 2.38 0 6.38-1 8.88"
  }), /*#__PURE__*/jsx("path", {
    d: "M2 16h.01"
  }), /*#__PURE__*/jsx("path", {
    d: "M21.8 16c.2-2 .131-5.354 0-6"
  }), /*#__PURE__*/jsx("path", {
    d: "M9 6.8a6 6 0 0 1 9 5.2c0 .47 0 1.17-.02 2"
  })]
});

// User 用户图标
const UserIcon = ({
  size = 20,
  className = ''
}) => /*#__PURE__*/jsxs("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: [/*#__PURE__*/jsx("path", {
    d: "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
  }), /*#__PURE__*/jsx("circle", {
    cx: "12",
    cy: "7",
    r: "4"
  })]
});

// Loader 加载图标
const LoaderIcon = ({
  size = 20,
  className = ''
}) => /*#__PURE__*/jsx("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: `did-spin ${className}`,
  children: /*#__PURE__*/jsx("path", {
    d: "M21 12a9 9 0 1 1-6.219-8.56"
  })
});

// Check 勾选图标
const CheckIcon = ({
  size = 20,
  className = ''
}) => /*#__PURE__*/jsx("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: /*#__PURE__*/jsx("polyline", {
    points: "20 6 9 17 4 12"
  })
});

// Alert 警告图标
const AlertIcon = ({
  size = 20,
  className = ''
}) => /*#__PURE__*/jsxs("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: [/*#__PURE__*/jsx("circle", {
    cx: "12",
    cy: "12",
    r: "10"
  }), /*#__PURE__*/jsx("line", {
    x1: "12",
    y1: "8",
    x2: "12",
    y2: "12"
  }), /*#__PURE__*/jsx("line", {
    x1: "12",
    y1: "16",
    x2: "12.01",
    y2: "16"
  })]
});

// ArrowRight 箭头图标
const ArrowRightIcon = ({
  size = 20,
  className = ''
}) => /*#__PURE__*/jsxs("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: [/*#__PURE__*/jsx("line", {
    x1: "5",
    y1: "12",
    x2: "19",
    y2: "12"
  }), /*#__PURE__*/jsx("polyline", {
    points: "12 5 19 12 12 19"
  })]
});

// Eye 眼睛图标
const EyeIcon = ({
  size = 18,
  className = ''
}) => /*#__PURE__*/jsxs("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: [/*#__PURE__*/jsx("path", {
    d: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
  }), /*#__PURE__*/jsx("circle", {
    cx: "12",
    cy: "12",
    r: "3"
  })]
});

// EyeOff 隐藏眼睛图标
const EyeOffIcon = ({
  size = 18,
  className = ''
}) => /*#__PURE__*/jsxs("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: [/*#__PURE__*/jsx("path", {
    d: "M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
  }), /*#__PURE__*/jsx("line", {
    x1: "1",
    y1: "1",
    x2: "23",
    y2: "23"
  })]
});

// Close 关闭图标
const CloseIcon = ({
  size = 20,
  className = ''
}) => /*#__PURE__*/jsx("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: /*#__PURE__*/jsx("path", {
    d: "M18 6L6 18M6 6l12 12"
  })
});

// Swap 切换图标
const SwapIcon = ({
  size = 18,
  className = ''
}) => /*#__PURE__*/jsxs("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: [/*#__PURE__*/jsx("polyline", {
    points: "16 3 21 3 21 8"
  }), /*#__PURE__*/jsx("line", {
    x1: "4",
    y1: "20",
    x2: "21",
    y2: "3"
  }), /*#__PURE__*/jsx("polyline", {
    points: "21 16 21 21 16 21"
  }), /*#__PURE__*/jsx("line", {
    x1: "15",
    y1: "15",
    x2: "21",
    y2: "21"
  })]
});

// Plus 加号图标
const PlusIcon = ({
  size = 18,
  className = ''
}) => /*#__PURE__*/jsxs("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: [/*#__PURE__*/jsx("line", {
    x1: "12",
    y1: "5",
    x2: "12",
    y2: "19"
  }), /*#__PURE__*/jsx("line", {
    x1: "5",
    y1: "12",
    x2: "19",
    y2: "12"
  })]
});

// Trash 删除图标
const TrashIcon = ({
  size = 16,
  className = ''
}) => /*#__PURE__*/jsxs("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: [/*#__PURE__*/jsx("polyline", {
    points: "3 6 5 6 21 6"
  }), /*#__PURE__*/jsx("path", {
    d: "M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
  }), /*#__PURE__*/jsx("line", {
    x1: "10",
    y1: "11",
    x2: "10",
    y2: "17"
  }), /*#__PURE__*/jsx("line", {
    x1: "14",
    y1: "11",
    x2: "14",
    y2: "17"
  })]
});

// Logout 登出图标
const LogoutIcon = ({
  size = 18,
  className = ''
}) => /*#__PURE__*/jsxs("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: [/*#__PURE__*/jsx("path", {
    d: "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"
  }), /*#__PURE__*/jsx("polyline", {
    points: "16 17 21 12 16 7"
  }), /*#__PURE__*/jsx("line", {
    x1: "21",
    y1: "12",
    x2: "9",
    y2: "12"
  })]
});

// ============ Wallet Icons ============

const MetaMaskIcon$1 = () => /*#__PURE__*/jsxs("svg", {
  width: "32",
  height: "32",
  viewBox: "0 0 32 32",
  children: [/*#__PURE__*/jsx("path", {
    fill: "#E17726",
    d: "M27.2 4L16.8 11.6l1.9-4.5z"
  }), /*#__PURE__*/jsx("path", {
    fill: "#E27625",
    d: "M4.8 4l10.3 7.7-1.8-5.2zM23.5 21.5l-2.7 6 5.8-1.9-.7-4.1zM5.4 25.6l5.8 1.9-2.7-6z"
  }), /*#__PURE__*/jsx("path", {
    fill: "#E27625",
    d: "M10.9 14l-2.4 3.5 8.5.4-.3-9.2zM21.1 14l-5.7-5.3-.2 9.2 8.5-.4z"
  }), /*#__PURE__*/jsx("path", {
    fill: "#D5BFB2",
    d: "M11.2 27.5l5-2.5-4.3-3.4zM15.8 25l5 2.5-.7-5.9z"
  }), /*#__PURE__*/jsx("path", {
    fill: "#233447",
    d: "M20.8 27.5l-5-2.5.4 3.3-.1 1.4zM11.2 27.5l4.7 2.2-.1-1.4.4-3.3z"
  }), /*#__PURE__*/jsx("path", {
    fill: "#CD6116",
    d: "M16 19.8l-4.2-1.2 3-.5zM16 19.8l1.2-1.7 3 .5z"
  }), /*#__PURE__*/jsx("path", {
    fill: "#E27625",
    d: "M11.2 27.5l.7-6-3.4.1zM20.1 21.5l.7 6 2.7-5.9zM23.5 17.5l-8.5.4.8 4.9 1.2-1.7 3 .5zM11.8 18.6l3-.5 1.2 1.7.8-4.9-8.5-.4z"
  }), /*#__PURE__*/jsx("path", {
    fill: "#F6851B",
    d: "M8.5 17.5l3.5 6.9-.2-3.8zM20.2 20.6l-.2 3.8 3.5-6.9zM17 17.9l-.8 4.9 1 5.2.2-6.9zM15 17.9l-.4 3.2.2 6.9 1-5.2z"
  }), /*#__PURE__*/jsx("path", {
    fill: "#C0AD9E",
    d: "M15.9 29.7l.1-1.4-.4-.3h-4l-.4.3.1 1.4-4.7-2.2 1.6 1.3 3.3 2.3h4.1l3.3-2.3 1.6-1.3z"
  }), /*#__PURE__*/jsx("path", {
    fill: "#161616",
    d: "M15.8 25l-5-2.5-.7-.6.4-.3 4.3 3.4zM16.2 25l5-2.5-.7.6.4.3-4.3-3.4z"
  })]
});
const WalletConnectIcon$1 = () => /*#__PURE__*/jsxs("svg", {
  width: "32",
  height: "32",
  viewBox: "0 0 32 32",
  children: [/*#__PURE__*/jsx("circle", {
    cx: "16",
    cy: "16",
    r: "14",
    fill: "#3B99FC"
  }), /*#__PURE__*/jsx("path", {
    fill: "white",
    d: "M10.5 12.5c3-3 7.9-3 10.9 0l.4.4c.2.1.2.4 0 .5l-1.2 1.2c0 .1-.1.1-.2 0l-.5-.5c-2.1-2-5.5-2-7.6 0l-.5.5c-.1.1-.2.1-.3 0l-1.2-1.2c-.1-.1-.1-.4 0-.5l.2-.4zm13.5 2.5l1.1 1c.1.2.1.4 0 .5l-5 4.8c-.1.2-.4.2-.5 0l-3.6-3.4c0 0-.1 0-.1 0l-3.6 3.4c-.1.2-.4.2-.5 0l-5-4.8c-.1-.1-.1-.3 0-.5l1.1-1c.1-.2.4-.2.5 0l3.6 3.4c0 0 .1 0 .1 0l3.6-3.4c.1-.2.4-.2.5 0l3.6 3.4c0 0 .1 0 .1 0l3.6-3.4c.1-.2.4-.2.5 0z"
  })]
});
const CoinbaseIcon = () => /*#__PURE__*/jsxs("svg", {
  width: "32",
  height: "32",
  viewBox: "0 0 32 32",
  children: [/*#__PURE__*/jsx("circle", {
    cx: "16",
    cy: "16",
    r: "14",
    fill: "#0052FF"
  }), /*#__PURE__*/jsx("circle", {
    cx: "16",
    cy: "16",
    r: "8",
    fill: "white"
  }), /*#__PURE__*/jsx("circle", {
    cx: "16",
    cy: "16",
    r: "4",
    fill: "#0052FF"
  })]
});
const QBitIcon = () => /*#__PURE__*/jsxs("svg", {
  viewBox: "0 0 40 40",
  width: "32",
  height: "32",
  children: [/*#__PURE__*/jsx("defs", {
    children: /*#__PURE__*/jsxs("linearGradient", {
      id: "qbit-grad-icon",
      x1: "0%",
      y1: "0%",
      x2: "100%",
      y2: "100%",
      children: [/*#__PURE__*/jsx("stop", {
        offset: "0%",
        stopColor: "#7C3AED"
      }), /*#__PURE__*/jsx("stop", {
        offset: "100%",
        stopColor: "#6366F1"
      })]
    })
  }), /*#__PURE__*/jsx("rect", {
    width: "40",
    height: "40",
    rx: "10",
    fill: "url(#qbit-grad-icon)"
  }), /*#__PURE__*/jsx("circle", {
    cx: "20",
    cy: "20",
    r: "8",
    fill: "none",
    stroke: "#fff",
    strokeWidth: "2.5"
  }), /*#__PURE__*/jsx("circle", {
    cx: "20",
    cy: "20",
    r: "3",
    fill: "#fff"
  }), /*#__PURE__*/jsx("circle", {
    cx: "25",
    cy: "25",
    r: "2.5",
    fill: "url(#qbit-grad-icon)",
    stroke: "#fff",
    strokeWidth: "1.5"
  })]
});
const TrustWalletIcon$1 = () => /*#__PURE__*/jsxs("svg", {
  viewBox: "0 0 40 40",
  width: "32",
  height: "32",
  children: [/*#__PURE__*/jsx("rect", {
    width: "40",
    height: "40",
    rx: "10",
    fill: "#0500FF"
  }), /*#__PURE__*/jsx("path", {
    d: "M20 10c0 0 7.5 3.5 7.5 10.5 0 7-7.5 11.5-7.5 11.5s-7.5-4.5-7.5-11.5C12.5 13.5 20 10 20 10z",
    fill: "none",
    stroke: "#fff",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/jsx("path", {
    d: "M20 14c0 0 4.5 2.5 4.5 6.5 0 4-4.5 7-4.5 7s-4.5-3-4.5-7C15.5 16.5 20 14 20 14z",
    fill: "#fff",
    opacity: "0.3"
  })]
});

// Zap 闪电图标
const ZapIcon = ({
  size = 16,
  className = ''
}) => /*#__PURE__*/jsx("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "currentColor",
  className: className,
  children: /*#__PURE__*/jsx("polygon", {
    points: "13 2 3 14 12 14 11 22 21 10 12 10 13 2"
  })
});

// ChevronDown 下拉箭头
const ChevronDownIcon = ({
  size = 16,
  className = ''
}) => /*#__PURE__*/jsx("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  style: {
    transition: 'transform 0.2s ease'
  },
  children: /*#__PURE__*/jsx("polyline", {
    points: "6 9 12 15 18 9"
  })
});

// Settings 设置图标
const SettingsIcon = ({
  size = 18,
  className = ''
}) => /*#__PURE__*/jsxs("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: [/*#__PURE__*/jsx("circle", {
    cx: "12",
    cy: "12",
    r: "3"
  }), /*#__PURE__*/jsx("path", {
    d: "M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"
  })]
});

// IdCard 身份证图标
const IdCardIcon = ({
  size = 18,
  className = ''
}) => /*#__PURE__*/jsxs("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: [/*#__PURE__*/jsx("rect", {
    x: "2",
    y: "4",
    width: "20",
    height: "16",
    rx: "2"
  }), /*#__PURE__*/jsx("circle", {
    cx: "8",
    cy: "10",
    r: "2"
  }), /*#__PURE__*/jsx("path", {
    d: "M8 14a3 3 0 0 0-3 3h6a3 3 0 0 0-3-3z"
  }), /*#__PURE__*/jsx("line", {
    x1: "14",
    y1: "9",
    x2: "20",
    y2: "9"
  }), /*#__PURE__*/jsx("line", {
    x1: "14",
    y1: "13",
    x2: "18",
    y2: "13"
  })]
});

// Star 星星图标
const StarIcon = ({
  size = 18,
  className = ''
}) => /*#__PURE__*/jsx("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: /*#__PURE__*/jsx("polygon", {
    points: "12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
  })
});

// Key 钥匙图标
const KeyIcon = ({
  size = 18,
  className = ''
}) => /*#__PURE__*/jsx("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: /*#__PURE__*/jsx("path", {
    d: "M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"
  })
});

// Expand 全屏展开图标
const ExpandIcon = ({
  size = 18,
  className = ''
}) => /*#__PURE__*/jsx("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: /*#__PURE__*/jsx("path", {
    d: "M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"
  })
});

// Maximize 最大化图标
const MaximizeIcon = ({
  size = 18,
  className = ''
}) => /*#__PURE__*/jsx("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: /*#__PURE__*/jsx("path", {
    d: "M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"
  })
});

// Palette 调色板图标
const PaletteIcon = ({
  size = 18,
  className = ''
}) => /*#__PURE__*/jsxs("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: [/*#__PURE__*/jsx("circle", {
    cx: "13.5",
    cy: "6.5",
    r: "0.5",
    fill: "currentColor"
  }), /*#__PURE__*/jsx("circle", {
    cx: "17.5",
    cy: "10.5",
    r: "0.5",
    fill: "currentColor"
  }), /*#__PURE__*/jsx("circle", {
    cx: "8.5",
    cy: "7.5",
    r: "0.5",
    fill: "currentColor"
  }), /*#__PURE__*/jsx("circle", {
    cx: "6.5",
    cy: "12.5",
    r: "0.5",
    fill: "currentColor"
  }), /*#__PURE__*/jsx("path", {
    d: "M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.555C21.965 6.012 17.461 2 12 2z"
  })]
});

// Sun 太阳图标 (浅色主题)
const SunIcon = ({
  size = 18,
  className = ''
}) => /*#__PURE__*/jsxs("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: [/*#__PURE__*/jsx("circle", {
    cx: "12",
    cy: "12",
    r: "5"
  }), /*#__PURE__*/jsx("line", {
    x1: "12",
    y1: "1",
    x2: "12",
    y2: "3"
  }), /*#__PURE__*/jsx("line", {
    x1: "12",
    y1: "21",
    x2: "12",
    y2: "23"
  }), /*#__PURE__*/jsx("line", {
    x1: "4.22",
    y1: "4.22",
    x2: "5.64",
    y2: "5.64"
  }), /*#__PURE__*/jsx("line", {
    x1: "18.36",
    y1: "18.36",
    x2: "19.78",
    y2: "19.78"
  }), /*#__PURE__*/jsx("line", {
    x1: "1",
    y1: "12",
    x2: "3",
    y2: "12"
  }), /*#__PURE__*/jsx("line", {
    x1: "21",
    y1: "12",
    x2: "23",
    y2: "12"
  }), /*#__PURE__*/jsx("line", {
    x1: "4.22",
    y1: "19.78",
    x2: "5.64",
    y2: "18.36"
  }), /*#__PURE__*/jsx("line", {
    x1: "18.36",
    y1: "5.64",
    x2: "19.78",
    y2: "4.22"
  })]
});

// Moon 月亮图标 (深色主题)
const MoonIcon = ({
  size = 18,
  className = ''
}) => /*#__PURE__*/jsx("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: /*#__PURE__*/jsx("path", {
    d: "M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
  })
});

// Layout 布局图标 (切换模式)
const LayoutIcon = ({
  size = 18,
  className = ''
}) => /*#__PURE__*/jsxs("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: [/*#__PURE__*/jsx("rect", {
    x: "3",
    y: "3",
    width: "18",
    height: "18",
    rx: "2",
    ry: "2"
  }), /*#__PURE__*/jsx("line", {
    x1: "12",
    y1: "3",
    x2: "12",
    y2: "21"
  })]
});

// Minimize 最小化图标 (简化模式)
const MinimizeIcon = ({
  size = 18,
  className = ''
}) => /*#__PURE__*/jsxs("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: [/*#__PURE__*/jsx("rect", {
    x: "3",
    y: "3",
    width: "18",
    height: "18",
    rx: "2",
    ry: "2"
  }), /*#__PURE__*/jsx("line", {
    x1: "8",
    y1: "12",
    x2: "16",
    y2: "12"
  })]
});

/**
 * QuantaBit Auth SDK - Internationalization (i18n)
 * 国际化支持模块
 * 
 * 使用浏览器全局事件系统实现语言同步
 * 不直接依赖 @quantabit/sdk-config 模块导入
 * 
 * 语言格式兼容：同时支持 'zh-CN' / 'zh' 两种格式
 * 内部统一使用短格式 (zh/en/ja/ko)，对外接受任意格式
 */

// 全局语言事件名称（与 sdk-config 保持一致）
const LANGUAGE_CHANGE_EVENT = 'qbit-did:language-change';
const LANGUAGE_STORAGE_KEY = 'qbit_did_language';

// 内部支持的语言短码
const INTERNAL_LANGUAGES = ['en', 'zh', 'ja', 'ko'];

// 对外支持的语言列表（同时接受长短格式）
const SUPPORTED_LANGUAGES = ['en', 'en-US', 'en-GB', 'zh', 'zh-CN', 'zh-TW', 'zh-HK', 'ja', 'ja-JP', 'ko', 'ko-KR'];

/**
 * 语言格式归一化
 * 将 'zh-CN' / 'zh-TW' / 'zh-HK' 等映射到内部短码 'zh'
 * 将 'en-US' / 'en-GB' 等映射到 'en'
 * 同时兼容直接传入短码 'zh' / 'en'
 * 
 * @param {string} lang - 任意格式的语言代码
 * @returns {string} 归一化后的短码 (zh/en/ja/ko)
 */
function normalizeLanguage(lang) {
  if (!lang || typeof lang !== 'string') return 'zh';
  const lower = lang.toLowerCase().trim();
  if (lower.startsWith('zh')) return 'zh';
  if (lower.startsWith('ja')) return 'ja';
  if (lower.startsWith('ko')) return 'ko';
  if (lower.startsWith('en')) return 'en';
  // 如果是已知短码直接返回
  if (INTERNAL_LANGUAGES.includes(lower)) return lower;
  return 'en';
}

// 语言信息
const LANGUAGE_INFO = {
  en: {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    flag: '🇺🇸'
  },
  zh: {
    code: 'zh',
    name: 'Chinese',
    nativeName: '中文',
    flag: '🇨🇳'
  },
  ja: {
    code: 'ja',
    name: 'Japanese',
    nativeName: '日本語',
    flag: '🇯🇵'
  },
  ko: {
    code: 'ko',
    name: 'Korean',
    nativeName: '한국어',
    flag: '🇰🇷'
  }
};

// 翻译消息
const messages = {
  zh: {
    // 标题和通用
    title: '安全连接到',
    subtitle: '选择您的身份验证方式',
    login: '登录',
    register: '注册',
    logout: '退出登录',
    back: '返回',
    cancel: '取消',
    confirm: '确认',
    save: '保存',
    close: '关闭',
    loading: '加载中...',
    success: '操作成功',
    error: '操作失败',
    // 语言切换
    language: '语言',
    switchLanguage: '切换语言',
    // 登录相关
    walletSection: '钱包登录',
    didSection: 'DID 登录',
    registerSection: '创建新身份',
    accountsSection: '账户管理',
    connecting: '连接中...',
    signing: '请在钱包中签名...',
    verifying: '验证签名...',
    // 钱包
    metamask: 'MetaMask',
    walletConnect: 'WalletConnect',
    coinbaseWallet: 'Coinbase Wallet',
    quantabitWallet: 'QBit 钱包',
    connectWallet: '连接钱包',
    connectDescription: '连接您的 Web3 钱包，安全验证您的去中心化身份',
    supportedWallets: '支持的钱包',
    noWallet: '未检测到钱包，请安装 MetaMask',
    noAccount: '未获取到账户，请解锁您的钱包',
    noWalletYet: '还没有钱包？',
    getWallet: '获取一个',
    // 表单
    didPlaceholder: 'DID 或用户名',
    passwordPlaceholder: '密码',
    confirmPasswordPlaceholder: '确认密码',
    emailPlaceholder: '邮箱 (可选)',
    nicknamePlaceholder: '昵称',
    loginButton: '安全登录',
    registerButton: '创建身份',
    loggingIn: '登录中...',
    registering: '创建中...',
    // 账户管理
    savedAccounts: '已验证账号',
    quickLogin: '快速登录',
    relogin: '重新登录',
    switchAccount: '切换账户',
    manageAccounts: '管理账户',
    addNewAccount: '添加新账户',
    removeAccount: '移除账户',
    removeConfirm: '确定移除此账户？',
    accountRemoved: '账户已移除',
    currentAccount: '当前账户',
    otherAccounts: '其他账户',
    noOtherAccounts: '没有其他保存的账户',
    // 导航
    orDivider: '或',
    newToDID: '还没有数字身份？',
    createDID: '创建一个',
    alreadyHaveDID: '已有身份？',
    goLogin: '去登录',
    noAccountYet: '还没有账号？',
    hasAccount: '已有账号？',
    // 错误消息
    errorNoWallet: '未检测到钱包，请安装 MetaMask',
    errorLoginFailed: '登录失败，请检查凭证',
    errorRegisterFailed: '注册失败，请重试',
    errorPasswordMismatch: '两次密码输入不一致',
    errorPasswordTooShort: '密码至少6位',
    errorNicknameRequired: '请输入昵称',
    errorDIDRequired: '请输入 DID',
    errorPasswordRequired: '请输入密码',
    rejected: '用户拒绝了签名请求',
    // 安全相关
    poweredBy: '由 QuantaBit 提供去中心化身份服务',
    securityNote: '端到端加密 · 私钥永不离开您的设备',
    verifiedBadge: '已验证',
    encryptedConnection: '加密连接',
    decentralizedAuth: '去中心化认证',
    // 功能特性
    feature1: '数据完全自主掌控',
    feature2: 'W3C 标准身份',
    feature3: '跨链通用身份',
    feature4: '隐私计算保护',
    // 注册页面
    createIdentity: '创建数字身份',
    chooseMethod: '选择您的注册方式，开启去中心化之旅',
    walletMethod: '钱包连接',
    emailMethod: '邮箱注册',
    walletDesc: '连接您的 Web3 钱包，我们将为您创建一个与钱包地址绑定的 DID。',
    nickname: '昵称',
    email: '邮箱',
    password: '密码',
    confirmPassword: '确认密码',
    createAccount: '创建账户',
    welcome: '欢迎加入',
    successDesc: '您的去中心化身份已创建成功',
    autoWallet: '自动为您创建钱包并关联',
    didIdentifier: 'DID 标识符',
    recommended: '推荐',
    redirecting: '正在跳转...',
    // 社交绑定
    socialBindings: '社交账号绑定',
    bindAccount: '绑定账号',
    unbindAccount: '解绑账号',
    verifiedAccount: '已验证账号',
    // 声誉系统
    reputation: '声誉分数',
    reputationLevel: '声誉等级',
    badges: '徽章',
    leaderboard: '排行榜',
    // 凭证
    credentials: '可验证凭证',
    issueCredential: '签发凭证',
    verifyCredential: '验证凭证',
    // 钱包绑定
    walletBindings: '钱包绑定',
    primaryWallet: '主钱包',
    addWallet: '添加钱包'
  },
  en: {
    // Title and General
    title: 'Securely Connect to',
    subtitle: 'Choose your authentication method',
    login: 'Login',
    register: 'Sign Up',
    logout: 'Logout',
    back: 'Back',
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    close: 'Close',
    loading: 'Loading...',
    success: 'Success',
    error: 'Error',
    // Language Switch
    language: 'Language',
    switchLanguage: 'Switch Language',
    // Login Related
    walletSection: 'Wallet Login',
    didSection: 'DID Login',
    registerSection: 'Create New Identity',
    accountsSection: 'Account Management',
    connecting: 'Connecting...',
    signing: 'Please sign in wallet...',
    verifying: 'Verifying signature...',
    // Wallets
    metamask: 'MetaMask',
    walletConnect: 'WalletConnect',
    coinbaseWallet: 'Coinbase Wallet',
    quantabitWallet: 'QBit Wallet',
    connectWallet: 'Connect Wallet',
    connectDescription: 'Connect your Web3 wallet to securely verify your decentralized identity',
    supportedWallets: 'Supported wallets',
    noWallet: 'No wallet detected. Please install MetaMask',
    noAccount: 'No account found. Please unlock your wallet',
    noWalletYet: "Don't have a wallet?",
    getWallet: 'Get one',
    // Forms
    didPlaceholder: 'DID or username',
    passwordPlaceholder: 'Password',
    confirmPasswordPlaceholder: 'Confirm password',
    emailPlaceholder: 'Email (optional)',
    nicknamePlaceholder: 'Nickname',
    loginButton: 'Secure Login',
    registerButton: 'Create Identity',
    loggingIn: 'Logging in...',
    registering: 'Creating...',
    // Account Management
    savedAccounts: 'Verified Accounts',
    quickLogin: 'Quick Login',
    relogin: 'Re-login',
    switchAccount: 'Switch Account',
    manageAccounts: 'Manage Accounts',
    addNewAccount: 'Add New Account',
    removeAccount: 'Remove Account',
    removeConfirm: 'Remove this account?',
    accountRemoved: 'Account removed',
    currentAccount: 'Current Account',
    otherAccounts: 'Other Accounts',
    noOtherAccounts: 'No other saved accounts',
    // Navigation
    orDivider: 'or',
    newToDID: 'New to digital identity?',
    createDID: 'Create one',
    alreadyHaveDID: 'Already have identity?',
    goLogin: 'Login',
    noAccountYet: "Don't have an account?",
    hasAccount: 'Already have an account?',
    // Error Messages
    errorNoWallet: 'No wallet detected. Please install MetaMask',
    errorLoginFailed: 'Login failed. Check your credentials',
    errorRegisterFailed: 'Registration failed. Please try again',
    errorPasswordMismatch: 'Passwords do not match',
    errorPasswordTooShort: 'Password must be at least 6 characters',
    errorNicknameRequired: 'Please enter nickname',
    errorDIDRequired: 'Please enter DID',
    errorPasswordRequired: 'Please enter password',
    rejected: 'User rejected the signature request',
    // Security Related
    poweredBy: 'Decentralized identity by QuantaBit',
    securityNote: 'End-to-end encrypted · Private keys never leave your device',
    verifiedBadge: 'Verified',
    encryptedConnection: 'Encrypted',
    decentralizedAuth: 'Decentralized Auth',
    // Features
    feature1: 'Full data sovereignty',
    feature2: 'W3C Standard Identity',
    feature3: 'Cross-chain Universal ID',
    feature4: 'Privacy Computing',
    // Register Page
    createIdentity: 'Create Digital Identity',
    chooseMethod: 'Choose your registration method to start your decentralized journey',
    walletMethod: 'Wallet Connect',
    emailMethod: 'Email Register',
    walletDesc: 'Connect your Web3 wallet and we will create a DID bound to your wallet address.',
    nickname: 'Nickname',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    createAccount: 'Create Account',
    welcome: 'Welcome to',
    successDesc: 'Your decentralized identity has been created successfully',
    autoWallet: 'Wallet automatically created and linked',
    didIdentifier: 'DID Identifier',
    recommended: 'Recommended',
    redirecting: 'Redirecting...',
    // Social Bindings
    socialBindings: 'Social Account Bindings',
    bindAccount: 'Bind Account',
    unbindAccount: 'Unbind Account',
    verifiedAccount: 'Verified Account',
    // Reputation System
    reputation: 'Reputation Score',
    reputationLevel: 'Reputation Level',
    badges: 'Badges',
    leaderboard: 'Leaderboard',
    // Credentials
    credentials: 'Verifiable Credentials',
    issueCredential: 'Issue Credential',
    verifyCredential: 'Verify Credential',
    // Wallet Bindings
    walletBindings: 'Wallet Bindings',
    primaryWallet: 'Primary Wallet',
    addWallet: 'Add Wallet'
  },
  ja: {
    // タイトルと一般
    title: '安全に接続',
    subtitle: '認証方法を選択してください',
    login: 'ログイン',
    register: '新規登録',
    logout: 'ログアウト',
    back: '戻る',
    cancel: 'キャンセル',
    confirm: '確認',
    save: '保存',
    close: '閉じる',
    loading: '読み込み中...',
    success: '成功',
    error: 'エラー',
    // 言語切替
    language: '言語',
    switchLanguage: '言語を切り替え',
    // ログイン関連
    walletSection: 'ウォレットログイン',
    didSection: 'DID ログイン',
    registerSection: '新しいIDを作成',
    accountsSection: 'アカウント管理',
    connecting: '接続中...',
    signing: 'ウォレットで署名してください...',
    verifying: '署名を確認中...',
    // ウォレット
    metamask: 'MetaMask',
    walletConnect: 'WalletConnect',
    coinbaseWallet: 'Coinbase Wallet',
    quantabitWallet: 'QBit ウォレット',
    connectWallet: 'ウォレット接続',
    connectDescription: 'Web3ウォレットを接続して、分散型IDを安全に確認',
    supportedWallets: '対応ウォレット',
    noWallet: 'ウォレットが検出されません。MetaMaskをインストールしてください',
    noAccount: 'アカウントが見つかりません。ウォレットのロックを解除してください',
    noWalletYet: 'ウォレットをお持ちでないですか？',
    getWallet: '取得する',
    // フォーム
    didPlaceholder: 'DID またはユーザー名',
    passwordPlaceholder: 'パスワード',
    confirmPasswordPlaceholder: 'パスワード確認',
    emailPlaceholder: 'メール (任意)',
    nicknamePlaceholder: 'ニックネーム',
    loginButton: '安全にログイン',
    registerButton: 'IDを作成',
    loggingIn: 'ログイン中...',
    registering: '作成中...',
    // アカウント管理
    savedAccounts: '確認済みアカウント',
    quickLogin: 'クイックログイン',
    relogin: '再ログイン',
    switchAccount: 'アカウント切替',
    manageAccounts: 'アカウント管理',
    addNewAccount: '新しいアカウントを追加',
    removeAccount: 'アカウントを削除',
    removeConfirm: 'このアカウントを削除しますか？',
    accountRemoved: 'アカウントが削除されました',
    currentAccount: '現在のアカウント',
    otherAccounts: '他のアカウント',
    noOtherAccounts: '他の保存されたアカウントはありません',
    // ナビゲーション
    orDivider: 'または',
    newToDID: 'デジタルIDは初めてですか？',
    createDID: '作成する',
    alreadyHaveDID: 'すでにIDをお持ちですか？',
    goLogin: 'ログインへ',
    noAccountYet: 'アカウントをお持ちでないですか？',
    hasAccount: 'すでにアカウントをお持ちですか？',
    // エラーメッセージ
    errorNoWallet: 'ウォレットが検出されません',
    errorLoginFailed: 'ログインに失敗しました',
    errorRegisterFailed: '登録に失敗しました',
    errorPasswordMismatch: 'パスワードが一致しません',
    errorPasswordTooShort: 'パスワードは6文字以上必要です',
    errorNicknameRequired: 'ニックネームを入力してください',
    errorDIDRequired: 'DIDを入力してください',
    errorPasswordRequired: 'パスワードを入力してください',
    rejected: 'ユーザーが署名リクエストを拒否しました',
    // セキュリティ
    poweredBy: 'QuantaBitによる分散型ID',
    securityNote: 'エンドツーエンド暗号化 · 秘密鍵はデバイスから離れません',
    verifiedBadge: '確認済み',
    encryptedConnection: '暗号化接続',
    decentralizedAuth: '分散型認証',
    // 機能
    feature1: '完全なデータ主権',
    feature2: 'W3C標準ID',
    feature3: 'クロスチェーンユニバーサルID',
    feature4: 'プライバシー計算',
    // 登録ページ
    createIdentity: 'デジタルIDを作成',
    chooseMethod: '登録方法を選択して、分散型の旅を始めましょう',
    walletMethod: 'ウォレット接続',
    emailMethod: 'メール登録',
    walletDesc: 'Web3ウォレットを接続すると、ウォレットアドレスに紐づくDIDを作成します。',
    nickname: 'ニックネーム',
    email: 'メール',
    password: 'パスワード',
    confirmPassword: 'パスワード確認',
    createAccount: 'アカウント作成',
    welcome: 'ようこそ',
    successDesc: '分散型IDが正常に作成されました',
    autoWallet: 'ウォレットが自動的に作成され、リンクされました',
    didIdentifier: 'DID識別子',
    recommended: 'おすすめ',
    redirecting: 'リダイレクト中...',
    // ソーシャル連携
    socialBindings: 'ソーシャルアカウント連携',
    bindAccount: 'アカウント連携',
    unbindAccount: '連携解除',
    verifiedAccount: '確認済みアカウント',
    // レピュテーション
    reputation: 'レピュテーションスコア',
    reputationLevel: 'レピュテーションレベル',
    badges: 'バッジ',
    leaderboard: 'リーダーボード',
    // クレデンシャル
    credentials: '検証可能なクレデンシャル',
    issueCredential: 'クレデンシャル発行',
    verifyCredential: 'クレデンシャル検証',
    // ウォレット連携
    walletBindings: 'ウォレット連携',
    primaryWallet: 'メインウォレット',
    addWallet: 'ウォレット追加'
  },
  ko: {
    // 제목 및 일반
    title: '안전하게 연결',
    subtitle: '인증 방법을 선택하세요',
    login: '로그인',
    register: '회원가입',
    logout: '로그아웃',
    back: '뒤로',
    cancel: '취소',
    confirm: '확인',
    save: '저장',
    close: '닫기',
    loading: '로딩 중...',
    success: '성공',
    error: '오류',
    // 언어 전환
    language: '언어',
    switchLanguage: '언어 전환',
    // 로그인 관련
    walletSection: '지갑 로그인',
    didSection: 'DID 로그인',
    registerSection: '새 ID 생성',
    accountsSection: '계정 관리',
    connecting: '연결 중...',
    signing: '지갑에서 서명해 주세요...',
    verifying: '서명 확인 중...',
    // 지갑
    metamask: 'MetaMask',
    walletConnect: 'WalletConnect',
    coinbaseWallet: 'Coinbase Wallet',
    quantabitWallet: 'QBit 지갑',
    connectWallet: '지갑 연결',
    connectDescription: 'Web3 지갑을 연결하여 탈중앙화 ID를 안전하게 확인',
    supportedWallets: '지원 지갑',
    noWallet: '지갑이 감지되지 않습니다. MetaMask를 설치해 주세요',
    noAccount: '계정을 찾을 수 없습니다. 지갑 잠금을 해제해 주세요',
    noWalletYet: '지갑이 없으신가요?',
    getWallet: '받기',
    // 폼
    didPlaceholder: 'DID 또는 사용자 이름',
    passwordPlaceholder: '비밀번호',
    confirmPasswordPlaceholder: '비밀번호 확인',
    emailPlaceholder: '이메일 (선택)',
    nicknamePlaceholder: '닉네임',
    loginButton: '안전하게 로그인',
    registerButton: 'ID 생성',
    loggingIn: '로그인 중...',
    registering: '생성 중...',
    // 계정 관리
    savedAccounts: '확인된 계정',
    quickLogin: '빠른 로그인',
    relogin: '다시 로그인',
    switchAccount: '계정 전환',
    manageAccounts: '계정 관리',
    addNewAccount: '새 계정 추가',
    removeAccount: '계정 삭제',
    removeConfirm: '이 계정을 삭제하시겠습니까?',
    accountRemoved: '계정이 삭제되었습니다',
    currentAccount: '현재 계정',
    otherAccounts: '다른 계정',
    noOtherAccounts: '저장된 다른 계정이 없습니다',
    // 네비게이션
    orDivider: '또는',
    newToDID: '디지털 ID가 처음이신가요?',
    createDID: '생성하기',
    alreadyHaveDID: '이미 ID가 있으신가요?',
    goLogin: '로그인',
    noAccountYet: '계정이 없으신가요?',
    hasAccount: '이미 계정이 있으신가요?',
    // 오류 메시지
    errorNoWallet: '지갑이 감지되지 않습니다',
    errorLoginFailed: '로그인에 실패했습니다',
    errorRegisterFailed: '등록에 실패했습니다',
    errorPasswordMismatch: '비밀번호가 일치하지 않습니다',
    errorPasswordTooShort: '비밀번호는 6자 이상이어야 합니다',
    errorNicknameRequired: '닉네임을 입력해 주세요',
    errorDIDRequired: 'DID를 입력해 주세요',
    errorPasswordRequired: '비밀번호를 입력해 주세요',
    rejected: '사용자가 서명 요청을 거부했습니다',
    // 보안
    poweredBy: 'QuantaBit 제공 탈중앙화 ID',
    securityNote: '종단 간 암호화 · 개인키는 기기를 떠나지 않습니다',
    verifiedBadge: '확인됨',
    encryptedConnection: '암호화 연결',
    decentralizedAuth: '탈중앙화 인증',
    // 기능
    feature1: '완전한 데이터 주권',
    feature2: 'W3C 표준 ID',
    feature3: '크로스체인 범용 ID',
    feature4: '프라이버시 컴퓨팅',
    // 등록 페이지
    createIdentity: '디지털 ID 생성',
    chooseMethod: '등록 방법을 선택하여 탈중앙화 여정을 시작하세요',
    walletMethod: '지갑 연결',
    emailMethod: '이메일 등록',
    walletDesc: 'Web3 지갑을 연결하면 지갑 주소에 연결된 DID를 생성합니다.',
    nickname: '닉네임',
    email: '이메일',
    password: '비밀번호',
    confirmPassword: '비밀번호 확인',
    createAccount: '계정 생성',
    welcome: '환영합니다',
    successDesc: '탈중앙화 ID가 성공적으로 생성되었습니다',
    autoWallet: '지갑이 자동으로 생성되고 연결되었습니다',
    didIdentifier: 'DID 식별자',
    recommended: '추천',
    redirecting: '리디렉션 중...',
    // 소셜 연동
    socialBindings: '소셜 계정 연동',
    bindAccount: '계정 연동',
    unbindAccount: '연동 해제',
    verifiedAccount: '확인된 계정',
    // 평판
    reputation: '평판 점수',
    reputationLevel: '평판 레벨',
    badges: '뱃지',
    leaderboard: '리더보드',
    // 크레덴셜
    credentials: '검증 가능한 크레덴셜',
    issueCredential: '크레덴셜 발급',
    verifyCredential: '크레덴셜 검증',
    // 지갑 연동
    walletBindings: '지갑 연동',
    primaryWallet: '기본 지갑',
    addWallet: '지갑 추가'
  }
};

// ============ 语言同步机制 ============

// 内部语言监听器列表
let _languageListeners = new Set();

/**
 * 获取当前语言
 * 优先从 localStorage 读取，保证与其他 SDK 同步
 * 
 * @returns {string} 当前语言代码
 */
function getLanguage() {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (saved) {
      const normalized = normalizeLanguage(saved);
      return normalized;
    }
  }
  return 'zh';
}

/**
 * 设置当前语言
 * 保存到 localStorage 并触发全局事件
 * 
 * @param {string} lang - 语言代码 (en/zh/ja/ko)
 */
function setLanguage(lang) {
  // 归一化语言代码（支持 'zh-CN' -> 'zh' 等）
  const normalized = normalizeLanguage(lang);
  const oldLang = getLanguage();

  // 保存归一化后的短码到 localStorage
  if (typeof window !== 'undefined') {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);

    // 触发全局事件
    const event = new CustomEvent(LANGUAGE_CHANGE_EVENT, {
      detail: {
        language: normalized,
        oldLanguage: oldLang
      }
    });
    window.dispatchEvent(event);
  }

  // 通知本地监听器
  _notifyListeners(normalized);
}

/**
 * 初始化语言并订阅全局变更
 */
function initLanguage() {
  if (typeof window !== 'undefined') {
    // 优先从 localStorage 读取
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (!saved) {
      // 没有保存的语言，使用浏览器检测
      const browserLang = detectBrowserLanguage();
      localStorage.setItem(LANGUAGE_STORAGE_KEY, browserLang);
    }

    // 订阅全局语言变更事件
    window.addEventListener(LANGUAGE_CHANGE_EVENT, e => {
      const newLang = e.detail?.language;
      if (newLang) {
        const normalized = normalizeLanguage(newLang);
        console.log(`[Auth SDK] 收到全局语言变更: ${newLang} -> ${normalized}`);
        _notifyListeners(normalized);
      }
    });
  }
  return getLanguage();
}

/**
 * 订阅语言变更
 * 
 * @param {function} listener - 回调函数 (newLang) => void
 * @returns {function} 取消订阅函数
 */
function onLanguageChange(listener) {
  _languageListeners.add(listener);
  console.log('[Auth SDK i18n] 订阅语言变更，当前监听器数量:', _languageListeners.size);

  // 同时监听全局事件（如果之前没有监听过）
  if (typeof window !== 'undefined') {
    const handler = e => {
      const newLang = e.detail?.language;
      if (newLang) {
        const normalized = normalizeLanguage(newLang);
        console.log('[Auth SDK i18n] 收到全局语言事件:', newLang, '-> 归一化:', normalized);
        listener(normalized);
      }
    };
    window.addEventListener(LANGUAGE_CHANGE_EVENT, handler);
    console.log('[Auth SDK i18n] 已添加全局事件监听:', LANGUAGE_CHANGE_EVENT);

    // 返回包含清理全局监听器的函数
    return () => {
      _languageListeners.delete(listener);
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, handler);
      console.log('[Auth SDK i18n] 取消订阅，剩余监听器:', _languageListeners.size);
    };
  }
  return () => _languageListeners.delete(listener);
}
function _notifyListeners(lang) {
  _languageListeners.forEach(listener => {
    try {
      listener(lang);
    } catch (err) {
      console.error('[Auth SDK] 语言监听器执行错误:', err);
    }
  });
}

/**
 * 检测浏览器语言
 * @returns {string} 检测到的语言代码
 */
function detectBrowserLanguage() {
  if (typeof navigator === 'undefined') return 'en';
  const browserLang = navigator.language || navigator.userLanguage;
  if (browserLang?.startsWith('zh')) return 'zh';
  if (browserLang?.startsWith('ja')) return 'ja';
  if (browserLang?.startsWith('ko')) return 'ko';
  return 'en';
}

/**
 * 获取翻译消息
 * @param {string} lang - 语言代码
 * @returns {Object} 翻译消息对象
 */
function getMessages$1(lang) {
  const language = normalizeLanguage(lang || getLanguage());
  return messages[language] || messages.zh;
}

/**
 * 翻译函数
 * @param {string} key - 翻译键
 * @param {Object} params - 替换参数
 * @returns {string} 翻译后的文本
 */
function t(key, params) {
  const msgs = getMessages$1();
  let text = msgs[key] || key;

  // 参数替换
  if (params) {
    Object.keys(params).forEach(k => {
      text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), params[k]);
    });
  }
  return text;
}

/**
 * 获取所有支持的语言列表
 * @returns {Array} 语言列表
 */
function getSupportedLanguages() {
  return SUPPORTED_LANGUAGES.map(code => LANGUAGE_INFO[code]);
}

/**
 * QuantaBit Auth SDK - 工具函数
 */

const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
const MAX_QBIT_MEMO_BYTES = 512;

// ============ 账户存储 ============

const STORAGE_KEY = 'qbit_did_saved_accounts';
const MAX_ACCOUNTS$1 = 5;

/**
 * 获取保存的账户列表
 */
function getSavedAccounts() {
  if (typeof window === 'undefined') return [];
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch (e) {
    return [];
  }
}

/**
 * 保存账户
 */
function saveAccount(account) {
  if (typeof window === 'undefined') return;
  try {
    const accounts = getSavedAccounts().filter(a => a.did !== account.did);
    accounts.unshift({
      ...account,
      lastLoginAt: new Date().toISOString()
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(accounts.slice(0, MAX_ACCOUNTS$1)));
  } catch (e) {
    // Ignore storage errors
  }
}

/**
 * 移除账户
 */
function removeAccount(did) {
  if (typeof window === 'undefined') return;
  try {
    const accounts = getSavedAccounts().filter(a => a.did !== did);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(accounts));
  } catch (e) {
    // Ignore storage errors
  }
}

/**
 * 获取当前会话
 */
function getCurrentSession() {
  if (typeof window === 'undefined') return null;
  try {
    const did = localStorage.getItem('qbit_did_current');
    const token = localStorage.getItem('qbit_did_access_token');
    if (did && token) return {
      did,
      token
    };
    return null;
  } catch (e) {
    return null;
  }
}

/**
 * 清除当前会话
 */
function clearCurrentSession() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem('qbit_did_current');
    localStorage.removeItem('qbit_did_access_token');
    localStorage.removeItem('qbit_did_refresh_token');
  } catch (e) {
    // Ignore
  }
}

/**
 * 设置当前 DID
 */
function setCurrentDID(did) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem('qbit_did_current', did);
  } catch (e) {
    // Ignore
  }
}

// ============ 格式化 ============

/**
 * 格式化 DID（截断显示）
 */
function formatDID$1(did, chars) {
  if (!did || did.length <= 20) return did || '';
  if (chars !== undefined) {
    if (did.startsWith('did:')) {
      const parts = did.split(':');
      if (parts.length >= 3) {
        const prefix = parts.slice(0, 2).join(':') + ':';
        const id = parts.slice(2).join(':');
        const start = Math.ceil(chars / 2);
        const end = Math.floor(chars / 2);
        return `${prefix}${id.slice(0, start)}...${id.slice(-end)}`;
      }
    }
    const start = Math.ceil(chars / 2);
    const end = Math.floor(chars / 2);
    return `${did.slice(0, start)}...${did.slice(-end)}`;
  }
  return `${did.slice(0, 12)}...${did.slice(-6)}`;
}

/**
 * 格式化钱包地址
 */
function formatAddress(address) {
  if (!address) return '';
  if (address.length <= 10) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

// ============ 钱包检测 ============

function isQbitChainType(chainType = 'quantabit') {
  return ['quantabit', 'qbit', 'qbitchain', 'solana'].includes(String(chainType).toLowerCase());
}
function normalizeWalletChainType(chainType = 'quantabit') {
  return isQbitChainType(chainType) ? 'quantabit' : chainType;
}
function encodeBase58(input) {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input || []);
  if (bytes.length === 0) return '';
  const digits = [0];
  for (let i = 0; i < bytes.length; i += 1) {
    let carry = bytes[i];
    for (let j = 0; j < digits.length; j += 1) {
      const value = digits[j] * 256 + carry;
      digits[j] = value % 58;
      carry = Math.floor(value / 58);
    }
    while (carry > 0) {
      digits.push(carry % 58);
      carry = Math.floor(carry / 58);
    }
  }
  let encoded = '';
  for (let i = 0; i < bytes.length && bytes[i] === 0; i += 1) {
    encoded += BASE58_ALPHABET[0];
  }
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    encoded += BASE58_ALPHABET[digits[i]];
  }
  return encoded;
}

/**
 * 检测是否安装了 MetaMask
 */
function hasMetaMask() {
  return typeof window !== 'undefined' && typeof window.ethereum !== 'undefined';
}
function hasQBitWallet() {
  return !!getQBitProvider();
}

/**
 * 获取钱包 Provider
 */
function getEthereumProvider() {
  if (typeof window === 'undefined') return null;
  return window.ethereum || null;
}
function getQBitProvider(preferredWallet = null) {
  if (typeof window === 'undefined') return null;
  const providers = {
    phantom: window.phantom?.solana || (window.solana?.isPhantom ? window.solana : null),
    solflare: window.solflare || null,
    backpack: window.backpack || null,
    okx: window.okxwallet?.solana || null,
    tokenpocket: window.tokenpocket?.solana || null,
    solana: window.solana || null
  };
  if (preferredWallet && providers[preferredWallet]) {
    return providers[preferredWallet];
  }
  return providers.phantom || providers.solflare || providers.backpack || providers.okx || providers.tokenpocket || providers.solana || null;
}
async function connectBrowserWallet(chainType = 'quantabit', preferredWallet = null) {
  const normalizedChainType = normalizeWalletChainType(chainType);
  if (isQbitChainType(normalizedChainType)) {
    const provider = getQBitProvider(preferredWallet);
    if (!provider) {
      throw new Error('未检测到 QBit/Solana 钱包，请安装 Phantom、Solflare、Backpack 或 OKX Wallet');
    }
    const response = await provider.connect();
    const publicKey = response?.publicKey || provider.publicKey || response || null;
    const address = typeof publicKey === 'string' ? publicKey : publicKey?.toBase58?.() || publicKey?.toString?.();
    if (!address) {
      throw new Error('未获取到钱包账户');
    }
    return {
      address,
      provider,
      chainType: normalizedChainType,
      walletType: preferredWallet || provider.isPhantom && 'phantom' || provider.isSolflare && 'solflare' || provider.isBackpack && 'backpack' || provider.isOkxWallet && 'okx' || 'solana'
    };
  }
  const provider = getEthereumProvider();
  if (!provider) {
    throw new Error('未检测到 EVM 钱包，请安装 MetaMask');
  }
  const accounts = await provider.request({
    method: 'eth_requestAccounts'
  });
  const address = accounts?.[0];
  if (!address) {
    throw new Error('未获取到钱包账户');
  }
  return {
    address,
    provider,
    chainType: normalizedChainType,
    walletType: preferredWallet || 'metamask'
  };
}
async function signWalletMessage(provider, chainType, message, address) {
  const normalizedChainType = normalizeWalletChainType(chainType);
  if (isQbitChainType(normalizedChainType)) {
    if (!provider?.signMessage) {
      throw new Error('当前钱包不支持消息签名');
    }
    const encodedMessage = new TextEncoder().encode(message);
    const signed = await provider.signMessage(encodedMessage, 'utf8');
    const signatureBytes = signed?.signature || signed;
    return encodeBase58(signatureBytes);
  }
  if (!provider?.request) {
    throw new Error('当前钱包不支持消息签名');
  }
  return provider.request({
    method: 'personal_sign',
    params: [message, address]
  });
}
async function loginWithBrowserWallet(api, chainType = 'quantabit', preferredWallet = null) {
  const {
    address,
    provider,
    walletType
  } = await connectBrowserWallet(chainType, preferredWallet);
  const authChainType = normalizeWalletChainType(chainType);
  const nonceRes = await api.getNonce(address, authChainType);
  const {
    message
  } = nonceRes.data || nonceRes;
  const signature = await signWalletMessage(provider, authChainType, message, address);
  const authRes = await api.walletLogin(address, signature, message, authChainType);
  return {
    address,
    provider,
    walletType,
    chainType: authChainType,
    message,
    signature,
    response: authRes.data || authRes
  };
}
async function bindBrowserWallet(api, chainType = 'quantabit', bindingType = 'secondary', preferredWallet = null) {
  const {
    address,
    provider,
    walletType
  } = await connectBrowserWallet(chainType, preferredWallet);
  const authChainType = normalizeWalletChainType(chainType);
  const messageRes = await api.getBindingMessage(address, authChainType);
  const message = messageRes.data?.message || messageRes.message;
  const signature = await signWalletMessage(provider, authChainType, message, address);
  const bindRes = await api.bindWallet(address, authChainType, signature, message, bindingType);
  return {
    address,
    provider,
    walletType,
    chainType: authChainType,
    message,
    signature,
    response: bindRes.data || bindRes
  };
}
function extractTransactionHash(result) {
  if (!result) return null;
  if (typeof result === 'string') {
    return result;
  }
  return result.txHash || result.tx_hash || result.signature || result.hash || null;
}
function buildQBitAnchorMemo$1(payload, options = {}) {
  const namespace = options.namespace || 'qbit_did';
  const memoPayload = {
    ns: namespace,
    action: payload?.action || 'anchor',
    did: payload?.did,
    credential_id: payload?.credential_id,
    document_hash: payload?.document_hash,
    credential_hash: payload?.credential_hash,
    version: payload?.version
  };
  const memo = JSON.stringify(Object.fromEntries(Object.entries(memoPayload).filter(([, value]) => value !== undefined && value !== null)));
  const memoBytes = new TextEncoder().encode(memo);
  if (memoBytes.length > MAX_QBIT_MEMO_BYTES) {
    throw new Error(`Memo 内容过长，当前 ${memoBytes.length} bytes，超过 ${MAX_QBIT_MEMO_BYTES} bytes 限制`);
  }
  return memo;
}
function createQBitMemoAnchorBroadcaster$1(options = {}) {
  const {
    provider,
    address,
    endpoint,
    network = 'mainnet',
    priorityFee,
    skipPreflight = false,
    namespace = 'qbit_did',
    waitForConfirmation = false,
    confirmCommitment = 'confirmed',
    confirmTimeoutMs
  } = options;
  return async payload => {
    if (!provider) {
      throw new Error('缺少 QBit 钱包 provider');
    }

    // 委托核心 SDK 的统一锚定广播逻辑（单一事实来源）
    const sdk = await import('@quantabit/qbit-chain-sdk');
    const connection = new sdk.QBitConnection({
      endpoint,
      network
    });
    const memo = buildQBitAnchorMemo$1(payload, {
      namespace
    });
    return sdk.broadcastQBitAnchor(connection, provider, memo, {
      address,
      priorityFee,
      skipPreflight,
      waitForConfirmation,
      commitment: confirmCommitment,
      timeoutMs: confirmTimeoutMs
    });
  };
}

/**
 * 等待 QBit 交易确认（委托核心 SDK 的 waitForAnchorConfirmation）
 *
 * QBit 主网 RPC 对 WebSocket 确认支持不稳定，统一采用 HTTP 轮询。
 *
 * @param {Object} connection - QBitConnection 或暴露 raw / getRawConnection 的连接
 * @param {string} signature - 交易签名
 * @param {Object} options - { commitment, pollIntervalMs, maxPolls, timeoutMs }
 * @returns {Promise<{confirmed: boolean, slot: number|null, status: string|null, timedOut?: boolean}>}
 */
async function waitForQBitConfirmation(connection, signature, options = {}) {
  const {
    waitForAnchorConfirmation
  } = await import('@quantabit/qbit-chain-sdk');
  return waitForAnchorConfirmation(connection, signature, options);
}

/**
 * 校验某笔交易是否成功锚定了 DID Memo（委托核心 SDK 的 verifyAnchoredMemo）
 *
 * @param {Object} options - 选项
 * @param {string} options.txHash - 交易签名
 * @param {string} options.endpoint - 自定义 RPC 端点
 * @param {string} options.network - 网络（默认 'mainnet'）
 * @param {string} options.namespace - anchor 命名空间（默认 'qbit_did'）
 * @param {Object} options.match - 期望匹配的字段（如 { did, document_hash }）
 * @returns {Promise<{found: boolean, matched: boolean, memos: Array<Object>}>}
 */
async function verifyAnchoredMemo(options = {}) {
  const {
    txHash,
    endpoint,
    network = 'mainnet',
    namespace = 'qbit_did',
    match
  } = options;
  if (!txHash) {
    throw new Error('缺少 txHash');
  }
  const sdk = await import('@quantabit/qbit-chain-sdk');
  const connection = new sdk.QBitConnection({
    endpoint,
    network
  });
  return sdk.verifyAnchoredMemo(connection, txHash, {
    namespace,
    match
  });
}
async function completeExternalChainOperation$1(operationResult, broadcaster, submitter) {
  if (!operationResult?.requires_external_signer) {
    return {
      skipped: true,
      reason: 'operation_does_not_require_external_signer',
      operation: operationResult
    };
  }
  if (typeof broadcaster !== 'function') {
    throw new Error('缺少外部签名广播器 broadcaster');
  }
  if (typeof submitter !== 'function') {
    throw new Error('缺少 tx_hash 提交器 submitter');
  }
  const broadcastResult = await broadcaster(operationResult.payload, operationResult);
  const txHash = extractTransactionHash(broadcastResult);
  if (!txHash) {
    throw new Error('外部签名广播器未返回 tx_hash/signature');
  }
  const submitResult = await submitter(txHash);
  return {
    txHash,
    broadcast: broadcastResult,
    submit: submitResult
  };
}

/**
 * QuantaBit Auth SDK - Language Switcher Component
 * 语言切换组件
 */

function LanguageSwitcher({
  onLanguageChange,
  currentLanguage,
  variant = 'dark',
  showFlag = true,
  showName = true,
  className = ''
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [lang, setLang] = useState(currentLanguage || getLanguage());
  const dropdownRef = useRef(null);

  // 如果外部传入了语言，同步更新
  useEffect(() => {
    if (currentLanguage && currentLanguage !== lang) {
      setLang(currentLanguage);
    }
  }, [currentLanguage]);

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleSelect = useCallback(code => {
    setLang(code);
    setLanguage(code);
    setIsOpen(false);
    if (onLanguageChange) {
      onLanguageChange(code);
    }
  }, [onLanguageChange]);
  const currentLangInfo = LANGUAGE_INFO[lang] || LANGUAGE_INFO.en;
  return /*#__PURE__*/jsxs("div", {
    ref: dropdownRef,
    className: `eco-lang-switcher eco-lang-${variant} ${className}`,
    onMouseEnter: () => setIsOpen(true),
    onMouseLeave: () => setIsOpen(false),
    onClick: e => e.stopPropagation(),
    children: [/*#__PURE__*/jsxs("button", {
      type: "button",
      className: "eco-lang-trigger",
      onClick: e => {
        e.preventDefault();
        setIsOpen(!isOpen);
      },
      "aria-expanded": isOpen,
      "aria-haspopup": "listbox",
      children: [showFlag && /*#__PURE__*/jsx("span", {
        className: "eco-lang-flag",
        children: currentLangInfo.flag
      }), showName && /*#__PURE__*/jsx("span", {
        className: "eco-lang-name",
        children: currentLangInfo.nativeName
      }), /*#__PURE__*/jsx("svg", {
        className: `eco-lang-arrow ${isOpen ? 'open' : ''}`,
        width: "12",
        height: "12",
        viewBox: "0 0 12 12",
        fill: "none",
        children: /*#__PURE__*/jsx("path", {
          d: "M3 4.5L6 7.5L9 4.5",
          stroke: "currentColor",
          strokeWidth: "1.5",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        })
      })]
    }), isOpen && /*#__PURE__*/jsx("ul", {
      className: "eco-lang-dropdown",
      role: "listbox",
      children: SUPPORTED_LANGUAGES.filter(code => LANGUAGE_INFO[code]).map(code => {
        const info = LANGUAGE_INFO[code];
        const isActive = code === lang;
        return /*#__PURE__*/jsx("li", {
          role: "option",
          "aria-selected": isActive,
          children: /*#__PURE__*/jsxs("button", {
            type: "button",
            className: `eco-lang-option ${isActive ? 'active' : ''}`,
            onClick: e => {
              e.preventDefault();
              handleSelect(code);
            },
            children: [/*#__PURE__*/jsx("span", {
              className: "eco-lang-flag",
              children: info.flag
            }), /*#__PURE__*/jsx("span", {
              className: "eco-lang-name",
              children: info.nativeName
            }), /*#__PURE__*/jsx("span", {
              className: "eco-lang-label",
              children: info.name
            }), isActive && /*#__PURE__*/jsx("svg", {
              className: "eco-lang-check",
              width: "14",
              height: "14",
              viewBox: "0 0 14 14",
              fill: "none",
              children: /*#__PURE__*/jsx("path", {
                d: "M2.5 7L5.5 10L11.5 4",
                stroke: "currentColor",
                strokeWidth: "2",
                strokeLinecap: "round",
                strokeLinejoin: "round"
              })
            })]
          })
        }, code);
      })
    }), /*#__PURE__*/jsx("style", {
      children: `
        .eco-lang-switcher {
          position: relative;
          z-index: 100;
        }

        .eco-lang-trigger {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 10px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          color: inherit;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .eco-lang-trigger:hover {
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.3);
        }

        .eco-lang-dark .eco-lang-trigger {
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(255, 255, 255, 0.15);
          color: rgba(255, 255, 255, 0.9);
        }

        .eco-lang-light .eco-lang-trigger {
          background: rgba(0, 0, 0, 0.05);
          border-color: rgba(0, 0, 0, 0.1);
          color: #333;
        }

        .eco-lang-minimal .eco-lang-trigger {
          background: transparent;
          border: none;
          padding: 4px 8px;
        }

        .eco-lang-flag {
          font-size: 16px;
          line-height: 1;
        }

        .eco-lang-name {
          font-weight: 500;
        }

        .eco-lang-arrow {
          transition: transform 0.2s ease;
        }

        .eco-lang-arrow.open {
          transform: rotate(180deg);
        }

        .eco-lang-dropdown {
          position: absolute;
          top: calc(100% + 6px);
          left: 0;
          list-style: none;
          margin: 0;
          padding: 4px;
          min-width: auto;
          white-space: nowrap;
          background: rgba(30, 30, 40, 0.98);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
          overflow: visible;
          animation: dropdownSlide 0.2s ease;
        }

        /* 透明桥接区域，防止鼠标穿越间隙时菜单消失 */
        .eco-lang-dropdown::before {
          content: '';
          position: absolute;
          top: -10px;
          left: 0;
          right: 0;
          height: 10px;
        }

        .eco-lang-light .eco-lang-dropdown {
          background: rgba(255, 255, 255, 0.98);
          border-color: rgba(0, 0, 0, 0.1);
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
        }

        @keyframes dropdownSlide {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .eco-lang-option {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 10px 12px;
          background: transparent;
          border: none;
          border-radius: 8px;
          color: rgba(255, 255, 255, 0.85);
          font-size: 14px;
          text-align: left;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .eco-lang-light .eco-lang-option {
          color: #333;
        }

        .eco-lang-option:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .eco-lang-light .eco-lang-option:hover {
          background: rgba(0, 0, 0, 0.05);
        }

        .eco-lang-option.active {
          background: linear-gradient(135deg, rgba(99, 102, 241, 0.3), rgba(168, 85, 247, 0.3));
        }

        .eco-lang-option .eco-lang-name {
          flex: 1;
        }

        .eco-lang-label {
          font-size: 12px;
          opacity: 0.6;
        }

        .eco-lang-check {
          color: #a855f7;
        }

        .eco-lang-light .eco-lang-check {
          color: #6366f1;
        }
      `
    })]
  });
}

/**
 * QuantaBit Auth SDK - 弹窗登录组件 (LoginModal)
 * 
 * 安全连接弹窗，支持：
 * - 钱包登录（MetaMask, WalletConnect, Coinbase, QBit）
 * - DID 密码登录
 * - 注册新身份
 * - 多账户管理和切换
 */

function LoginModal({
  isOpen,
  onClose,
  onSuccess,
  onError,
  onLogout,
  onModeChange,
  apiBaseUrl = '/api/v1',
  appName = 'QuantaBit',
  appLogo,
  language: initialLanguage = 'zh',
  showRegister = true,
  currentUser = null,
  showSocialLogin = true,
  socialProviders = ['apple', 'google', 'microsoft', 'wechat', 'tiktok'],
  renderSocialLogin,
  layoutMode = 'double',
  theme = 'dark'
}) {
  const [mode, setMode] = useState('login'); // login, register, accounts
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Client-side mounting state
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // 语言状态 - 与 DarkLoginModal 保持一致的同步机制
  const [currentLanguage, setCurrentLanguage] = useState(initialLanguage);

  // 语言切换处理
  const handleLanguageChange = useCallback(newLang => {
    setCurrentLanguage(newLang);
    setLanguage(newLang);
  }, []);

  // 同步外部 props.language 变化
  useEffect(() => {
    if (initialLanguage && initialLanguage !== currentLanguage) {
      console.log('[LoginModal] props.language 变化:', initialLanguage);
      setCurrentLanguage(initialLanguage);
    }
  }, [initialLanguage]);

  // 订阅全局语言变更
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const LANGUAGE_CHANGE_EVENT = 'qbit-did:language-change';
    const SUPPORTED_LANGUAGES = ['en', 'zh', 'ja', 'ko'];
    const handler = e => {
      const newLang = e.detail?.language;
      console.log('[LoginModal] 收到全局语言变更事件:', newLang);
      if (newLang && SUPPORTED_LANGUAGES.includes(newLang)) {
        setCurrentLanguage(newLang);
      }
    };
    window.addEventListener(LANGUAGE_CHANGE_EVENT, handler);
    console.log('[LoginModal] 已订阅全局语言事件');
    return () => {
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, handler);
      console.log('[LoginModal] 取消语言订阅');
    };
  }, []);

  // Wallet states
  const [walletStatus, setWalletStatus] = useState('idle');
  const [activeWallet, setActiveWallet] = useState(null);

  // Form states
  const [did, setDid] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [email, setEmail] = useState('');

  // Saved accounts
  const [savedAccounts, setSavedAccounts] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const msg = getMessages$1(currentLanguage);

  // 配置 API
  useEffect(() => {
    if (apiBaseUrl) {
      if (typeof authApi.configure === 'function') {
        authApi.configure({
          apiBaseUrl
        });
      } else if (typeof authApi.setBaseUrl === 'function') {
        authApi.setBaseUrl(apiBaseUrl);
      }
    }
  }, [apiBaseUrl]);

  // 加载保存的账户
  useEffect(() => {
    if (isOpen) {
      setSavedAccounts(getSavedAccounts());
      setCurrentSession(getCurrentSession());
      setError(null);
      setSuccess(null);
      // 如果已登录，默认显示账户管理
      if (currentUser?.did) {
        setMode('accounts');
      } else {
        setMode('login');
      }
    }
  }, [isOpen, currentUser]);

  // 社交登录成功回调
  const handleSocialSuccess = useCallback(result => {
    const {
      access_token,
      refresh_token,
      did,
      user: resUser,
      is_new_user
    } = result;

    // 保存 tokens 到 authApi (apiClient)
    if (typeof authApi.setTokens === 'function') {
      authApi.setTokens(access_token, refresh_token);
    }
    setCurrentDID(did);
    saveAccount({
      did,
      displayName: resUser?.display_name || formatDID$1(did),
      avatar: resUser?.avatar_url || resUser?.avatar,
      verified: true
    });
    setSavedAccounts(getSavedAccounts());
    setSuccess(msg.success || '登录成功');
    setTimeout(() => {
      onSuccess && onSuccess({
        success: true,
        accessToken: access_token,
        refreshToken: refresh_token,
        did,
        user: {
          nickname: resUser?.display_name || resUser?.name,
          avatar: resUser?.avatar_url || resUser?.avatar,
          ...resUser
        },
        isNewUser: is_new_user
      });
      onClose && onClose();
    }, 1200);
  }, [onSuccess, onClose, msg]);
  const handleSocialError = useCallback(err => {
    setError(err?.message || '社交登录失败');
  }, []);

  // 处理钱包登录
  const handleWalletLogin = useCallback(async walletType => {
    const ethereum = typeof window !== 'undefined' ? window.ethereum : undefined;
    if (!ethereum) {
      setError(msg.errorNoWallet || '未检测到钱包插件，请先安装');
      return;
    }
    setLoading(true);
    setError(null);
    setActiveWallet(walletType);
    setWalletStatus('connecting');
    try {
      const chainType = walletType === 'quantabit' ? 'quantabit' : 'ethereum';
      const preferredWallet = walletType === 'quantabit' ? 'phantom' : walletType;
      const {
        address: walletAddress,
        provider
      } = await connectBrowserWallet(chainType, preferredWallet);

      // 2. 获取 nonce
      setWalletStatus('signing');
      const nonceRes = await authApi.getNonce(walletAddress, chainType);
      const {
        message
      } = nonceRes.data || nonceRes;

      // 3. 签名
      const signature = await signWalletMessage(chainType === 'quantabit' ? provider || getQBitProvider() : provider, chainType, message, walletAddress);

      // 4. 验证登录
      setWalletStatus('verifying');
      const loginData = await authApi.walletLogin(walletAddress, signature, message, chainType);
      const result = loginData.data || loginData;

      // 保存 session
      authApi.setTokens(result.access_token, result.refresh_token);
      setCurrentDID(result.did);
      saveAccount({
        did: result.did,
        displayName: result.display_name || result.user?.nickname,
        avatar: result.avatar || result.user?.avatar,
        walletAddress,
        verified: true
      });
      setSavedAccounts(getSavedAccounts());
      setSuccess('✓ ' + msg.success);
      setTimeout(() => {
        onSuccess && onSuccess({
          success: true,
          accessToken: result.access_token,
          refreshToken: result.refresh_token,
          did: result.did,
          isNewUser: result.is_new_user,
          user: result.user
        });
        onClose();
      }, 800);
    } catch (err) {
      setError(err.message || msg.errorLoginFailed);
      onError && onError(err);
    } finally {
      setLoading(false);
      setWalletStatus('idle');
      setActiveWallet(null);
    }
  }, [msg, onSuccess, onError, onClose]);

  // 处理 DID 登录
  const handleDIDLogin = useCallback(async () => {
    if (!did.trim() || !password) {
      setError('请输入 DID 和密码');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await authApi.didLogin(did, password);
      const data = result.data || result;
      authApi.setTokens(data.access_token, data.refresh_token);
      setCurrentDID(data.did);
      saveAccount({
        did: data.did,
        displayName: data.display_name || data.user?.nickname,
        avatar: data.avatar || data.user?.avatar,
        verified: true
      });
      setSavedAccounts(getSavedAccounts());
      setSuccess('✓ ' + msg.success);
      setTimeout(() => {
        onSuccess && onSuccess({
          success: true,
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
          did: data.did,
          isNewUser: false,
          user: data.user
        });
        onClose();
      }, 800);
    } catch (err) {
      setError(err.message || msg.errorLoginFailed);
      onError && onError(err);
    } finally {
      setLoading(false);
    }
  }, [did, password, msg, onSuccess, onError, onClose]);

  // 处理快速登录
  const handleQuickLogin = useCallback(account => {
    setDid(account.did);
    setMode('login');
    setTimeout(() => {
      document.getElementById('did-password-input')?.focus();
    }, 100);
  }, []);

  // 处理注册
  const handleRegister = useCallback(async () => {
    if (password !== confirmPassword) {
      setError(msg.errorPasswordMismatch);
      return;
    }
    if (!nickname.trim()) {
      setError('请输入昵称');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await authApi.register({
        displayName: nickname.trim(),
        email: email || undefined,
        password
      });
      const data = result.data || result;
      authApi.setTokens(data.access_token, data.refresh_token);
      setCurrentDID(data.did);
      saveAccount({
        did: data.did,
        displayName: nickname.trim(),
        verified: true
      });
      setSavedAccounts(getSavedAccounts());
      setSuccess('✓ 身份创建成功！');
      setTimeout(() => {
        onSuccess && onSuccess({
          success: true,
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
          did: data.did,
          isNewUser: true,
          user: data.user
        });
        onClose();
      }, 800);
    } catch (err) {
      setError(err.message || msg.errorRegisterFailed);
      onError && onError(err);
    } finally {
      setLoading(false);
    }
  }, [nickname, email, password, confirmPassword, msg, onSuccess, onError, onClose]);

  // 处理移除账户
  const handleRemoveAccount = useCallback((accountDid, e) => {
    e.stopPropagation();
    if (window.confirm(msg.removeConfirm)) {
      removeAccount(accountDid);
      setSavedAccounts(getSavedAccounts());
      setSuccess(msg.accountRemoved);
      setTimeout(() => setSuccess(null), 2000);
    }
  }, [msg]);

  // 处理登出
  const handleLogout = useCallback(() => {
    clearCurrentSession();
    setCurrentSession(null);
    setMode('login');
    onLogout && onLogout();
  }, [onLogout]);
  if (!isOpen || !mounted) return null;

  // 渲染账户管理视图
  const renderAccountsView = () => {
    const otherAccounts = savedAccounts.filter(a => a.did !== currentUser?.did);
    return /*#__PURE__*/jsxs("div", {
      className: "did-accounts-view",
      children: [/*#__PURE__*/jsxs("div", {
        className: "did-accounts-header",
        children: [/*#__PURE__*/jsx("button", {
          className: "did-accounts-back",
          onClick: () => setMode('login'),
          children: msg.back
        }), /*#__PURE__*/jsxs("h3", {
          children: [/*#__PURE__*/jsx(SwapIcon, {}), " ", msg.accountsSection]
        })]
      }), currentUser?.did && /*#__PURE__*/jsxs("div", {
        className: "did-current-account",
        children: [/*#__PURE__*/jsx("p", {
          className: "did-section-label",
          children: msg.currentAccount
        }), /*#__PURE__*/jsxs("div", {
          className: "did-account-card current",
          children: [/*#__PURE__*/jsx("div", {
            className: "did-account-avatar",
            children: currentUser.avatar ? /*#__PURE__*/jsx("img", {
              src: currentUser.avatar,
              alt: ""
            }) : /*#__PURE__*/jsx("span", {
              children: "\uD83D\uDD10"
            })
          }), /*#__PURE__*/jsxs("div", {
            className: "did-account-info",
            children: [/*#__PURE__*/jsxs("span", {
              className: "did-account-name",
              children: [currentUser.nickname || currentUser.displayName || formatDID$1(currentUser.did), /*#__PURE__*/jsx(VerifiedIcon, {})]
            }), /*#__PURE__*/jsx("span", {
              className: "did-account-did",
              children: formatDID$1(currentUser.did)
            })]
          }), /*#__PURE__*/jsx("button", {
            className: "did-logout-btn",
            onClick: handleLogout,
            title: msg.logout,
            children: /*#__PURE__*/jsx(LogoutIcon, {})
          })]
        })]
      }), /*#__PURE__*/jsxs("button", {
        className: "did-add-account-btn",
        onClick: () => setMode('login'),
        children: [/*#__PURE__*/jsx(PlusIcon, {}), /*#__PURE__*/jsx("span", {
          children: msg.addNewAccount
        })]
      }), otherAccounts.length > 0 && /*#__PURE__*/jsxs("div", {
        className: "did-other-accounts",
        children: [/*#__PURE__*/jsx("p", {
          className: "did-section-label",
          children: msg.otherAccounts
        }), otherAccounts.map(account => /*#__PURE__*/jsxs("div", {
          className: "did-account-card",
          onClick: () => handleQuickLogin(account),
          children: [/*#__PURE__*/jsx("div", {
            className: "did-account-avatar",
            children: account.avatar ? /*#__PURE__*/jsx("img", {
              src: account.avatar,
              alt: ""
            }) : /*#__PURE__*/jsx("span", {
              children: "\uD83D\uDD10"
            })
          }), /*#__PURE__*/jsxs("div", {
            className: "did-account-info",
            children: [/*#__PURE__*/jsxs("span", {
              className: "did-account-name",
              children: [account.displayName || formatDID$1(account.did), account.verified && /*#__PURE__*/jsx(VerifiedIcon, {})]
            }), /*#__PURE__*/jsx("span", {
              className: "did-account-did",
              children: formatDID$1(account.did)
            })]
          }), /*#__PURE__*/jsx("button", {
            className: "did-switch-btn",
            children: msg.switchAccount
          }), /*#__PURE__*/jsx("button", {
            className: "did-remove-btn",
            onClick: e => handleRemoveAccount(account.did, e),
            title: msg.removeAccount,
            children: /*#__PURE__*/jsx(TrashIcon, {})
          })]
        }, account.did))]
      }), otherAccounts.length === 0 && !currentUser?.did && /*#__PURE__*/jsx("p", {
        className: "did-no-accounts",
        children: msg.noOtherAccounts
      })]
    });
  };
  return /*#__PURE__*/ReactDOM.createPortal(/*#__PURE__*/jsx("div", {
    className: `did-login-overlay did-theme-${theme}`,
    onClick: e => e.target === e.currentTarget && onClose(),
    children: /*#__PURE__*/jsxs("div", {
      className: `did-login-modal${layoutMode === 'single' ? ' layout-single' : ''}`,
      children: [/*#__PURE__*/jsxs("div", {
        className: "did-login-toolbar",
        children: [/*#__PURE__*/jsx(LanguageSwitcher, {
          currentLanguage: currentLanguage,
          onLanguageChange: handleLanguageChange,
          variant: "dark",
          showFlag: true,
          showName: false
        }), onModeChange && /*#__PURE__*/jsx("button", {
          className: "did-login-mode-btn",
          onClick: () => onModeChange('compact'),
          title: currentLanguage === 'zh' ? '简易模式' : 'Compact Mode',
          children: /*#__PURE__*/jsx(MinimizeIcon, {
            size: 18
          })
        }), /*#__PURE__*/jsx("button", {
          className: "did-login-close",
          onClick: onClose,
          children: /*#__PURE__*/jsx(CloseIcon, {})
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "did-login-header",
        children: [/*#__PURE__*/jsxs("div", {
          className: "did-login-brand",
          children: [appLogo ? /*#__PURE__*/jsx("img", {
            src: appLogo,
            alt: appName,
            className: "did-login-app-logo"
          }) : /*#__PURE__*/jsx("div", {
            className: "did-login-logo",
            children: /*#__PURE__*/jsx(ShieldIcon, {})
          }), /*#__PURE__*/jsxs("div", {
            children: [/*#__PURE__*/jsxs("h2", {
              children: [msg.title, " ", /*#__PURE__*/jsx("span", {
                className: "did-app-name",
                children: appName
              })]
            }), /*#__PURE__*/jsx("p", {
              style: {
                display: 'inline'
              },
              children: msg.subtitle
            }), /*#__PURE__*/jsxs("span", {
              className: "did-security-badge",
              style: {
                padding: '2px 8px',
                fontSize: '11px',
                marginLeft: '8px',
                display: 'inline-flex',
                verticalAlign: 'middle'
              },
              children: [/*#__PURE__*/jsx(LockIcon, {
                size: 12
              }), " ", msg.encryptedConnection]
            }), /*#__PURE__*/jsxs("span", {
              className: "did-security-badge",
              style: {
                padding: '2px 8px',
                fontSize: '11px',
                marginLeft: '4px',
                display: 'inline-flex',
                verticalAlign: 'middle'
              },
              children: [/*#__PURE__*/jsx(VerifiedIcon, {
                size: 12
              }), " ", msg.decentralizedAuth]
            })]
          })]
        }), savedAccounts.length > 0 && mode !== 'accounts' && /*#__PURE__*/jsx("div", {
          style: {
            padding: '0 24px 8px'
          },
          children: /*#__PURE__*/jsxs("button", {
            className: "did-manage-accounts-btn",
            onClick: () => setMode('accounts'),
            children: [/*#__PURE__*/jsx(SwapIcon, {
              size: 14
            }), " ", msg.manageAccounts]
          })
        })]
      }), error && /*#__PURE__*/jsxs("div", {
        className: "did-login-error",
        children: ["\u26A0\uFE0F ", error]
      }), success && /*#__PURE__*/jsx("div", {
        className: "did-login-success",
        children: success
      }), mode === 'accounts' ? /*#__PURE__*/jsx("div", {
        className: "did-login-content single-column",
        children: renderAccountsView()
      }) : /*#__PURE__*/jsxs("div", {
        className: `did-login-content${layoutMode === 'single' ? ' layout-single' : ''}`,
        children: [/*#__PURE__*/jsxs("div", {
          className: "did-login-left",
          children: [/*#__PURE__*/jsxs("h3", {
            children: [/*#__PURE__*/jsx("span", {
              className: "did-login-icon",
              children: "\uD83D\uDC5B"
            }), msg.walletSection]
          }), /*#__PURE__*/jsxs("div", {
            className: "did-login-wallets",
            children: [/*#__PURE__*/jsxs("button", {
              className: `did-wallet-btn featured ${activeWallet === 'quantabit' ? 'active' : ''}`,
              onClick: () => handleWalletLogin('quantabit'),
              disabled: loading,
              children: [/*#__PURE__*/jsx(QBitIcon, {}), /*#__PURE__*/jsx("span", {
                children: msg.quantabitWallet
              }), /*#__PURE__*/jsx("span", {
                className: "did-wallet-badge",
                children: msg.recommended
              })]
            }), /*#__PURE__*/jsxs("button", {
              className: `did-wallet-btn ${activeWallet === 'metamask' ? 'active' : ''}`,
              onClick: () => handleWalletLogin('metamask'),
              disabled: loading,
              children: [/*#__PURE__*/jsx(MetaMaskIcon$1, {}), /*#__PURE__*/jsxs("span", {
                children: [activeWallet === 'metamask' && walletStatus === 'connecting' && msg.connecting, activeWallet === 'metamask' && walletStatus === 'signing' && msg.signing, activeWallet === 'metamask' && walletStatus === 'verifying' && msg.verifying, activeWallet !== 'metamask' && msg.metamask]
              }), activeWallet === 'metamask' && /*#__PURE__*/jsx("span", {
                className: "did-wallet-spinner"
              }), !loading && /*#__PURE__*/jsx("span", {
                className: "did-secure-icon",
                children: /*#__PURE__*/jsx(LockIcon, {})
              })]
            }), /*#__PURE__*/jsxs("button", {
              className: "did-wallet-btn",
              onClick: () => handleWalletLogin('walletconnect'),
              disabled: loading,
              children: [/*#__PURE__*/jsx(WalletConnectIcon$1, {}), /*#__PURE__*/jsx("span", {
                children: msg.walletConnect
              }), /*#__PURE__*/jsx("span", {
                className: "did-secure-icon",
                children: /*#__PURE__*/jsx(LockIcon, {})
              })]
            }), /*#__PURE__*/jsxs("button", {
              className: "did-wallet-btn",
              onClick: () => handleWalletLogin('trust'),
              disabled: loading,
              children: [/*#__PURE__*/jsx(TrustWalletIcon$1, {}), /*#__PURE__*/jsx("span", {
                children: "Trust Wallet"
              }), /*#__PURE__*/jsx("span", {
                className: "did-secure-icon",
                children: /*#__PURE__*/jsx(LockIcon, {})
              })]
            })]
          })]
        }), /*#__PURE__*/jsx("div", {
          className: "did-login-divider",
          children: /*#__PURE__*/jsx("span", {
            children: msg.orDivider
          })
        }), /*#__PURE__*/jsx("div", {
          className: "did-login-right",
          children: mode === 'login' ? /*#__PURE__*/jsxs(Fragment, {
            children: [/*#__PURE__*/jsxs("h3", {
              children: [/*#__PURE__*/jsx("span", {
                className: "did-login-icon",
                children: "\uD83C\uDD94"
              }), msg.didSection]
            }), savedAccounts.length > 0 && /*#__PURE__*/jsxs("div", {
              className: "did-saved-accounts",
              children: [/*#__PURE__*/jsx("p", {
                className: "did-saved-label",
                children: msg.savedAccounts
              }), savedAccounts.slice(0, 3).map(account => /*#__PURE__*/jsxs("button", {
                className: `did-saved-account ${currentSession?.did === account.did ? 'current' : ''}`,
                onClick: () => handleQuickLogin(account),
                children: [/*#__PURE__*/jsx("div", {
                  className: "did-saved-avatar",
                  children: account.avatar ? /*#__PURE__*/jsx("img", {
                    src: account.avatar,
                    alt: ""
                  }) : /*#__PURE__*/jsx("span", {
                    children: "\uD83D\uDD10"
                  })
                }), /*#__PURE__*/jsxs("div", {
                  className: "did-saved-info",
                  children: [/*#__PURE__*/jsxs("span", {
                    className: "did-saved-name",
                    children: [account.displayName || formatDID$1(account.did), account.verified && /*#__PURE__*/jsx(VerifiedIcon, {})]
                  }), /*#__PURE__*/jsx("span", {
                    className: "did-saved-did",
                    children: formatDID$1(account.did)
                  })]
                }), currentSession?.did === account.did ? /*#__PURE__*/jsx("span", {
                  className: "did-saved-status current",
                  children: msg.relogin
                }) : /*#__PURE__*/jsx("span", {
                  className: "did-saved-status",
                  children: msg.quickLogin
                })]
              }, account.did))]
            }), /*#__PURE__*/jsxs("div", {
              className: "did-login-form",
              children: [/*#__PURE__*/jsx("div", {
                className: "did-input-wrapper",
                children: /*#__PURE__*/jsx("input", {
                  type: "text",
                  className: "did-login-input",
                  placeholder: msg.didPlaceholder,
                  value: did,
                  onChange: e => setDid(e.target.value),
                  disabled: loading
                })
              }), /*#__PURE__*/jsxs("div", {
                className: "did-input-wrapper",
                children: [/*#__PURE__*/jsx("input", {
                  id: "did-password-input",
                  type: "password",
                  className: "did-login-input",
                  placeholder: msg.passwordPlaceholder,
                  value: password,
                  onChange: e => setPassword(e.target.value),
                  disabled: loading,
                  onKeyDown: e => e.key === 'Enter' && handleDIDLogin()
                }), /*#__PURE__*/jsx("span", {
                  className: "did-input-icon",
                  children: /*#__PURE__*/jsx(LockIcon, {})
                })]
              }), /*#__PURE__*/jsxs("button", {
                className: "did-login-submit",
                onClick: handleDIDLogin,
                disabled: loading || !did.trim() || !password,
                children: [/*#__PURE__*/jsx(ShieldIcon, {}), loading ? msg.loggingIn : msg.loginButton]
              })]
            }), showRegister && /*#__PURE__*/jsxs("p", {
              className: "did-login-switch",
              children: [msg.newToDID, " ", /*#__PURE__*/jsx("button", {
                onClick: () => setMode('register'),
                children: msg.createDID
              })]
            }), showSocialLogin && /*#__PURE__*/jsxs("div", {
              className: "did-social-login-container",
              style: {
                margin: '15px 0 5px 0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%'
              },
              children: [/*#__PURE__*/jsxs("div", {
                className: "did-social-divider",
                style: {
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  margin: '10px 0 15px 0',
                  color: '#6b7280',
                  fontSize: '13px'
                },
                children: [/*#__PURE__*/jsx("div", {
                  style: {
                    flex: 1,
                    height: '1px',
                    backgroundColor: 'rgba(255,255,255,0.08)'
                  }
                }), /*#__PURE__*/jsx("span", {
                  style: {
                    padding: '0 10px'
                  },
                  children: msg.orDivider || '或'
                }), /*#__PURE__*/jsx("div", {
                  style: {
                    flex: 1,
                    height: '1px',
                    backgroundColor: 'rgba(255,255,255,0.08)'
                  }
                })]
              }), renderSocialLogin ? typeof renderSocialLogin === 'function' ? renderSocialLogin() : renderSocialLogin : /*#__PURE__*/jsx(OAuthIconGroup, {
                providers: socialProviders,
                onSuccess: handleSocialSuccess,
                onError: handleSocialError
              })]
            })]
          }) : mode === 'register' ? /*#__PURE__*/jsxs(Fragment, {
            children: [/*#__PURE__*/jsxs("h3", {
              children: [/*#__PURE__*/jsx("span", {
                className: "did-login-icon",
                children: "\u2728"
              }), msg.registerSection]
            }), /*#__PURE__*/jsxs("div", {
              className: "did-login-form",
              children: [/*#__PURE__*/jsx("input", {
                type: "text",
                className: "did-login-input",
                placeholder: msg.nicknamePlaceholder,
                value: nickname,
                onChange: e => setNickname(e.target.value),
                disabled: loading
              }), /*#__PURE__*/jsx("input", {
                type: "email",
                className: "did-login-input",
                placeholder: msg.emailPlaceholder,
                value: email,
                onChange: e => setEmail(e.target.value),
                disabled: loading
              }), /*#__PURE__*/jsxs("div", {
                className: "did-input-wrapper",
                children: [/*#__PURE__*/jsx("input", {
                  type: "password",
                  className: "did-login-input",
                  placeholder: msg.passwordPlaceholder,
                  value: password,
                  onChange: e => setPassword(e.target.value),
                  disabled: loading
                }), /*#__PURE__*/jsx("span", {
                  className: "did-input-icon",
                  children: /*#__PURE__*/jsx(LockIcon, {})
                })]
              }), /*#__PURE__*/jsxs("div", {
                className: "did-input-wrapper",
                children: [/*#__PURE__*/jsx("input", {
                  type: "password",
                  className: "did-login-input",
                  placeholder: msg.confirmPasswordPlaceholder,
                  value: confirmPassword,
                  onChange: e => setConfirmPassword(e.target.value),
                  disabled: loading,
                  onKeyDown: e => e.key === 'Enter' && handleRegister()
                }), /*#__PURE__*/jsx("span", {
                  className: "did-input-icon",
                  children: /*#__PURE__*/jsx(LockIcon, {})
                })]
              }), /*#__PURE__*/jsxs("button", {
                className: "did-login-submit",
                onClick: handleRegister,
                disabled: loading || !nickname.trim() || !password,
                children: [/*#__PURE__*/jsx(ShieldIcon, {}), loading ? msg.registering : msg.registerButton]
              })]
            }), /*#__PURE__*/jsxs("p", {
              className: "did-login-switch",
              children: [msg.alreadyHaveDID, " ", /*#__PURE__*/jsx("button", {
                onClick: () => setMode('login'),
                children: msg.goLogin
              })]
            }), showSocialLogin && /*#__PURE__*/jsxs("div", {
              className: "did-social-login-container",
              style: {
                margin: '15px 0 5px 0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%'
              },
              children: [/*#__PURE__*/jsxs("div", {
                className: "did-social-divider",
                style: {
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  margin: '10px 0 15px 0',
                  color: '#6b7280',
                  fontSize: '13px'
                },
                children: [/*#__PURE__*/jsx("div", {
                  style: {
                    flex: 1,
                    height: '1px',
                    backgroundColor: 'rgba(255,255,255,0.08)'
                  }
                }), /*#__PURE__*/jsx("span", {
                  style: {
                    padding: '0 10px'
                  },
                  children: msg.orDivider || '或'
                }), /*#__PURE__*/jsx("div", {
                  style: {
                    flex: 1,
                    height: '1px',
                    backgroundColor: 'rgba(255,255,255,0.08)'
                  }
                })]
              }), renderSocialLogin ? typeof renderSocialLogin === 'function' ? renderSocialLogin() : renderSocialLogin : /*#__PURE__*/jsx(OAuthIconGroup, {
                providers: socialProviders,
                onSuccess: handleSocialSuccess,
                onError: handleSocialError
              })]
            })]
          }) : null
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "did-login-footer",
        style: {
          padding: '16px 32px'
        },
        children: [/*#__PURE__*/jsxs("p", {
          style: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            marginBottom: '4px'
          },
          children: [/*#__PURE__*/jsx("span", {
            style: {
              color: '#22c55e',
              display: 'flex'
            },
            children: /*#__PURE__*/jsx(LockIcon, {
              size: 14
            })
          }), /*#__PURE__*/jsx("span", {
            style: {
              color: '#888'
            },
            children: msg.securityNote
          })]
        }), /*#__PURE__*/jsxs("p", {
          style: {
            color: '#aaa',
            fontSize: '12px'
          },
          children: ["\uFFFD ", msg.poweredBy]
        })]
      })]
    })
  }), document.body);
}

/**
 * QuantaBit Auth SDK - 完整 API 客户端
 * 
 * 核心 API 服务，对接 qbit_sdk 后端所有模块：
 * - 认证 (Auth)
 * - DID 管理 (DID)
 * - 用户资料 (Profile)
 * - 钱包绑定 (Wallet)
 * - 凭证管理 (Credentials)
 * - 声誉系统 (Reputation)
 * - 社交绑定 (Social)
 * - 验证服务 (Verification)
 * - OAuth 授权 (OAuth)
 * 
 * 统一配置：
 * - 所有 SDK 共享 @quantabit/sdk-config 的配置
 * - 修改 sdk-config 配置后，所有 SDK 同时生效
 */


/**
 * 获取当前配置（优先使用统一配置）
 */
function getGlobalConfig() {
  try {
    const config = getConfig();
    if (config) return config;
  } catch (e) {
    // Ignore fallback
  }
  return {
    apiBaseUrl: '/api/v1',
    timeout: 30000,
    tokenStorageKey: 'qbit_did_access_token',
    refreshTokenStorageKey: 'qbit_did_refresh_token',
    currentSessionKey: 'qbit_did_current',
    autoRefreshToken: true,
    debug: false
  };
}
const DEFAULT_API_BASE = '/api/v1';
class QbitDIDApiClient {
  constructor(config = {}) {
    // 如果没有传入 apiBaseUrl，尝试从统一配置获取
    const globalConfig = getGlobalConfig();
    this.baseUrl = config.apiBaseUrl || globalConfig.apiBaseUrl || DEFAULT_API_BASE;
    this.timeout = config.timeout || globalConfig.timeout || 30000;
    this.onTokenRefresh = config.onTokenRefresh;
    this.onAuthError = config.onAuthError;
    this.onTokenExpired = config.onTokenExpired;
    this.autoRefreshToken = config.autoRefreshToken ?? globalConfig.autoRefreshToken ?? true;
    this._refreshPromise = null;
    this._isRefreshing = false; // 防止刷新递归

    // Token 存储键可配置
    this._tokenKey = config.tokenStorageKey || globalConfig.tokenStorageKey || 'qbit_did_access_token';
    this._refreshTokenKey = config.refreshTokenStorageKey || globalConfig.refreshTokenStorageKey || 'qbit_did_refresh_token';
    this._sessionKey = config.currentSessionKey || globalConfig.currentSessionKey || 'qbit_did_current';

    // 订阅配置变更（如果 sdk-config 可用，使用 WeakRef 避免内存泄漏）
    if (typeof subscribeConfig === 'function') {
      if (typeof WeakRef !== 'undefined') {
        const weakSelf = new WeakRef(this);
        let unsubscribe;
        const listener = newConfig => {
          const self = weakSelf.deref();
          if (self) {
            // 动态更新配置
            if (!config.apiBaseUrl) {
              self.baseUrl = newConfig.apiBaseUrl;
            }
            if (!config.timeout) {
              self.timeout = newConfig.timeout;
            }
          } else if (unsubscribe) {
            unsubscribe();
          }
        };
        unsubscribe = subscribeConfig(listener);
        this._unsubscribe = unsubscribe;
      } else {
        this._unsubscribe = subscribeConfig(newConfig => {
          // 动态更新配置
          if (!config.apiBaseUrl) {
            this.baseUrl = newConfig.apiBaseUrl;
          }
          if (!config.timeout) {
            this.timeout = newConfig.timeout;
          }
        });
      }
    }
  }

  /**
   * 配置 API
   */
  configure(config) {
    if (config.apiBaseUrl) this.baseUrl = config.apiBaseUrl;
    if (config.timeout) this.timeout = config.timeout;
    if (config.onTokenRefresh) this.onTokenRefresh = config.onTokenRefresh;
    if (config.onAuthError) this.onAuthError = config.onAuthError;
    if (config.onTokenExpired) this.onTokenExpired = config.onTokenExpired;
    if (config.autoRefreshToken !== undefined) this.autoRefreshToken = config.autoRefreshToken;
  }

  /**
   * 销毁实例，主动取消全局配置订阅，防止内存泄漏
   */
  destroy() {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
  }

  // ============ 通用请求方法 ============

  async request(endpoint, options = {}) {
    const {
      params: rawParams,
      ...fetchOptions
    } = options;
    const params = rawParams ? new URLSearchParams() : null;
    if (params) {
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }
    const queryString = params && Array.from(params.keys()).length > 0 ? `?${params.toString()}` : '';
    const url = `${this.baseUrl}${endpoint}${queryString}`;
    const token = this.getToken();
    const isFormData = typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData;
    const headers = {
      ...options.headers
    };
    if (!isFormData && headers['Content-Type'] !== null) {
      headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);
    try {
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (!response.ok) {
        // 401 自动刷新 Token（防止递归：刷新请求本身不触发二次刷新）
        if (response.status === 401 && this.autoRefreshToken && !this._isRefreshing) {
          const refreshed = await this._tryRefreshToken();
          if (refreshed) {
            // 用新 Token 重试原始请求
            headers['Authorization'] = `Bearer ${this.getToken()}`;
            return this.request(endpoint, {
              ...options,
              _retried: true
            });
          }
          this.clearTokens();
          this.onTokenExpired?.();
        }
        const error = await response.json().catch(() => ({}));
        // 提取可读的错误消息（防止 [object Object]）
        let errorMsg = error.detail || error.message;
        if (Array.isArray(errorMsg)) {
          errorMsg = errorMsg[0]?.msg || errorMsg[0]?.message || JSON.stringify(errorMsg);
        } else if (typeof errorMsg === 'object' && errorMsg !== null) {
          errorMsg = errorMsg.msg || errorMsg.message || JSON.stringify(errorMsg);
        }
        const err = new Error(errorMsg || `HTTP ${response.status}`);
        err.code = error.code || `HTTP_${response.status}`;
        err.status = response.status;
        this.onAuthError?.(err);
        throw err;
      }
      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }
  async _tryRefreshToken() {
    if (this._refreshPromise) {
      return this._refreshPromise;
    }
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return false;
    this._isRefreshing = true; // 防止递归
    this._refreshPromise = (async () => {
      try {
        const result = await this.request('/auth/refresh', {
          method: 'POST',
          body: JSON.stringify({
            refresh_token: refreshToken
          })
        });
        const data = result.data || result;
        this.setTokens(data.access_token, data.refresh_token);
        return true;
      } catch {
        this.clearTokens();
        return false;
      } finally {
        this._refreshPromise = null;
        this._isRefreshing = false;
      }
    })();
    return this._refreshPromise;
  }

  // ============ Token 管理 ============

  getToken() {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(this._tokenKey);
  }
  getRefreshToken() {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(this._refreshTokenKey);
  }
  setTokens(accessToken, refreshToken) {
    if (typeof window === 'undefined') return;
    localStorage.setItem(this._tokenKey, accessToken);
    if (refreshToken) {
      localStorage.setItem(this._refreshTokenKey, refreshToken);
    }
    this.onTokenRefresh?.({
      accessToken,
      refreshToken
    });
  }
  clearTokens() {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(this._tokenKey);
    localStorage.removeItem(this._refreshTokenKey);
    localStorage.removeItem(this._sessionKey);
  }
  isAuthenticated() {
    return !!this.getToken();
  }
  setAccessToken(token) {
    this.setTokens(token);
  }
  async loginWithWallet({
    walletAddress,
    signature,
    message,
    chainType = 'quantabit'
  }) {
    return this.walletLogin(walletAddress, signature, message, chainType);
  }

  // ============ 认证 API ============

  async getNonce(walletAddress, chainType = 'quantabit') {
    return this.request('/auth/nonce', {
      method: 'POST',
      body: JSON.stringify({
        wallet_address: walletAddress,
        chain_type: chainType
      })
    });
  }
  async walletLogin(walletAddress, signature, message, chainType = 'quantabit') {
    return this.request('/auth/wallet', {
      method: 'POST',
      body: JSON.stringify({
        wallet_address: walletAddress,
        signature,
        message,
        chain_type: chainType
      })
    });
  }
  async didLogin(did, password, pin = null, totpCode = null) {
    return this.request('/auth/did-login', {
      method: 'POST',
      body: JSON.stringify({
        did: did.trim(),
        password,
        pin,
        totp_code: totpCode
      })
    });
  }
  async register(data) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        username: data.displayName || data.nickname,
        nickname: data.displayName || data.nickname,
        display_name: data.displayName || data.nickname,
        email: data.email || undefined,
        password: data.password
      })
    });
  }
  async refreshToken(refreshToken) {
    return this.request('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({
        refresh_token: refreshToken
      })
    });
  }
  async logout() {
    try {
      await this.request('/auth/logout', {
        method: 'POST',
        body: '{}'
      });
    } catch {
      // Ignore errors
    }
    this.clearTokens();
  }
  async setPin(did, password, pin) {
    return this.request('/auth/set-pin', {
      method: 'POST',
      body: JSON.stringify({
        did,
        password,
        pin
      })
    });
  }

  // ============ DID API ============

  async getMyDID() {
    return this.request('/did/me', {
      method: 'GET'
    });
  }
  async resolveDID(did) {
    return this.request(`/did/resolve/${encodeURIComponent(did)}`, {
      method: 'GET'
    });
  }
  async resolveDIDFromChain(did) {
    return this.request(`/chain/did/resolve/${encodeURIComponent(did)}`, {
      method: 'GET'
    });
  }
  async createDID(options = {}) {
    return this.request('/did/create', {
      method: 'POST',
      body: JSON.stringify({
        initial_keys: options.initialKeys,
        services: options.services
      })
    });
  }
  async updateDIDDocument(operation) {
    return this.request('/did/document', {
      method: 'PUT',
      body: JSON.stringify(operation)
    });
  }
  async registerDIDOnChain() {
    return this.request('/chain/did/register', {
      method: 'POST',
      body: '{}'
    });
  }
  async submitDIDRegisterTxHash(txHash) {
    return this.request('/chain/did/register/submit', {
      method: 'POST',
      body: JSON.stringify({
        tx_hash: txHash
      })
    });
  }
  async updateDIDOnChain() {
    return this.request('/chain/did/update', {
      method: 'POST',
      body: '{}'
    });
  }
  async submitDIDUpdateTxHash(txHash) {
    return this.request('/chain/did/update/submit', {
      method: 'POST',
      body: JSON.stringify({
        tx_hash: txHash
      })
    });
  }
  async verifyDIDOnChain(did) {
    return this.request(`/chain/did/verify/${encodeURIComponent(did)}`, {
      method: 'GET'
    });
  }

  // ============ Profile API ============

  async getProfile() {
    return this.request('/profile/me', {
      method: 'GET'
    });
  }
  async getMe() {
    return this.getProfile();
  }
  async updateProfile(data) {
    return this.request('/profile/me', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }
  async getPublicProfile(did) {
    return this.request(`/profile/public/${encodeURIComponent(did)}`, {
      method: 'GET'
    });
  }
  async updatePrivacySettings(settings) {
    return this.request('/profile/privacy', {
      method: 'PUT',
      body: JSON.stringify({
        privacy_settings: settings
      })
    });
  }
  async uploadAvatar(file) {
    const formData = new FormData();
    formData.append('file', file);
    return this.request('/profile/avatar', {
      method: 'POST',
      headers: {},
      // 让浏览器自动设置 Content-Type
      body: formData
    });
  }

  // ============ 钱包绑定 API ============

  async getWalletBindings() {
    return this.request('/wallet/bindings', {
      method: 'GET'
    });
  }
  async bindWallet(walletAddress, chainType, signature, message, bindingType = 'secondary') {
    return this.request('/wallet/bind', {
      method: 'POST',
      body: JSON.stringify({
        wallet_address: walletAddress,
        chain_type: chainType,
        signature,
        message,
        binding_type: bindingType
      })
    });
  }
  async unbindWallet(bindingId, signature) {
    return this.request('/wallet/unbind', {
      method: 'POST',
      body: JSON.stringify({
        binding_id: bindingId,
        signature
      })
    });
  }
  async setPrimaryWallet(bindingId) {
    return this.request('/wallet/set-primary', {
      method: 'POST',
      body: JSON.stringify({
        binding_id: bindingId
      })
    });
  }
  async getBindingMessage(walletAddress, chainType = 'quantabit') {
    return this.request('/wallet/binding-message', {
      method: 'GET',
      params: {
        wallet_address: walletAddress,
        chain_type: chainType
      }
    });
  }

  // ============ 凭证 API ============

  async getCredentials(type) {
    const params = type ? `?type=${encodeURIComponent(type)}` : '';
    return this.request(`/credentials${params}`, {
      method: 'GET'
    });
  }
  async getCredential(credentialId) {
    return this.request(`/credentials/${encodeURIComponent(credentialId)}`, {
      method: 'GET'
    });
  }
  async createPresentation(credentialIds, disclosedFields, challenge, domain) {
    return this.request('/credentials/present', {
      method: 'POST',
      body: JSON.stringify({
        credential_ids: credentialIds,
        disclosed_fields: disclosedFields,
        challenge,
        domain
      })
    });
  }
  async verifyPresentation(presentation) {
    return this.request('/credentials/verify', {
      method: 'POST',
      body: JSON.stringify({
        presentation
      })
    });
  }
  async requestCredential(issuer, type, claims) {
    return this.request('/credentials/request', {
      method: 'POST',
      body: JSON.stringify({
        issuer,
        type,
        claims
      })
    });
  }
  async revokeCredential(credentialId, reason) {
    return this.request('/credentials/revoke', {
      method: 'POST',
      body: JSON.stringify({
        credential_id: credentialId,
        reason
      })
    });
  }
  async anchorCredentialOnChain(credentialId) {
    return this.request(`/chain/credential/${encodeURIComponent(credentialId)}/anchor`, {
      method: 'POST',
      body: '{}'
    });
  }
  async submitCredentialAnchorTxHash(credentialId, txHash) {
    return this.request(`/chain/credential/${encodeURIComponent(credentialId)}/anchor/submit`, {
      method: 'POST',
      body: JSON.stringify({
        tx_hash: txHash
      })
    });
  }
  async revokeCredentialOnChain(credentialId, reason = '') {
    return this.request(`/chain/credential/${encodeURIComponent(credentialId)}/revoke`, {
      method: 'POST',
      body: JSON.stringify({
        reason
      })
    });
  }
  async submitCredentialRevokeTxHash(credentialId, txHash) {
    return this.request(`/chain/credential/${encodeURIComponent(credentialId)}/revoke/submit`, {
      method: 'POST',
      body: JSON.stringify({
        tx_hash: txHash
      })
    });
  }

  // ============ 声誉 API ============

  async getMyReputation() {
    return this.request('/reputation/me', {
      method: 'GET'
    });
  }
  async getReputation(did) {
    return this.request(`/reputation/${encodeURIComponent(did)}`, {
      method: 'GET'
    });
  }
  async getMyBadges() {
    return this.request('/reputation/badges', {
      method: 'GET'
    });
  }
  async getLeaderboard(dimension = 'overall', limit = 20) {
    return this.request(`/reputation/leaderboard?dimension=${dimension}&limit=${limit}`, {
      method: 'GET'
    });
  }
  async getReputationHistory(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return this.request(`/reputation/history?${params.toString()}`, {
      method: 'GET'
    });
  }

  // ============ 社交绑定 API ============

  async getSocialBindings() {
    return this.request('/social/bindings', {
      method: 'GET'
    });
  }
  async initSocialBinding(platform, returnUrl) {
    return this.request('/social/bind/init', {
      method: 'POST',
      body: JSON.stringify({
        platform,
        return_url: returnUrl
      })
    });
  }
  async completeSocialBinding(bindingId, verificationCode) {
    return this.request('/social/bind/complete', {
      method: 'POST',
      body: JSON.stringify({
        binding_id: bindingId,
        verification_code: verificationCode
      })
    });
  }
  async unbindSocial(platform) {
    return this.request('/social/unbind', {
      method: 'POST',
      body: JSON.stringify({
        platform
      })
    });
  }
  async verifySocialAccount(platform, proof) {
    return this.request('/social/verify', {
      method: 'POST',
      body: JSON.stringify({
        platform,
        proof
      })
    });
  }

  // ============ 验证 API ============

  async getVerificationStatus() {
    return this.request('/verification/status', {
      method: 'GET'
    });
  }
  async startKYC(level = 'basic') {
    return this.request('/verification/kyc/start', {
      method: 'POST',
      body: JSON.stringify({
        level
      })
    });
  }
  async submitKYCDocument(documentType, file) {
    const formData = new FormData();
    formData.append('document_type', documentType);
    formData.append('file', file);
    return this.request('/verification/kyc/document', {
      method: 'POST',
      headers: {},
      body: formData
    });
  }
  async getKYCStatus() {
    return this.request('/verification/kyc/status', {
      method: 'GET'
    });
  }
  async verifyEmail(token) {
    return this.request('/verification/email/verify', {
      method: 'POST',
      body: JSON.stringify({
        token
      })
    });
  }
  async sendEmailVerification(email) {
    return this.request('/verification/email/send', {
      method: 'POST',
      body: JSON.stringify({
        email
      })
    });
  }
  async verifyPhone(code) {
    return this.request('/verification/phone/verify', {
      method: 'POST',
      body: JSON.stringify({
        code
      })
    });
  }
  async sendPhoneVerification(phone) {
    return this.request('/verification/phone/send', {
      method: 'POST',
      body: JSON.stringify({
        phone
      })
    });
  }

  // ============ OAuth 授权 API ============

  async getAvailableScopes() {
    return this.request('/oauth/scopes', {
      method: 'GET'
    });
  }
  async createAuthorizationRequest(appId, appName, redirectUri, scopes, state) {
    return this.request('/oauth/authorize', {
      method: 'POST',
      body: JSON.stringify({
        app_id: appId,
        app_name: appName,
        redirect_uri: redirectUri,
        scopes,
        state
      })
    });
  }
  async approveAuthorization(requestId, appId, scopes, durationDays = 30) {
    return this.request('/oauth/approve', {
      method: 'POST',
      body: JSON.stringify({
        request_id: requestId,
        app_id: appId,
        scopes,
        duration_days: durationDays
      })
    });
  }
  async getAuthorizations() {
    return this.request('/oauth/authorizations', {
      method: 'GET'
    });
  }
  async revokeAuthorization(appId) {
    return this.request('/oauth/revoke', {
      method: 'POST',
      body: JSON.stringify({
        app_id: appId
      })
    });
  }

  // ============ 多账户同步 API ============

  async syncProfileToWallet() {
    return this.request('/sync/profile', {
      method: 'POST'
    });
  }
  async syncDIDToWallet() {
    return this.request('/sync/did', {
      method: 'POST'
    });
  }
  async getAggregatedBalance() {
    return this.request('/sync/balance', {
      method: 'GET'
    });
  }
  async getTransactions(chainType, limit = 20, offset = 0) {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset)
    });
    if (chainType) params.append('chain_type', chainType);
    return this.request(`/sync/transactions?${params.toString()}`, {
      method: 'GET'
    });
  }
  async getHoldings() {
    return this.request('/sync/holdings', {
      method: 'GET'
    });
  }

  // ============ 健康检查 ============

  async healthCheck() {
    return this.request('/health', {
      method: 'GET'
    });
  }
  // ============ 隐私合规 (GDPR/CCPA) ============

  /**
   * 清除所有本地认证数据 — GDPR Art.17 被遗忘权
   * 清除 token、DID 缓存、会话数据
   */
  clearAllUserData() {
    this.clearTokens();
    if (typeof localStorage !== 'undefined') {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('qbit_did_') || key.startsWith('qbit_auth_') || key.startsWith('qbit_wallet_') || key.startsWith('qbit_credential_'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
    }

    // 清除配置订阅
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
    return {
      success: true,
      action: 'auth_local_data_cleared'
    };
  }

  /**
   * 导出用户身份数据 — GDPR Art.20 数据可携权
   * @returns {Promise<object>} 身份数据导出
   */
  async exportMyData() {
    try {
      return await this.request('/privacy/export', {
        method: 'GET'
      });
    } catch (e) {
      // 回退：聚合多个端点数据
      const [profile, did, wallets, credentials, reputation] = await Promise.allSettled([this.getProfile(), this.getMyDID(), this.getWalletBindings(), this.getCredentials(), this.getMyReputation()]);
      return {
        sdk: '@quantabit/did-auth-sdk',
        exportedAt: new Date().toISOString(),
        profile: profile.status === 'fulfilled' ? profile.value?.data : null,
        did: did.status === 'fulfilled' ? did.value?.data : null,
        wallets: wallets.status === 'fulfilled' ? wallets.value?.data : null,
        credentials: credentials.status === 'fulfilled' ? credentials.value?.data : null,
        reputation: reputation.status === 'fulfilled' ? reputation.value?.data : null
      };
    }
  }

  /**
   * 请求删除服务端账户数据 — GDPR Art.17
   * @returns {Promise<object>}
   */
  async requestDataDeletion() {
    return this.request('/privacy/delete-request', {
      method: 'POST',
      body: JSON.stringify({
        reason: 'gdpr_right_to_erasure'
      })
    });
  }

  /**
   * 获取隐私数据声明
   */
  getDataDisclosure() {
    return {
      sdk: '@quantabit/did-auth-sdk',
      privacyLevel: 'essential',
      consentRequired: false,
      collected: [{
        type: 'did_identity',
        description: 'Decentralized identifier & DID Document',
        retention: 'Account lifetime'
      }, {
        type: 'wallet_bindings',
        description: 'Associated wallet addresses',
        retention: 'Until unbound'
      }, {
        type: 'credentials',
        description: 'Verifiable credentials (encrypted)',
        retention: 'Until revoked'
      }, {
        type: 'auth_tokens',
        description: 'JWT access/refresh tokens',
        retention: 'Session / 30 days'
      }, {
        type: 'profile',
        description: 'User profile information',
        retention: 'Account lifetime'
      }, {
        type: 'reputation',
        description: 'On-chain reputation score',
        retention: 'Persistent (blockchain)'
      }, {
        type: 'kyc_data',
        description: 'Identity verification documents',
        retention: '5 years (regulatory)'
      }],
      gdprCapabilities: ['export', 'delete', 'portability'],
      encryption: 'All credentials encrypted at rest; tokens use short-lived JWT',
      specialCategories: 'KYC documents are special category data under GDPR Art.9'
    };
  }

  // ============ 区块链相关 API ============

  /**
   * 从链上解析 DID
   * @param {string} did DID标识符
   * @returns {Promise<object>}
   */
  async resolveDIDFromChain(did) {
    return this.request(`/chain/did/resolve/${encodeURIComponent(did)}`, {
      method: 'GET'
    });
  }

  /**
   * 将当前用户的 DID 注册到链上
   * @returns {Promise<object>}
   */
  async registerDIDOnChain() {
    return this.request('/chain/did/register', {
      method: 'POST'
    });
  }

  /**
   * 提交 DID 注册广播后的主网 tx_hash
   * @param {string} txHash 交易哈希
   * @returns {Promise<object>}
   */
  async submitDIDRegisterTxHash(txHash) {
    return this.request('/chain/did/register/submit', {
      method: 'POST',
      body: JSON.stringify({
        tx_hash: txHash
      })
    });
  }

  /**
   * 更新链上 DID 文档
   * @returns {Promise<object>}
   */
  async updateDIDOnChain() {
    return this.request('/chain/did/update', {
      method: 'POST'
    });
  }

  /**
   * 提交 DID 更新广播后的主网 tx_hash
   * @param {string} txHash 交易哈希
   * @returns {Promise<object>}
   */
  async submitDIDUpdateTxHash(txHash) {
    return this.request('/chain/did/update/submit', {
      method: 'POST',
      body: JSON.stringify({
        tx_hash: txHash
      })
    });
  }

  /**
   * 验证链上 DID 状态
   * @param {string} did DID标识符
   * @returns {Promise<object>}
   */
  async verifyDIDOnChain(did) {
    return this.request(`/chain/did/verify/${encodeURIComponent(did)}`, {
      method: 'GET'
    });
  }

  /**
   * 将凭证锚定到链上
   * @param {string} credentialId 凭证ID
   * @returns {Promise<object>}
   */
  async anchorCredentialOnChain(credentialId) {
    return this.request(`/chain/credential/${encodeURIComponent(credentialId)}/anchor`, {
      method: 'POST'
    });
  }

  /**
   * 提交凭证锚定广播后的主网 tx_hash
   * @param {string} credentialId 凭证ID
   * @param {string} txHash 交易哈希
   * @returns {Promise<object>}
   */
  async submitCredentialAnchorTxHash(credentialId, txHash) {
    return this.request(`/chain/credential/${encodeURIComponent(credentialId)}/anchor/submit`, {
      method: 'POST',
      body: JSON.stringify({
        tx_hash: txHash
      })
    });
  }

  /**
   * 在链上撤销凭证
   * @param {string} credentialId 凭证ID
   * @param {string} reason 撤销原因
   * @returns {Promise<object>}
   */
  async revokeCredentialOnChain(credentialId, reason = "Revoked by issuer") {
    return this.request(`/chain/credential/${encodeURIComponent(credentialId)}/revoke?reason=${encodeURIComponent(reason)}`, {
      method: 'POST'
    });
  }

  /**
   * 提交凭证撤销广播后的主网 tx_hash
   * @param {string} credentialId 凭证ID
   * @param {string} txHash 交易哈希
   * @returns {Promise<object>}
   */
  async submitCredentialRevokeTxHash(credentialId, txHash) {
    return this.request(`/chain/credential/${encodeURIComponent(credentialId)}/revoke/submit`, {
      method: 'POST',
      body: JSON.stringify({
        tx_hash: txHash
      })
    });
  }

  // ============ 高级链上编排（一次调用：后端操作 → 钱包广播 → 确认 → 链上验证 → 回执提交） ============

  /**
   * 通用链上锚定编排
   *
   * 流程：调用后端操作接口 → 若需外部签名则用钱包 provider 广播 anchor memo →
   * （可选）等待确认 →（可选）链上回执验证 → 提交 tx_hash 回后端。
   *
   * @private
   * @param {Object} params
   * @param {Function} params.operation - 返回后端操作结果的函数（含 requires_external_signer / payload）
   * @param {Function} params.submit - (txHash) => Promise，提交 tx_hash 回后端
   * @param {Object} params.provider - QBit 钱包 provider
   * @param {Object} params.options - 编排选项
   * @returns {Promise<{operation, broadcast, submit, verified, requiresExternalSigner}>}
   */
  async _anchorWithWallet({
    operation,
    submit,
    provider,
    options = {}
  }) {
    const operationResult = await operation();

    // 后端托管完成（无需外部签名）：直接返回，不广播
    if (!operationResult?.requires_external_signer) {
      return {
        operation: operationResult,
        broadcast: null,
        submit: null,
        verified: null,
        requiresExternalSigner: false
      };
    }
    if (!provider) {
      throw new Error('该操作需要外部钱包签名，但未提供 provider');
    }
    const namespace = options.namespace || 'qbit_did';
    const network = options.network || 'mainnet';
    const connection = new QBitConnection({
      endpoint: options.endpoint,
      network
    });
    const broadcast = await broadcastQBitAnchor(connection, provider, operationResult.payload || {}, {
      namespace,
      address: options.address,
      priorityFee: options.priorityFee,
      skipPreflight: options.skipPreflight,
      waitForConfirmation: options.waitForConfirmation !== false,
      commitment: options.commitment,
      timeoutMs: options.confirmTimeoutMs
    });
    if (!broadcast.txHash) {
      throw new Error('钱包广播未返回 tx_hash');
    }

    // 链上回执验证（默认开启，可通过 options.verify=false 关闭）
    let verified = null;
    if (options.verify !== false) {
      verified = await verifyAnchoredMemo$1(connection, broadcast.txHash, {
        namespace,
        match: options.match,
        commitment: options.commitment
      });
    }
    const submitResult = await submit(broadcast.txHash);
    return {
      operation: operationResult,
      broadcast,
      submit: submitResult,
      verified,
      requiresExternalSigner: true
    };
  }

  /**
   * 一次性完成 DID 链上注册（后端操作 → 钱包广播 → 确认/验证 → 提交 tx_hash）
   * @param {Object} provider - QBit 钱包 provider
   * @param {Object} options - 编排选项
   */
  async registerDIDWithWallet(provider, options = {}) {
    return this._anchorWithWallet({
      operation: () => this.registerDIDOnChain(),
      submit: txHash => this.submitDIDRegisterTxHash(txHash),
      provider,
      options
    });
  }

  /**
   * 一次性完成 DID 链上更新
   * @param {Object} provider - QBit 钱包 provider
   * @param {Object} options - 编排选项
   */
  async updateDIDWithWallet(provider, options = {}) {
    return this._anchorWithWallet({
      operation: () => this.updateDIDOnChain(),
      submit: txHash => this.submitDIDUpdateTxHash(txHash),
      provider,
      options
    });
  }

  /**
   * 一次性完成凭证链上锚定
   * @param {string} credentialId - 凭证 ID
   * @param {Object} provider - QBit 钱包 provider
   * @param {Object} options - 编排选项
   */
  async anchorCredentialWithWallet(credentialId, provider, options = {}) {
    return this._anchorWithWallet({
      operation: () => this.anchorCredentialOnChain(credentialId),
      submit: txHash => this.submitCredentialAnchorTxHash(credentialId, txHash),
      provider,
      options
    });
  }

  /**
   * 一次性完成凭证链上吊销
   * @param {string} credentialId - 凭证 ID
   * @param {Object} provider - QBit 钱包 provider
   * @param {Object} options - 编排选项（reason 可通过 options.reason 传入）
   */
  async revokeCredentialWithWallet(credentialId, provider, options = {}) {
    return this._anchorWithWallet({
      operation: () => this.revokeCredentialOnChain(credentialId, options.reason || ''),
      submit: txHash => this.submitCredentialRevokeTxHash(credentialId, txHash),
      provider,
      options
    });
  }
}

// 创建单例实例
const apiClient = new QbitDIDApiClient();
function buildQBitAnchorMemo(payload, options = {}) {
  return buildQBitAnchorMemo$2(payload, options);
}
function createQBitMemoAnchorBroadcaster(options = {}) {
  const {
    provider,
    address,
    endpoint
  } = options;
  return async (payload, context) => {
    const conn = new QBitConnection(endpoint ? {
      endpoint
    } : {});
    const feePayer = address || typeof window !== 'undefined' && window.qbit && window.qbit.publicKey?.toBase58();
    if (!feePayer) {
      throw new Error('Fee payer address is required for broadcasting');
    }
    const {
      transaction
    } = await createQBitAnchorTransaction(conn, feePayer, payload, {
      namespace: 'qbit_did'
    });
    let activeProvider = provider;
    if (!activeProvider && typeof window !== 'undefined' && window.qbit) {
      activeProvider = window.qbit;
    }
    if (!activeProvider || typeof activeProvider.signAndSendTransaction !== 'function') {
      throw new Error('No active wallet provider supporting signAndSendTransaction found');
    }
    const response = await activeProvider.signAndSendTransaction(transaction);
    return response.signature || response.txHash || response;
  };
}
async function completeExternalChainOperation(payload, options = {}) {
  const {
    broadcaster,
    onSubmit
  } = options;
  if (typeof broadcaster !== 'function') {
    throw new Error('Broadcaster is required for external chain operation');
  }
  const txHash = await broadcaster(payload);
  if (typeof onSubmit === 'function') {
    await onSubmit(txHash);
  }
  return txHash;
}

var apiClient$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  QbitDIDApiClient: QbitDIDApiClient,
  apiClient: apiClient,
  buildQBitAnchorMemo: buildQBitAnchorMemo,
  completeExternalChainOperation: completeExternalChainOperation,
  createQBitMemoAnchorBroadcaster: createQBitMemoAnchorBroadcaster,
  default: apiClient
});

/**
 * Web3WalletPanel - 基于 Ant Design Web3 的钱包连接面板
 * 
 * 使用 @ant-design/web3 的 ConnectModal.ModalPanel 组件
 * 提供专业的 Web3 钱包连接 UI，替代原来的简单钱包按钮
 * 
 * 集成方式：作为纯 UI 组件，自行处理钱包连接逻辑
 */

const MetaMaskIcon = () => /*#__PURE__*/jsxs("svg", {
  viewBox: "0 0 40 40",
  width: "40",
  height: "40",
  children: [/*#__PURE__*/jsx("rect", {
    width: "40",
    height: "40",
    rx: "10",
    fill: "#F6851B"
  }), /*#__PURE__*/jsx("path", {
    d: "M29.3 10.5l-7.2 5.3 1.3-3.2 5.9-2.1z",
    fill: "#E2761B",
    stroke: "#E2761B",
    strokeWidth: "0.25"
  }), /*#__PURE__*/jsx("path", {
    d: "M10.7 10.5l7.1 5.4-1.2-3.3-5.9-2.1z",
    fill: "#E4761B",
    stroke: "#E4761B",
    strokeWidth: "0.25"
  }), /*#__PURE__*/jsx("path", {
    d: "M26.1 25.2l-1.9 2.9 4.1 1.1.6-4-2.8 0z",
    fill: "#E4761B",
    stroke: "#E4761B",
    strokeWidth: "0.25"
  }), /*#__PURE__*/jsx("path", {
    d: "M11.1 25.2l.6 4 4.1-1.1-1.9-2.9h-2.8z",
    fill: "#E4761B",
    stroke: "#E4761B",
    strokeWidth: "0.25"
  }), /*#__PURE__*/jsx("path", {
    d: "M15.5 19.2l-1.2 1.8 4.3.2-.2-4.6-2.9 2.6z",
    fill: "#E4761B",
    stroke: "#E4761B",
    strokeWidth: "0.25"
  }), /*#__PURE__*/jsx("path", {
    d: "M24.5 19.2l-3-2.7-.1 4.7 4.3-.2-1.2-1.8z",
    fill: "#E4761B",
    stroke: "#E4761B",
    strokeWidth: "0.25"
  }), /*#__PURE__*/jsx("path", {
    d: "M15.8 28.1l2.6-1.3-2.2-1.7-.4 3z",
    fill: "#E4761B",
    stroke: "#E4761B",
    strokeWidth: "0.25"
  }), /*#__PURE__*/jsx("path", {
    d: "M21.6 26.8l2.6 1.3-.4-3-2.2 1.7z",
    fill: "#E4761B",
    stroke: "#E4761B",
    strokeWidth: "0.25"
  })]
});
const WalletConnectIcon = () => /*#__PURE__*/jsxs("svg", {
  viewBox: "0 0 40 40",
  width: "40",
  height: "40",
  children: [/*#__PURE__*/jsx("rect", {
    width: "40",
    height: "40",
    rx: "10",
    fill: "#3B99FC"
  }), /*#__PURE__*/jsx("path", {
    d: "M13.5 17c3.6-3.6 9.4-3.6 13 0l.4.4c.2.2.2.5 0 .6l-1.3 1.3c-.1.1-.2.1-.3 0l-.6-.6c-2.5-2.5-6.6-2.5-9.1 0l-.6.6c-.1.1-.2.1-.3 0L13.4 18c-.2-.2-.2-.5 0-.6l.1-.4z",
    fill: "#fff"
  }), /*#__PURE__*/jsx("path", {
    d: "M28.5 19l1.2 1.2c.2.2.2.5 0 .6L25 25.6c-.2.2-.4.2-.6 0l-2.9-2.9c0-.1-.1-.1-.2 0l-2.9 2.9c-.2.2-.4.2-.6 0l-4.7-4.8c-.2-.2-.2-.5 0-.6L14.3 19c.2-.2.4-.2.6 0l2.9 2.9c0 .1.1.1.2 0L20.9 19c.2-.2.4-.2.6 0l2.9 2.9c0 .1.1.1.2 0l2.9-2.9c.2-.2.5-.2.7 0z",
    fill: "#fff"
  })]
});
const OKXIcon = () => /*#__PURE__*/jsxs("svg", {
  viewBox: "0 0 40 40",
  width: "40",
  height: "40",
  children: [/*#__PURE__*/jsx("rect", {
    width: "40",
    height: "40",
    rx: "10",
    fill: "#000"
  }), /*#__PURE__*/jsx("rect", {
    x: "10",
    y: "10",
    width: "8",
    height: "8",
    rx: "1.5",
    fill: "#fff"
  }), /*#__PURE__*/jsx("rect", {
    x: "22",
    y: "10",
    width: "8",
    height: "8",
    rx: "1.5",
    fill: "#fff"
  }), /*#__PURE__*/jsx("rect", {
    x: "10",
    y: "22",
    width: "8",
    height: "8",
    rx: "1.5",
    fill: "#fff"
  }), /*#__PURE__*/jsx("rect", {
    x: "22",
    y: "22",
    width: "8",
    height: "8",
    rx: "1.5",
    fill: "#fff"
  }), /*#__PURE__*/jsx("rect", {
    x: "16",
    y: "16",
    width: "8",
    height: "8",
    rx: "1.5",
    fill: "#fff"
  })]
});
const TrustWalletIcon = () => /*#__PURE__*/jsxs("svg", {
  viewBox: "0 0 40 40",
  width: "40",
  height: "40",
  children: [/*#__PURE__*/jsx("rect", {
    width: "40",
    height: "40",
    rx: "10",
    fill: "#0500FF"
  }), /*#__PURE__*/jsx("path", {
    d: "M20 10c0 0 7.5 3.5 7.5 10.5 0 7-7.5 11.5-7.5 11.5s-7.5-4.5-7.5-11.5C12.5 13.5 20 10 20 10z",
    fill: "none",
    stroke: "#fff",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/jsx("path", {
    d: "M20 14c0 0 4.5 2.5 4.5 6.5 0 4-4.5 7-4.5 7s-4.5-3-4.5-7C15.5 16.5 20 14 20 14z",
    fill: "#fff",
    opacity: "0.3"
  })]
});
const TokenPocketIcon = () => /*#__PURE__*/jsxs("svg", {
  viewBox: "0 0 40 40",
  width: "40",
  height: "40",
  children: [/*#__PURE__*/jsx("rect", {
    width: "40",
    height: "40",
    rx: "10",
    fill: "#2980FE"
  }), /*#__PURE__*/jsx("path", {
    d: "M14 12h12c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H14c-1.1 0-2-.9-2-2V14c0-1.1.9-2 2-2z",
    fill: "none",
    stroke: "#fff",
    strokeWidth: "1.5"
  }), /*#__PURE__*/jsx("path", {
    d: "M17 16h6v2h-6zM17 20h4v2h-4z",
    fill: "#fff",
    opacity: "0.9"
  })]
});

// QBit 钱包图标（品牌紫色）
const QBitWalletIcon = () => /*#__PURE__*/jsxs("svg", {
  viewBox: "0 0 40 40",
  width: "40",
  height: "40",
  children: [/*#__PURE__*/jsx("defs", {
    children: /*#__PURE__*/jsxs("linearGradient", {
      id: "qbit-grad",
      x1: "0%",
      y1: "0%",
      x2: "100%",
      y2: "100%",
      children: [/*#__PURE__*/jsx("stop", {
        offset: "0%",
        stopColor: "#7C3AED"
      }), /*#__PURE__*/jsx("stop", {
        offset: "100%",
        stopColor: "#6366F1"
      })]
    })
  }), /*#__PURE__*/jsx("rect", {
    width: "40",
    height: "40",
    rx: "10",
    fill: "url(#qbit-grad)"
  }), /*#__PURE__*/jsx("circle", {
    cx: "20",
    cy: "20",
    r: "8",
    fill: "none",
    stroke: "#fff",
    strokeWidth: "2.5"
  }), /*#__PURE__*/jsx("circle", {
    cx: "20",
    cy: "20",
    r: "3",
    fill: "#fff"
  }), /*#__PURE__*/jsx("circle", {
    cx: "25",
    cy: "25",
    r: "2.5",
    fill: "url(#qbit-grad)",
    stroke: "#fff",
    strokeWidth: "1.5"
  })]
});

// 钱包状态枚举
const WalletStatus = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  SIGNING: 'signing',
  SUCCESS: 'success',
  ERROR: 'error'
};

/**
 * Web3WalletPanel 组件
 * 
 * 提供 Ant Design Web3 风格的钱包连接面板
 * 替代原来 DarkLoginModal 中的简单钱包连接 UI
 * 
 * @param {Object} props
 * @param {Function} props.onConnect - 钱包连接成功回调 (address, walletType) => void
 * @param {Function} props.onSign - 签名完成回调 (signature, message, address) => void
 * @param {Function} props.onError - 错误回调 (error) => void
 * @param {string} props.language - 语言 'zh' | 'en'
 * @param {string} props.status - 外部控制的连接状态
 * @param {string} props.theme - 主题 'dark' | 'light'
 */
function Web3WalletPanel({
  onConnect,
  onSign,
  onError,
  language = 'zh',
  status: externalStatus,
  theme = 'dark',
  apiBaseUrl,
  layoutMode = 'double'
}) {
  const [internalStatus, setInternalStatus] = useState(WalletStatus.IDLE);
  const [selectedWallet, setSelectedWallet] = useState('qbit');
  const [hoverWallet, setHoverWallet] = useState(null);
  const [connectedAddress, setConnectedAddress] = useState('');
  const [error, setError] = useState(null);
  const status = externalStatus || internalStatus;
  const isZh = language === 'zh';

  // 按热度排序，QBit 排第一且默认选中
  const wallets = useMemo(() => [{
    id: 'qbit',
    name: isZh ? 'QBit 钱包' : 'QBit Wallet',
    icon: /*#__PURE__*/jsx(QBitWalletIcon, {}),
    description: isZh ? 'QBit 生态原生钱包，推荐使用' : 'QBit ecosystem native wallet, recommended',
    installed: typeof window !== 'undefined' && !!(window.qbit || window.phantom?.solana),
    tags: ['Recommended'],
    downloadUrl: 'https://qbitwallet.io',
    group: 'Popular',
    recommended: true
  }, {
    id: 'metamask',
    name: 'MetaMask',
    icon: /*#__PURE__*/jsx(MetaMaskIcon, {}),
    description: isZh ? '最受欢迎的以太坊钱包' : 'The most popular Ethereum wallet',
    installed: typeof window !== 'undefined' && !!window.ethereum,
    tags: ['Popular', 'EVM'],
    downloadUrl: 'https://metamask.io/download/',
    group: 'Popular'
  }, {
    id: 'okx',
    name: 'OKX Wallet',
    icon: /*#__PURE__*/jsx(OKXIcon, {}),
    description: isZh ? '多链全能 Web3 钱包' : 'Multi-chain Web3 wallet',
    installed: typeof window !== 'undefined' && !!window.okxwallet,
    tags: ['Multi-chain'],
    downloadUrl: 'https://www.okx.com/web3',
    group: 'Popular'
  }, {
    id: 'trust',
    name: 'Trust Wallet',
    icon: /*#__PURE__*/jsx(TrustWalletIcon, {}),
    description: isZh ? '全球领先的多链移动钱包' : 'Leading multi-chain mobile wallet',
    installed: typeof window !== 'undefined' && !!window.trustwallet,
    tags: ['Mobile', 'Multi-chain'],
    downloadUrl: 'https://trustwallet.com/',
    group: 'Popular'
  }, {
    id: 'walletconnect',
    name: 'WalletConnect',
    icon: /*#__PURE__*/jsx(WalletConnectIcon, {}),
    description: isZh ? '通过扫码连接移动端钱包' : 'Connect mobile wallets via QR code',
    installed: true,
    tags: ['Mobile'],
    group: 'Others'
  }, {
    id: 'tokenpocket',
    name: 'TokenPocket',
    icon: /*#__PURE__*/jsx(TokenPocketIcon, {}),
    description: isZh ? '支持多链的移动端钱包' : 'Multi-chain mobile wallet',
    installed: false,
    tags: ['Mobile'],
    downloadUrl: 'https://www.tokenpocket.pro/',
    group: 'Others'
  }], [isZh]);
  const walletGroups = useMemo(() => {
    const groups = {};
    wallets.forEach(w => {
      if (!groups[w.group]) groups[w.group] = [];
      groups[w.group].push(w);
    });
    return groups;
  }, [wallets]);
  const connectWallet = async walletId => {
    setSelectedWallet(walletId);
    setInternalStatus(WalletStatus.CONNECTING);
    setError(null);
    try {
      const chainType = ['metamask', 'coinbase', 'walletconnect'].includes(walletId) ? 'ethereum' : 'quantabit';
      const {
        address,
        provider,
        walletType
      } = await connectBrowserWallet(chainType, walletId);
      setConnectedAddress(address);
      setInternalStatus(WalletStatus.SUCCESS);
      if (onConnect) {
        await onConnect(address, walletType, provider);
      }
    } catch (err) {
      setError(err.message || (isZh ? '连接失败' : 'Failed to connect'));
      setInternalStatus(WalletStatus.ERROR);
      if (onError) onError(err);
    }
  };

  // 渲染引导信息（右侧面板）
  const renderGuide = () => {
    const guideItems = [{
      icon: '🔐',
      title: isZh ? '去中心化身份' : 'Decentralized Identity',
      desc: isZh ? '您的钱包地址即是您的身份凭证' : 'Your wallet address is your identity credential'
    }, {
      icon: '🛡️',
      title: isZh ? '安全签名验证' : 'Secure Signature Verification',
      desc: isZh ? '通过 QBit/Solana 钱包签名验证所有权，无需暴露私钥' : 'Verify ownership through your QBit/Solana wallet without exposing private keys'
    }, {
      icon: '🌐',
      title: isZh ? 'QBit 主网就绪' : 'QBit Mainnet Ready',
      desc: isZh ? '默认面向 QBit 主网 RPC 与 Solana 兼容钱包流程' : 'Built around QBit mainnet RPC and Solana-compatible wallet flows'
    }];
    return /*#__PURE__*/jsxs("div", {
      className: "web3-guide-panel",
      children: [/*#__PURE__*/jsx("h4", {
        className: "web3-guide-title",
        children: isZh ? '什么是钱包登录？' : 'What is wallet login?'
      }), /*#__PURE__*/jsx("div", {
        className: "web3-guide-items",
        children: guideItems.map((item, i) => /*#__PURE__*/jsxs("div", {
          className: "web3-guide-item",
          children: [/*#__PURE__*/jsx("span", {
            className: "web3-guide-icon",
            children: item.icon
          }), /*#__PURE__*/jsxs("div", {
            className: "web3-guide-text",
            children: [/*#__PURE__*/jsx("span", {
              className: "web3-guide-item-title",
              children: item.title
            }), /*#__PURE__*/jsx("span", {
              className: "web3-guide-item-desc",
              children: item.desc
            })]
          })]
        }, i))
      })]
    });
  };

  // 渲染连接中状态
  const renderConnecting = () => /*#__PURE__*/jsxs("div", {
    className: "web3-connecting-panel",
    children: [/*#__PURE__*/jsx("div", {
      className: "web3-connecting-icon",
      children: wallets.find(w => w.id === selectedWallet)?.icon
    }), /*#__PURE__*/jsx("div", {
      className: "web3-connecting-spinner"
    }), /*#__PURE__*/jsx("p", {
      className: "web3-connecting-text",
      children: isZh ? '正在连接...' : 'Connecting...'
    }), /*#__PURE__*/jsx("p", {
      className: "web3-connecting-hint",
      children: isZh ? '请在钱包中确认连接请求' : 'Please confirm the connection in your wallet'
    }), /*#__PURE__*/jsx("button", {
      className: "web3-cancel-btn",
      onClick: () => {
        setSelectedWallet(null);
        setInternalStatus(WalletStatus.IDLE);
      },
      children: isZh ? '取消' : 'Cancel'
    })]
  });

  // 渲染成功状态
  const renderSuccess = () => /*#__PURE__*/jsxs("div", {
    className: "web3-success-panel",
    children: [/*#__PURE__*/jsx("div", {
      className: "web3-success-icon",
      children: "\u2705"
    }), /*#__PURE__*/jsx("p", {
      className: "web3-success-text",
      children: isZh ? '连接成功！' : 'Connected!'
    }), /*#__PURE__*/jsx("code", {
      className: "web3-success-address",
      children: formatAddress(connectedAddress)
    }), /*#__PURE__*/jsx("p", {
      className: "web3-success-hint",
      children: isZh ? '正在验证签名...' : 'Verifying signature...'
    })]
  });

  // 渲染错误状态
  const renderError = () => /*#__PURE__*/jsxs("div", {
    className: "web3-error-panel",
    children: [/*#__PURE__*/jsx("div", {
      className: "web3-error-icon",
      children: "\u26A0\uFE0F"
    }), /*#__PURE__*/jsx("p", {
      className: "web3-error-text",
      children: error
    }), /*#__PURE__*/jsx("button", {
      className: "web3-retry-btn",
      onClick: () => {
        setError(null);
        setInternalStatus(WalletStatus.IDLE);
        setSelectedWallet(null);
      },
      children: isZh ? '重试' : 'Retry'
    })]
  });
  return /*#__PURE__*/jsxs("div", {
    className: `web3-wallet-panel web3-theme-${theme}${layoutMode === 'single' ? ' layout-single' : ''}`,
    children: [status === WalletStatus.CONNECTING && renderConnecting(), status === WalletStatus.SUCCESS && renderSuccess(), status === WalletStatus.ERROR && renderError(), status === WalletStatus.IDLE && /*#__PURE__*/jsxs("div", {
      className: `web3-wallet-layout${layoutMode === 'single' ? ' layout-single' : ''}`,
      children: [/*#__PURE__*/jsx("div", {
        className: "web3-wallet-list",
        children: Object.entries(walletGroups).map(([group, groupWallets]) => /*#__PURE__*/jsxs("div", {
          className: "web3-wallet-group",
          children: [layoutMode !== 'single' && /*#__PURE__*/jsx("div", {
            className: "web3-wallet-group-title",
            children: group
          }), groupWallets.map(wallet => /*#__PURE__*/jsxs("button", {
            className: `web3-wallet-item ${selectedWallet === wallet.id ? 'selected' : ''} ${hoverWallet === wallet.id ? 'hovered' : ''}`,
            onClick: () => connectWallet(wallet.id),
            onMouseEnter: () => setHoverWallet(wallet.id),
            onMouseLeave: () => setHoverWallet(null),
            children: [/*#__PURE__*/jsx("div", {
              className: "web3-wallet-icon",
              children: wallet.icon
            }), /*#__PURE__*/jsx("span", {
              className: "web3-wallet-name",
              children: wallet.name
            }), wallet.tags.length > 0 && /*#__PURE__*/jsx("span", {
              className: "web3-wallet-tag",
              children: wallet.tags[0]
            })]
          }, wallet.id))]
        }, group))
      }), layoutMode !== 'single' && /*#__PURE__*/jsx("div", {
        className: "web3-wallet-detail",
        children: hoverWallet ? /*#__PURE__*/jsxs("div", {
          className: "web3-wallet-preview",
          children: [/*#__PURE__*/jsx("div", {
            className: "web3-preview-icon",
            children: wallets.find(w => w.id === hoverWallet)?.icon
          }), /*#__PURE__*/jsx("h4", {
            className: "web3-preview-name",
            children: wallets.find(w => w.id === hoverWallet)?.name
          }), /*#__PURE__*/jsx("p", {
            className: "web3-preview-desc",
            children: wallets.find(w => w.id === hoverWallet)?.description
          }), !wallets.find(w => w.id === hoverWallet)?.installed && /*#__PURE__*/jsx("a", {
            href: wallets.find(w => w.id === hoverWallet)?.downloadUrl,
            target: "_blank",
            rel: "noopener noreferrer",
            className: "web3-download-link",
            children: isZh ? '下载安装 →' : 'Download →'
          })]
        }) : renderGuide()
      })]
    })]
  });
}

/**
 * QuantaBit Auth SDK - 暗色风格弹窗登录组件 (DarkLoginModal)
 * 
 * 单栏 Tab 切换布局的暗色风格弹窗，支持：
 * - 钱包登录
 * - DID 密码登录
 * - 邮箱注册（内置）
 * - 多账户管理
 * - 主题风格切换
 * - 全屏页面跳转
 */

const KeypadIcon = ({
  size = 20,
  className = ''
}) => /*#__PURE__*/jsxs("svg", {
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  className: className,
  children: [/*#__PURE__*/jsx("rect", {
    x: "5",
    y: "4",
    width: "2",
    height: "2",
    rx: "0.5"
  }), /*#__PURE__*/jsx("rect", {
    x: "11",
    y: "4",
    width: "2",
    height: "2",
    rx: "0.5"
  }), /*#__PURE__*/jsx("rect", {
    x: "17",
    y: "4",
    width: "2",
    height: "2",
    rx: "0.5"
  }), /*#__PURE__*/jsx("rect", {
    x: "5",
    y: "10",
    width: "2",
    height: "2",
    rx: "0.5"
  }), /*#__PURE__*/jsx("rect", {
    x: "11",
    y: "10",
    width: "2",
    height: "2",
    rx: "0.5"
  }), /*#__PURE__*/jsx("rect", {
    x: "17",
    y: "10",
    width: "2",
    height: "2",
    rx: "0.5"
  }), /*#__PURE__*/jsx("rect", {
    x: "5",
    y: "16",
    width: "2",
    height: "2",
    rx: "0.5"
  }), /*#__PURE__*/jsx("rect", {
    x: "11",
    y: "16",
    width: "2",
    height: "2",
    rx: "0.5"
  }), /*#__PURE__*/jsx("rect", {
    x: "17",
    y: "16",
    width: "2",
    height: "2",
    rx: "0.5"
  })]
});

// 可用主题列表
const AVAILABLE_THEMES = [{
  key: 'dark',
  label: '深色',
  labelEn: 'Dark',
  icon: MoonIcon
}, {
  key: 'light',
  label: '浅色',
  labelEn: 'Light',
  icon: SunIcon
}, {
  key: 'gradient',
  label: '渐变',
  labelEn: 'Gradient',
  icon: PaletteIcon
}, {
  key: 'glass',
  label: '毛玻璃',
  labelEn: 'Glass',
  icon: ShieldIcon
}];

/**
 * DarkLoginModal 组件
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - 是否显示弹窗
 * @param {Function} props.onClose - 关闭回调
 * @param {Function} props.onSuccess - 登录成功回调
 * @param {Function} props.onError - 错误回调
 * @param {Function} props.onLogout - 登出回调
 * @param {Function} props.onNavigateToRegister - 导航到外部注册页回调（可选，若不传则使用内置注册）
 * @param {Function} props.onOpenFullscreen - 打开全屏页面回调
 * @param {Function} props.onThemeChange - 主题切换回调 (theme: string) => void
 * @param {Function} props.onModeChange - 模式切换回调 (mode: 'compact' | 'full') => void
 * @param {string} props.theme - 当前主题 (dark/light/gradient)
 * @param {boolean} props.showToolbar - 是否显示工具栏 (default: true)
 * @param {string} props.apiBaseUrl - API 基础 URL
 * @param {string} props.appName - 应用名称
 * @param {string} props.appLogo - 应用 Logo URL
 * @param {string} props.language - 语言 (zh/en)
 * @param {boolean} props.showRegister - 是否显示注册选项
 * @param {Object} props.currentUser - 当前用户信息
 */
function DarkLoginModal({
  isOpen,
  onClose,
  onSuccess,
  onError,
  onLogout,
  onNavigateToRegister,
  onOpenFullscreen,
  onThemeChange,
  onModeChange,
  theme = 'dark',
  showToolbar = true,
  apiBaseUrl = '/api/v1',
  appName = 'QuantaBit',
  appLogo,
  language: initialLanguage,
  showRegister = true,
  showLanguageSwitch = true,
  currentUser = null,
  defaultLoginType = 'did',
  // wallet | did - 默认使用 DID 登录
  showSocialLogin = true,
  socialProviders = ['apple', 'google', 'microsoft', 'wechat', 'tiktok'],
  renderSocialLogin,
  layoutMode = 'double'
}) {
  const [mode, setMode] = useState('login'); // login, register, accounts, set-pin
  const [loginType, setLoginType] = useState(defaultLoginType); // wallet, did
  const [didMethod, setDidMethod] = useState('password'); // password, pin
  const [step, setStep] = useState('connect'); // connect, connecting, signing, success
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [walletAddress, setWalletAddress] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);

  // 客户端挂载状态
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // 语言状态 - 优先使用 props，否则从全局获取（自动归一化）
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    if (initialLanguage) {
      return normalizeLanguage(initialLanguage);
    }
    return getLanguage();
  });

  // 同步外部 props.language 变化（自动归一化）
  useEffect(() => {
    if (initialLanguage) {
      const normalized = normalizeLanguage(initialLanguage);
      if (normalized !== currentLanguage) {
        setCurrentLanguage(normalized);
        setLanguage(normalized);
      }
    }
  }, [initialLanguage, currentLanguage]);

  // 表单数据
  const [formData, setFormData] = useState({
    did: '',
    password: '',
    pin: '',
    nickname: '',
    email: '',
    confirmPassword: '',
    newPin: '' // For setting PIN
  });

  // 账户列表
  const [savedAccounts, setSavedAccounts] = useState([]);
  const msg = getMessages$1(currentLanguage);

  // 语言切换处理
  const handleLanguageChange = useCallback(newLang => {
    console.log('[DarkLoginModal] 手动切换语言:', newLang);
    setCurrentLanguage(newLang);
    setLanguage(newLang);
  }, []);

  // 订阅全局语言变更（当用户在页面其他地方切换语言时）
  useEffect(() => {
    // 订阅 i18n 模块的语言变更（内部已包含全局事件监听）
    const unsubscribe = onLanguageChange(newLang => {
      console.log('[DarkLoginModal] 收到全局语言变更事件:', newLang);
      setCurrentLanguage(newLang);
    });
    return () => {
      console.log('[DarkLoginModal] 取消语言订阅');
      unsubscribe();
    };
  }, []);

  // 配置 API
  useEffect(() => {
    if (apiBaseUrl) {
      apiClient.configure({
        apiBaseUrl
      });
    }
  }, [apiBaseUrl]);

  // 重置状态 + 同步语言
  useEffect(() => {
    if (isOpen) {
      setStep('connect');
      setError(null);
      setSuccess(null);
      setFormData({
        did: '',
        password: '',
        nickname: '',
        email: '',
        confirmPassword: ''
      });
      setSavedAccounts(getSavedAccounts());

      // 同步最新语言设置
      const latestLang = getLanguage();
      if (latestLang !== currentLanguage) {
        console.log('[DarkLoginModal] 同步语言状态:', latestLang);
        setCurrentLanguage(latestLang);
      }

      // 如果已登录，显示账户管理
      if (currentUser?.did) {
        setMode('accounts');
      } else {
        setMode('login');
      }
    }
  }, [isOpen, currentUser]);

  // 社交登录成功回调
  const handleSocialSuccess = useCallback(result => {
    const {
      access_token,
      refresh_token,
      did,
      user: resUser,
      is_new_user
    } = result;
    apiClient.setTokens(access_token, refresh_token);
    setCurrentDID(did);
    saveAccount({
      did,
      displayName: resUser?.display_name || formatDID$1(did),
      avatar: resUser?.avatar_url || resUser?.avatar,
      verified: true
    });
    setSavedAccounts(getSavedAccounts());
    setStep('success');
    setSuccess(msg.success || '登录成功');
    setTimeout(() => {
      onSuccess && onSuccess({
        success: true,
        accessToken: access_token,
        refreshToken: refresh_token,
        did,
        user: {
          nickname: resUser?.display_name || resUser?.name,
          avatar: resUser?.avatar_url || resUser?.avatar,
          ...resUser
        },
        isNewUser: is_new_user
      });
      onClose && onClose();
    }, 1200);
  }, [onSuccess, onClose, msg]);
  const handleSocialError = useCallback(err => {
    setError(err?.message || '社交登录失败');
  }, []);

  // 钱包连接成功回调 — Web3WalletPanel 连接钱包后进入签名流程
  const handleWalletConnect = useCallback(async (address, walletType, provider) => {
    setError(null);
    setWalletAddress(address);
    setStep('signing');
    try {
      const chainType = ['metamask', 'coinbase', 'walletconnect'].includes(walletType) ? 'ethereum' : 'quantabit';
      const walletProvider = provider || window.ethereum;
      if (!walletProvider) {
        throw new Error(msg.noWallet || '未检测到钱包');
      }

      // 获取 nonce
      const nonceRes = await apiClient.getNonce(address, chainType);
      const {
        message
      } = nonceRes.data || nonceRes;

      // 请求签名
      const signature = await signWalletMessage(walletProvider, chainType, message, address);

      // 验证登录
      const authRes = await apiClient.walletLogin(address, signature, message, chainType);
      const {
        access_token,
        refresh_token,
        did,
        user,
        is_new_user
      } = authRes.data || authRes;
      apiClient.setTokens(access_token, refresh_token);
      setCurrentDID(did);

      // 保存账户
      saveAccount({
        did,
        displayName: user?.nickname || formatAddress(address),
        avatar: user?.avatar,
        walletAddress: address,
        verified: true
      });
      setSavedAccounts(getSavedAccounts());
      setStep('success');
      setTimeout(() => {
        onSuccess && onSuccess({
          success: true,
          accessToken: access_token,
          refreshToken: refresh_token,
          did,
          user,
          isNewUser: is_new_user
        });
        onClose && onClose();
      }, 1200);
    } catch (err) {
      console.error('[DarkLoginModal] 钱包签名/验证失败:', err);
      if (err.code === 4001) {
        setError(msg.rejected || '用户拒绝了签名');
      } else {
        setError(err.message || msg.errorLoginFailed || '登录失败');
      }
      setStep('connect');
      onError && onError(err);
    }
  }, [msg, onSuccess, onError, onClose]);

  // DID 登录
  const handleDIDLogin = useCallback(async e => {
    e.preventDefault();
    setError(null);
    if (!formData.did.trim()) {
      setError(currentLanguage === 'zh' ? '请输入 DID' : 'Please enter DID');
      return;
    }
    if (didMethod === 'password' && !formData.password) {
      setError(currentLanguage === 'zh' ? '请输入密码' : 'Please enter password');
      return;
    }
    if (didMethod === 'pin' && !formData.pin) {
      setError(currentLanguage === 'zh' ? '请输入 PIN' : 'Please enter PIN');
      return;
    }
    try {
      setLoading(true);
      setStep('signing');
      const authRes = await apiClient.didLogin(formData.did, didMethod === 'password' ? formData.password : null, didMethod === 'pin' ? formData.pin : null);
      const {
        access_token,
        refresh_token,
        did,
        user
      } = authRes.data || authRes;
      apiClient.setTokens(access_token, refresh_token);
      setCurrentDID(did);

      // 保存账户
      saveAccount({
        did,
        displayName: user?.nickname || formatDID$1(did),
        avatar: user?.avatar,
        verified: true
      });
      setSavedAccounts(getSavedAccounts());
      setStep('success');
      setTimeout(() => {
        onSuccess && onSuccess({
          success: true,
          accessToken: access_token,
          refreshToken: refresh_token,
          did,
          user,
          isNewUser: false
        });
        onClose && onClose();
      }, 1200);
    } catch (err) {
      console.error('DID Login error:', err);
      setError(err.message || msg.errorLoginFailed || '登录失败');
      setStep('connect');
      onError && onError(err);
    } finally {
      setLoading(false);
    }
  }, [formData, didMethod, currentLanguage, msg, onSuccess, onError, onClose]);

  // 设置 PIN
  const handleSetPin = useCallback(async e => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!formData.password) {
      setError(currentLanguage === 'zh' ? '请输入当前密码以验证身份' : 'Please enter password to verify');
      return;
    }
    if (!formData.newPin || formData.newPin.length < 4) {
      setError(currentLanguage === 'zh' ? 'PIN 码至少4位' : 'PIN must be at least 4 chars');
      return;
    }
    try {
      setLoading(true);
      await apiClient.setPin(currentUser.did, formData.password, formData.newPin);
      setSuccess(currentLanguage === 'zh' ? 'PIN 设置成功！' : 'PIN set successfully!');

      // Update local storage to reflect user has PIN? 
      // Ideally we should update the saved account info.
      const updatedAccounts = savedAccounts.map(a => {
        if (a.did === currentUser.did) {
          return {
            ...a,
            hasPin: true
          };
        }
        return a;
      });
      setSavedAccounts(updatedAccounts);
      localStorage.setItem('qbit_did_saved_accounts', JSON.stringify(updatedAccounts));
      setTimeout(() => {
        setMode('accounts');
        setSuccess(null);
        setFormData(prev => ({
          ...prev,
          password: '',
          newPin: ''
        }));
      }, 1500);
    } catch (err) {
      console.error('Set PIN error:', err);
      setError(err.message || '设置失败');
    } finally {
      setLoading(false);
    }
  }, [formData, currentUser, savedAccounts, currentLanguage]);

  // 注册
  const handleRegister = useCallback(async e => {
    e.preventDefault();
    setError(null);

    // 验证
    if (!formData.nickname.trim()) {
      setError(currentLanguage === 'zh' ? '请输入昵称' : 'Please enter nickname');
      return;
    }
    if (!formData.password) {
      setError(currentLanguage === 'zh' ? '请输入密码' : 'Please enter password');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError(msg.errorPasswordMismatch || '两次密码不一致');
      return;
    }
    if (formData.password.length < 6) {
      setError(currentLanguage === 'zh' ? '密码至少6位' : 'Password must be at least 6 characters');
      return;
    }
    try {
      setLoading(true);
      const authRes = await apiClient.register({
        displayName: formData.nickname.trim(),
        email: formData.email || undefined,
        password: formData.password
      });
      const {
        access_token,
        refresh_token,
        did,
        user
      } = authRes.data || authRes;
      apiClient.setTokens(access_token, refresh_token);
      setCurrentDID(did);

      // 保存账户
      saveAccount({
        did,
        displayName: formData.nickname.trim(),
        verified: true
      });
      setSavedAccounts(getSavedAccounts());
      setSuccess(currentLanguage === 'zh' ? '注册成功！' : 'Registration successful!');
      setTimeout(() => {
        onSuccess && onSuccess({
          success: true,
          accessToken: access_token,
          refreshToken: refresh_token,
          did,
          user,
          isNewUser: true
        });
        onClose && onClose();
      }, 1200);
    } catch (err) {
      console.error('Register error:', err);
      setError(err.message || msg.errorRegisterFailed || '注册失败');
      onError && onError(err);
    } finally {
      setLoading(false);
    }
  }, [formData, currentLanguage, msg, onSuccess, onError, onClose]);

  // 快速登录
  const handleQuickLogin = useCallback(account => {
    setFormData(prev => ({
      ...prev,
      did: account.did
    }));
    setMode('login');
    setLoginType('did');
    // If account has PIN, default to PIN method?
    if (account.hasPin) {
      setDidMethod('pin');
    } else {
      setDidMethod('password');
    }
    setTimeout(() => {
      if (account.hasPin) {
        document.getElementById('dark-pin')?.focus();
      } else {
        document.getElementById('dark-password')?.focus();
      }
    }, 100);
  }, []);

  // 移除账户
  const handleRemoveAccount = useCallback((did, e) => {
    e.stopPropagation();
    if (window.confirm(msg.removeConfirm || '确定移除此账户？')) {
      removeAccount(did);
      setSavedAccounts(getSavedAccounts());
    }
  }, [msg]);

  // 登出
  const handleLogout = useCallback(() => {
    clearCurrentSession();
    setMode('login');
    onLogout && onLogout();
  }, [onLogout]);

  // 切换到注册
  const goToRegister = useCallback(() => {
    if (onNavigateToRegister) {
      onNavigateToRegister();
    } else {
      setMode('register');
      setError(null);
      setFormData({
        did: '',
        password: '',
        nickname: '',
        email: '',
        confirmPassword: ''
      });
    }
  }, [onNavigateToRegister]);

  // 默认 Logo
  const defaultLogo = /*#__PURE__*/jsxs("svg", {
    viewBox: "0 0 40 40",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [/*#__PURE__*/jsx("rect", {
      width: "40",
      height: "40",
      rx: "10",
      fill: "url(#dark-modal-grad)"
    }), /*#__PURE__*/jsx("path", {
      d: "M12 20C12 15.5817 15.5817 12 20 12C24.4183 12 28 15.5817 28 20C28 24.4183 24.4183 28 20 28",
      stroke: "white",
      strokeWidth: "2.5",
      strokeLinecap: "round"
    }), /*#__PURE__*/jsx("circle", {
      cx: "20",
      cy: "20",
      r: "4",
      fill: "white"
    }), /*#__PURE__*/jsx("defs", {
      children: /*#__PURE__*/jsxs("linearGradient", {
        id: "dark-modal-grad",
        x1: "0",
        y1: "0",
        x2: "40",
        y2: "40",
        gradientUnits: "userSpaceOnUse",
        children: [/*#__PURE__*/jsx("stop", {
          stopColor: "#6366f1"
        }), /*#__PURE__*/jsx("stop", {
          offset: "1",
          stopColor: "#a855f7"
        })]
      })
    })]
  });
  if (!isOpen || !mounted) return null;
  const otherAccounts = savedAccounts.filter(a => a.did !== currentUser?.did);

  // 渲染账户管理视图
  const renderAccountsView = () => /*#__PURE__*/jsxs("div", {
    className: "dark-accounts-view",
    children: [/*#__PURE__*/jsxs("div", {
      className: "dark-accounts-header",
      children: [/*#__PURE__*/jsxs("button", {
        className: "dark-back-btn",
        onClick: () => setMode('login'),
        children: ["\u2190 ", msg.back || '返回']
      }), /*#__PURE__*/jsxs("h3", {
        children: [/*#__PURE__*/jsx(SwapIcon, {
          size: 18
        }), " ", msg.accountsSection || '账户管理']
      })]
    }), currentUser?.did && /*#__PURE__*/jsxs("div", {
      className: "dark-current-account",
      children: [/*#__PURE__*/jsx("p", {
        className: "dark-section-label",
        children: msg.currentAccount || '当前账户'
      }), /*#__PURE__*/jsxs("div", {
        className: "dark-account-card current",
        children: [/*#__PURE__*/jsx("div", {
          className: "dark-account-avatar",
          children: currentUser.avatar ? /*#__PURE__*/jsx("img", {
            src: currentUser.avatar,
            alt: ""
          }) : /*#__PURE__*/jsx("span", {
            children: "\uD83D\uDD10"
          })
        }), /*#__PURE__*/jsxs("div", {
          className: "dark-account-info",
          children: [/*#__PURE__*/jsxs("span", {
            className: "dark-account-name",
            children: [currentUser.nickname || currentUser.displayName || formatDID$1(currentUser.did), /*#__PURE__*/jsx(VerifiedIcon, {
              size: 14
            })]
          }), /*#__PURE__*/jsx("span", {
            className: "dark-account-did",
            children: formatDID$1(currentUser.did)
          })]
        }), /*#__PURE__*/jsx("button", {
          className: "dark-logout-btn",
          onClick: () => setMode('set-pin'),
          title: msg.setPin || '设置 PIN',
          children: /*#__PURE__*/jsx(KeypadIcon, {
            size: 18
          })
        }), /*#__PURE__*/jsx("button", {
          className: "dark-logout-btn",
          onClick: handleLogout,
          title: msg.logout,
          children: /*#__PURE__*/jsx(LogoutIcon, {
            size: 18
          })
        })]
      })]
    }), /*#__PURE__*/jsxs("button", {
      className: "dark-add-account-btn",
      onClick: () => setMode('login'),
      children: ["+ ", msg.addNewAccount || '添加新账户']
    }), otherAccounts.length > 0 && /*#__PURE__*/jsxs("div", {
      className: "dark-other-accounts",
      children: [/*#__PURE__*/jsx("p", {
        className: "dark-section-label",
        children: msg.otherAccounts || '其他账户'
      }), otherAccounts.map(account => /*#__PURE__*/jsxs("div", {
        className: "dark-account-card",
        onClick: () => handleQuickLogin(account),
        children: [/*#__PURE__*/jsx("div", {
          className: "dark-account-avatar",
          children: account.avatar ? /*#__PURE__*/jsx("img", {
            src: account.avatar,
            alt: ""
          }) : /*#__PURE__*/jsx("span", {
            children: "\uD83D\uDD10"
          })
        }), /*#__PURE__*/jsxs("div", {
          className: "dark-account-info",
          children: [/*#__PURE__*/jsxs("span", {
            className: "dark-account-name",
            children: [account.displayName || formatDID$1(account.did), account.verified && /*#__PURE__*/jsx(VerifiedIcon, {
              size: 14
            })]
          }), /*#__PURE__*/jsx("span", {
            className: "dark-account-did",
            children: formatDID$1(account.did)
          })]
        }), /*#__PURE__*/jsx("button", {
          className: "dark-switch-btn",
          children: msg.switchAccount || '切换'
        }), /*#__PURE__*/jsx("button", {
          className: "dark-remove-btn",
          onClick: e => handleRemoveAccount(account.did, e),
          title: msg.removeAccount || '移除',
          children: /*#__PURE__*/jsx(TrashIcon, {
            size: 16
          })
        })]
      }, account.did))]
    }), otherAccounts.length === 0 && !currentUser?.did && /*#__PURE__*/jsx("p", {
      className: "dark-no-accounts",
      children: msg.noOtherAccounts || '没有其他账户'
    })]
  });

  // 渲染注册视图
  const renderSocialLoginSection = () => {
    if (!showSocialLogin) return null;
    return /*#__PURE__*/jsxs("div", {
      className: "dark-social-login-container",
      style: {
        padding: '0 32px 16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        boxSizing: 'border-box'
      },
      children: [/*#__PURE__*/jsxs("div", {
        className: "dark-social-divider",
        style: {
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          margin: '10px 0 15px 0',
          color: '#6b7280',
          fontSize: '13px'
        },
        children: [/*#__PURE__*/jsx("div", {
          style: {
            flex: 1,
            height: '1px',
            backgroundColor: 'rgba(255,255,255,0.08)'
          }
        }), /*#__PURE__*/jsx("span", {
          style: {
            padding: '0 10px'
          },
          children: msg.orDivider || '或'
        }), /*#__PURE__*/jsx("div", {
          style: {
            flex: 1,
            height: '1px',
            backgroundColor: 'rgba(255,255,255,0.08)'
          }
        })]
      }), renderSocialLogin ? typeof renderSocialLogin === 'function' ? renderSocialLogin() : renderSocialLogin : /*#__PURE__*/jsx(OAuthIconGroup, {
        providers: socialProviders,
        onSuccess: handleSocialSuccess,
        onError: handleSocialError
      })]
    });
  };
  const renderRegisterView = () => /*#__PURE__*/jsx("div", {
    className: "dark-login-step",
    children: /*#__PURE__*/jsxs("form", {
      onSubmit: handleRegister,
      className: "dark-login-form",
      children: [/*#__PURE__*/jsxs("div", {
        className: "dark-form-group",
        children: [/*#__PURE__*/jsxs("label", {
          htmlFor: "dark-nickname",
          children: [msg.nickname || '昵称', " *"]
        }), /*#__PURE__*/jsx("input", {
          type: "text",
          id: "dark-nickname",
          value: formData.nickname,
          onChange: e => setFormData({
            ...formData,
            nickname: e.target.value
          }),
          placeholder: currentLanguage === 'zh' ? '请输入昵称' : 'Enter nickname',
          required: true,
          disabled: loading
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "dark-form-group",
        children: [/*#__PURE__*/jsxs("label", {
          htmlFor: "dark-email",
          children: [msg.email || '邮箱', " (", currentLanguage === 'zh' ? '可选' : 'optional', ")"]
        }), /*#__PURE__*/jsx("input", {
          type: "email",
          id: "dark-email",
          value: formData.email,
          onChange: e => setFormData({
            ...formData,
            email: e.target.value
          }),
          placeholder: "user@example.com",
          disabled: loading
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "dark-form-group",
        children: [/*#__PURE__*/jsxs("label", {
          htmlFor: "dark-reg-password",
          children: [msg.password || '密码', " *"]
        }), /*#__PURE__*/jsxs("div", {
          className: "dark-password-input",
          children: [/*#__PURE__*/jsx("input", {
            type: showPassword ? 'text' : 'password',
            id: "dark-reg-password",
            value: formData.password,
            onChange: e => setFormData({
              ...formData,
              password: e.target.value
            }),
            placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022",
            required: true,
            disabled: loading
          }), /*#__PURE__*/jsx("button", {
            type: "button",
            className: "dark-toggle-password",
            onClick: () => setShowPassword(!showPassword),
            children: showPassword ? /*#__PURE__*/jsx(EyeOffIcon, {
              size: 18
            }) : /*#__PURE__*/jsx(EyeIcon, {
              size: 18
            })
          })]
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "dark-form-group",
        children: [/*#__PURE__*/jsxs("label", {
          htmlFor: "dark-confirm-password",
          children: [msg.confirmPassword || '确认密码', " *"]
        }), /*#__PURE__*/jsxs("div", {
          className: "dark-password-input",
          children: [/*#__PURE__*/jsx("input", {
            type: showConfirmPassword ? 'text' : 'password',
            id: "dark-confirm-password",
            value: formData.confirmPassword,
            onChange: e => setFormData({
              ...formData,
              confirmPassword: e.target.value
            }),
            placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022",
            required: true,
            disabled: loading
          }), /*#__PURE__*/jsx("button", {
            type: "button",
            className: "dark-toggle-password",
            onClick: () => setShowConfirmPassword(!showConfirmPassword),
            children: showConfirmPassword ? /*#__PURE__*/jsx(EyeOffIcon, {
              size: 18
            }) : /*#__PURE__*/jsx(EyeIcon, {
              size: 18
            })
          })]
        })]
      }), /*#__PURE__*/jsxs("button", {
        type: "submit",
        className: "dark-connect-btn",
        disabled: loading,
        children: [loading ? /*#__PURE__*/jsx(LoaderIcon, {
          size: 20
        }) : /*#__PURE__*/jsx(ShieldIcon, {
          size: 20
        }), loading ? msg.registering || '注册中...' : msg.registerButton || '创建身份', !loading && /*#__PURE__*/jsx(ArrowRightIcon, {
          size: 20
        })]
      })]
    })
  });

  // 渲染设置 PIN 视图
  const renderSetPinView = () => /*#__PURE__*/jsxs("div", {
    className: "dark-login-step",
    children: [/*#__PURE__*/jsxs("div", {
      className: "dark-accounts-header",
      children: [/*#__PURE__*/jsxs("button", {
        className: "dark-back-btn",
        onClick: () => setMode('accounts'),
        children: ["\u2190 ", msg.back || '返回']
      }), /*#__PURE__*/jsxs("h3", {
        children: [/*#__PURE__*/jsx(KeyIcon, {
          size: 18
        }), " ", msg.setPin || '设置 PIN']
      })]
    }), /*#__PURE__*/jsx("p", {
      className: "dark-step-description",
      children: currentLanguage === 'zh' ? '设置 PIN 码可让您更快速地登录 DID 账户' : 'Set a PIN for faster DID login'
    }), success && /*#__PURE__*/jsxs("div", {
      className: "dark-login-step dark-success-step",
      children: [/*#__PURE__*/jsx("div", {
        className: "dark-success-icon",
        children: /*#__PURE__*/jsx(CheckIcon, {
          size: 32
        })
      }), /*#__PURE__*/jsx("p", {
        children: success
      })]
    }), !success && /*#__PURE__*/jsxs("form", {
      onSubmit: handleSetPin,
      className: "dark-login-form",
      children: [/*#__PURE__*/jsxs("div", {
        className: "dark-form-group",
        children: [/*#__PURE__*/jsxs("label", {
          htmlFor: "dark-verify-password",
          children: [msg.password || '当前密码', " *"]
        }), /*#__PURE__*/jsxs("div", {
          className: "dark-password-input",
          children: [/*#__PURE__*/jsx("input", {
            type: showPassword ? 'text' : 'password',
            id: "dark-verify-password",
            value: formData.password,
            onChange: e => setFormData({
              ...formData,
              password: e.target.value
            }),
            placeholder: currentLanguage === 'zh' ? '验证身份' : 'Verify identity',
            required: true,
            disabled: loading
          }), /*#__PURE__*/jsx("button", {
            type: "button",
            className: "dark-toggle-password",
            onClick: () => setShowPassword(!showPassword),
            children: showPassword ? /*#__PURE__*/jsx(EyeOffIcon, {
              size: 18
            }) : /*#__PURE__*/jsx(EyeIcon, {
              size: 18
            })
          })]
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "dark-form-group",
        children: [/*#__PURE__*/jsx("label", {
          htmlFor: "dark-new-pin",
          children: "BTC / PIN Code *"
        }), /*#__PURE__*/jsx("div", {
          className: "dark-password-input",
          children: /*#__PURE__*/jsx("input", {
            type: "password",
            id: "dark-new-pin",
            maxLength: 6,
            value: formData.newPin,
            onChange: e => setFormData({
              ...formData,
              newPin: e.target.value.replace(/\D/g, '')
            }),
            placeholder: "6-digit PIN",
            required: true,
            disabled: loading,
            className: "dark-pin-input",
            style: {
              letterSpacing: '8px',
              fontSize: '18px',
              textAlign: 'center'
            }
          })
        })]
      }), /*#__PURE__*/jsxs("button", {
        type: "submit",
        className: "dark-connect-btn",
        disabled: loading,
        children: [loading ? /*#__PURE__*/jsx(LoaderIcon, {
          size: 20
        }) : /*#__PURE__*/jsx(KeyIcon, {
          size: 20
        }), loading ? msg.processing || '处理中...' : msg.confirmSetPin || '确认设置']
      })]
    }), error && /*#__PURE__*/jsxs("div", {
      className: "dark-login-error",
      children: [/*#__PURE__*/jsx(AlertIcon, {
        size: 18
      }), /*#__PURE__*/jsx("span", {
        children: error
      })]
    })]
  });
  return /*#__PURE__*/ReactDOM.createPortal(/*#__PURE__*/jsx("div", {
    className: `dark-login-overlay dark-theme-${theme}`,
    onClick: e => e.target === e.currentTarget && onClose(),
    children: /*#__PURE__*/jsxs("div", {
      className: `dark-login-modal${loginType === 'wallet' && mode === 'login' && layoutMode !== 'single' ? ' dark-wallet-mode' : ''}${layoutMode === 'single' ? ' layout-single' : ''}`,
      children: [/*#__PURE__*/jsxs("div", {
        className: "dark-login-topbar",
        children: [/*#__PURE__*/jsxs("div", {
          className: "dark-topbar-left",
          children: [showLanguageSwitch && /*#__PURE__*/jsx(LanguageSwitcher, {
            currentLanguage: currentLanguage,
            onLanguageChange: handleLanguageChange,
            variant: "dark",
            showFlag: true,
            showName: false
          }), showToolbar && onThemeChange && /*#__PURE__*/jsxs("div", {
            className: "dark-toolbar-item",
            children: [/*#__PURE__*/jsx("button", {
              className: "dark-toolbar-btn",
              onClick: () => setShowThemeMenu(!showThemeMenu),
              title: currentLanguage === 'zh' ? '切换主题' : 'Switch Theme',
              children: /*#__PURE__*/jsx(PaletteIcon, {
                size: 16
              })
            }), showThemeMenu && /*#__PURE__*/jsx("div", {
              className: "dark-theme-dropdown",
              children: AVAILABLE_THEMES.map(t => {
                const ThemeIcon = t.icon;
                return /*#__PURE__*/jsxs("button", {
                  className: `dark-theme-option ${theme === t.key ? 'active' : ''}`,
                  onClick: () => {
                    onThemeChange(t.key);
                    setShowThemeMenu(false);
                  },
                  children: [/*#__PURE__*/jsx(ThemeIcon, {
                    size: 16
                  }), /*#__PURE__*/jsx("span", {
                    children: currentLanguage === 'zh' ? t.label : t.labelEn
                  }), theme === t.key && /*#__PURE__*/jsx(CheckIcon, {
                    size: 14
                  })]
                }, t.key);
              })
            })]
          })]
        }), /*#__PURE__*/jsx("div", {
          className: "dark-topbar-center",
          children: /*#__PURE__*/jsx("h2", {
            children: mode === 'register' ? msg.registerSection || '创建身份' : mode === 'accounts' ? msg.accountsSection || '账户管理' : mode === 'set-pin' ? msg.securitySettings || '安全设置' : currentLanguage === 'zh' ? '登录' : 'Login'
          })
        }), /*#__PURE__*/jsxs("div", {
          className: "dark-topbar-right",
          children: [showToolbar && onModeChange && /*#__PURE__*/jsx("button", {
            className: "dark-toolbar-btn",
            onClick: () => onModeChange('full'),
            title: currentLanguage === 'zh' ? '全功能模式' : 'Full Mode',
            children: /*#__PURE__*/jsx(LayoutIcon, {
              size: 16
            })
          }), showToolbar && onOpenFullscreen && /*#__PURE__*/jsx("button", {
            className: "dark-toolbar-btn",
            onClick: () => {
              onClose();
              onOpenFullscreen();
            },
            title: currentLanguage === 'zh' ? '全屏登录页' : 'Open Fullscreen',
            children: /*#__PURE__*/jsx(ExpandIcon, {
              size: 16
            })
          }), /*#__PURE__*/jsx("button", {
            className: "dark-toolbar-btn",
            onClick: onClose,
            children: /*#__PURE__*/jsx(CloseIcon, {})
          })]
        })]
      }), /*#__PURE__*/jsx("div", {
        className: "dark-login-subtitle",
        children: /*#__PURE__*/jsx("p", {
          children: mode === 'register' ? msg.chooseMethod || '创建您的去中心化身份' : msg.subtitle
        })
      }), /*#__PURE__*/jsxs("div", {
        className: "dark-login-bg",
        children: [/*#__PURE__*/jsx("div", {
          className: "dark-login-orb dark-login-orb-1"
        }), /*#__PURE__*/jsx("div", {
          className: "dark-login-orb dark-login-orb-2"
        }), /*#__PURE__*/jsx("div", {
          className: "dark-login-grid"
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "dark-login-container",
        children: [/*#__PURE__*/jsxs("div", {
          className: "dark-login-card",
          children: [/*#__PURE__*/jsxs("div", {
            className: "dark-login-brand",
            style: {
              margin: '0',
              padding: '24px 28px 20px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
              justifyContent: 'center'
            },
            children: [/*#__PURE__*/jsx("div", {
              className: "dark-login-logo-icon",
              children: appLogo ? /*#__PURE__*/jsx("img", {
                src: appLogo,
                alt: appName
              }) : defaultLogo
            }), /*#__PURE__*/jsx("span", {
              style: {
                fontSize: '1.4rem'
              },
              children: appName
            })]
          }), mode === 'accounts' ? /*#__PURE__*/jsx("div", {
            className: "dark-login-content",
            children: renderAccountsView()
          }) : mode === 'set-pin' ? /*#__PURE__*/jsx("div", {
            className: "dark-login-content",
            children: renderSetPinView()
          }) : mode === 'register' ? /*#__PURE__*/jsxs(Fragment, {
            children: [/*#__PURE__*/jsxs("div", {
              className: "dark-login-content",
              children: [success ? /*#__PURE__*/jsxs("div", {
                className: "dark-login-step dark-success-step",
                children: [/*#__PURE__*/jsx("div", {
                  className: "dark-success-icon",
                  children: /*#__PURE__*/jsx(CheckIcon, {
                    size: 32
                  })
                }), /*#__PURE__*/jsx("p", {
                  children: success
                }), /*#__PURE__*/jsx("span", {
                  children: msg.redirecting || '正在跳转...'
                })]
              }) : renderRegisterView(), error && /*#__PURE__*/jsxs("div", {
                className: "dark-login-error",
                children: [/*#__PURE__*/jsx(AlertIcon, {
                  size: 18
                }), /*#__PURE__*/jsx("span", {
                  children: error
                })]
              })]
            }), renderSocialLoginSection(), /*#__PURE__*/jsx("div", {
              className: "dark-login-footer",
              children: /*#__PURE__*/jsxs("p", {
                children: [msg.alreadyHaveDID || '已有账号？', /*#__PURE__*/jsx("button", {
                  onClick: () => {
                    setMode('login');
                    setError(null);
                  },
                  children: msg.goLogin || '去登录'
                })]
              })
            })]
          }) : /*#__PURE__*/jsxs(Fragment, {
            children: [/*#__PURE__*/jsxs("div", {
              className: "dark-login-tabs",
              children: [/*#__PURE__*/jsxs("button", {
                className: `dark-login-tab ${loginType === 'wallet' ? 'active' : ''}`,
                onClick: () => {
                  setLoginType('wallet');
                  setError(null);
                  setStep('connect');
                },
                children: [/*#__PURE__*/jsx(WalletIcon, {
                  size: 18
                }), msg.walletSection || '钱包登录']
              }), /*#__PURE__*/jsxs("button", {
                className: `dark-login-tab ${loginType === 'did' ? 'active' : ''}`,
                onClick: () => {
                  setLoginType('did');
                  setError(null);
                  setStep('connect');
                },
                children: [/*#__PURE__*/jsx(MailIcon, {
                  size: 18
                }), msg.didSection || 'DID 登录']
              })]
            }), /*#__PURE__*/jsxs("div", {
              className: "dark-login-content",
              children: [loginType === 'wallet' && /*#__PURE__*/jsxs(Fragment, {
                children: [step === 'connect' && /*#__PURE__*/jsx(Web3WalletPanel, {
                  onConnect: handleWalletConnect,
                  onError: err => {
                    setError(err.message);
                    onError && onError(err);
                  },
                  language: currentLanguage,
                  theme: theme,
                  apiBaseUrl: apiBaseUrl,
                  layoutMode: layoutMode
                }), step === 'signing' && /*#__PURE__*/jsxs("div", {
                  className: "dark-login-step dark-signing-step",
                  children: [/*#__PURE__*/jsxs("div", {
                    className: "dark-wallet-address",
                    children: [/*#__PURE__*/jsx(ShieldIcon, {
                      size: 20
                    }), /*#__PURE__*/jsx("code", {
                      children: formatAddress(walletAddress)
                    })]
                  }), /*#__PURE__*/jsx(LoaderIcon, {
                    size: 32
                  }), /*#__PURE__*/jsx("p", {
                    children: msg.signing || '请在钱包中签名...'
                  }), /*#__PURE__*/jsx("span", {
                    className: "dark-signing-hint",
                    children: msg.securityNote || '请确认在您的钱包中签名'
                  })]
                })]
              }), loginType === 'did' && step === 'connect' && /*#__PURE__*/jsxs("div", {
                className: "dark-login-step",
                children: [savedAccounts.length > 0 && /*#__PURE__*/jsxs("div", {
                  className: "dark-saved-accounts",
                  children: [/*#__PURE__*/jsx("p", {
                    className: "dark-saved-label",
                    children: msg.savedAccounts || '已保存账户'
                  }), savedAccounts.slice(0, 3).map(account => /*#__PURE__*/jsxs("button", {
                    className: "dark-saved-account",
                    onClick: () => handleQuickLogin(account),
                    children: [/*#__PURE__*/jsx("div", {
                      className: "dark-saved-avatar",
                      children: account.avatar ? /*#__PURE__*/jsx("img", {
                        src: account.avatar,
                        alt: ""
                      }) : /*#__PURE__*/jsx("span", {
                        children: "\uD83D\uDD10"
                      })
                    }), /*#__PURE__*/jsxs("div", {
                      className: "dark-saved-info",
                      children: [/*#__PURE__*/jsx("span", {
                        className: "dark-saved-name",
                        children: account.displayName || formatDID$1(account.did)
                      }), /*#__PURE__*/jsx("span", {
                        className: "dark-saved-did",
                        children: formatDID$1(account.did)
                      })]
                    }), /*#__PURE__*/jsx("span", {
                      className: "dark-quick-label",
                      children: msg.quickLogin || '快速登录'
                    })]
                  }, account.did))]
                }), /*#__PURE__*/jsxs(Fragment, {
                  children: [/*#__PURE__*/jsxs("div", {
                    className: "dark-method-switch",
                    style: {
                      display: 'flex',
                      gap: '8px',
                      marginBottom: '16px',
                      background: 'rgba(255,255,255,0.05)',
                      padding: '4px',
                      borderRadius: '8px'
                    },
                    children: [/*#__PURE__*/jsxs("button", {
                      type: "button",
                      onClick: () => setDidMethod('password'),
                      style: {
                        flex: 1,
                        padding: '6px',
                        border: 'none',
                        borderRadius: '6px',
                        background: didMethod === 'password' ? 'rgba(255,255,255,0.15)' : 'transparent',
                        color: didMethod === 'password' ? '#fff' : 'rgba(255,255,255,0.6)',
                        cursor: 'pointer',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      },
                      children: [/*#__PURE__*/jsx(LockIcon, {
                        size: 14
                      }), " Password"]
                    }), /*#__PURE__*/jsxs("button", {
                      type: "button",
                      onClick: () => setDidMethod('pin'),
                      style: {
                        flex: 1,
                        padding: '6px',
                        border: 'none',
                        borderRadius: '6px',
                        background: didMethod === 'pin' ? 'rgba(255,255,255,0.15)' : 'transparent',
                        color: didMethod === 'pin' ? '#fff' : 'rgba(255,255,255,0.6)',
                        cursor: 'pointer',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      },
                      children: [/*#__PURE__*/jsx(KeypadIcon, {
                        size: 14
                      }), " PIN"]
                    })]
                  }), /*#__PURE__*/jsxs("form", {
                    onSubmit: handleDIDLogin,
                    className: "dark-login-form",
                    children: [/*#__PURE__*/jsxs("div", {
                      className: "dark-form-group",
                      children: [/*#__PURE__*/jsxs("label", {
                        htmlFor: "dark-did",
                        children: ["DID / ", msg.email || '邮箱']
                      }), /*#__PURE__*/jsx("input", {
                        type: "text",
                        id: "dark-did",
                        value: formData.did,
                        onChange: e => setFormData({
                          ...formData,
                          did: e.target.value
                        }),
                        placeholder: currentLanguage === 'zh' ? 'did:qbit:... 或 邮箱' : 'did:qbit:... or email',
                        required: true,
                        disabled: loading
                      })]
                    }), didMethod === 'password' ? /*#__PURE__*/jsxs("div", {
                      className: "dark-form-group",
                      children: [/*#__PURE__*/jsx("label", {
                        htmlFor: "dark-password",
                        children: msg.password || '密码'
                      }), /*#__PURE__*/jsxs("div", {
                        className: "dark-password-input",
                        children: [/*#__PURE__*/jsx("input", {
                          type: showPassword ? 'text' : 'password',
                          id: "dark-password",
                          value: formData.password,
                          onChange: e => setFormData({
                            ...formData,
                            password: e.target.value
                          }),
                          placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022",
                          required: true,
                          disabled: loading
                        }), /*#__PURE__*/jsx("button", {
                          type: "button",
                          className: "dark-toggle-password",
                          onClick: () => setShowPassword(!showPassword),
                          children: showPassword ? /*#__PURE__*/jsx(EyeOffIcon, {
                            size: 18
                          }) : /*#__PURE__*/jsx(EyeIcon, {
                            size: 18
                          })
                        })]
                      })]
                    }) : /*#__PURE__*/jsxs("div", {
                      className: "dark-form-group",
                      children: [/*#__PURE__*/jsx("label", {
                        htmlFor: "dark-pin",
                        children: "PIN Code"
                      }), /*#__PURE__*/jsx("div", {
                        className: "dark-password-input",
                        children: /*#__PURE__*/jsx("input", {
                          type: "password",
                          id: "dark-pin",
                          maxLength: 6,
                          value: formData.pin,
                          onChange: e => setFormData({
                            ...formData,
                            pin: e.target.value.replace(/\D/g, '')
                          }),
                          placeholder: "6-digit PIN",
                          required: true,
                          disabled: loading,
                          className: "dark-pin-input",
                          style: {
                            letterSpacing: '8px',
                            fontSize: '18px',
                            textAlign: 'center'
                          }
                        })
                      })]
                    }), /*#__PURE__*/jsxs("button", {
                      type: "submit",
                      className: "dark-connect-btn",
                      disabled: loading,
                      children: [loading ? /*#__PURE__*/jsx(LoaderIcon, {
                        size: 20
                      }) : /*#__PURE__*/jsx(MailIcon, {
                        size: 20
                      }), loading ? msg.loggingIn || '登录中...' : msg.loginButton || '安全登录', !loading && /*#__PURE__*/jsx(ArrowRightIcon, {
                        size: 20
                      })]
                    })]
                  }), error && /*#__PURE__*/jsxs("div", {
                    className: "dark-login-error",
                    children: [/*#__PURE__*/jsx(AlertIcon, {
                      size: 18
                    }), /*#__PURE__*/jsx("span", {
                      children: error
                    })]
                  })]
                })]
              }), loginType === 'did' && step === 'signing' && /*#__PURE__*/jsxs("div", {
                className: "dark-login-step dark-loading-step",
                children: [/*#__PURE__*/jsx(LoaderIcon, {
                  size: 48
                }), /*#__PURE__*/jsx("p", {
                  children: msg.verifying || '验证中...'
                })]
              }), step === 'success' && /*#__PURE__*/jsxs("div", {
                className: "dark-login-step dark-success-step",
                children: [/*#__PURE__*/jsx("div", {
                  className: "dark-success-icon",
                  children: /*#__PURE__*/jsx(CheckIcon, {
                    size: 32
                  })
                }), /*#__PURE__*/jsx("p", {
                  children: msg.success || '登录成功'
                }), /*#__PURE__*/jsx("span", {
                  children: msg.redirecting || '正在跳转...'
                })]
              })]
            }), renderSocialLoginSection(), /*#__PURE__*/jsxs("div", {
              className: "dark-login-footer",
              children: [showRegister && /*#__PURE__*/jsxs("p", {
                children: [msg.noAccountYet || '还没有账号？', /*#__PURE__*/jsx("button", {
                  type: "button",
                  onClick: goToRegister,
                  children: msg.register || '立即注册'
                })]
              }), savedAccounts.length > 0 && /*#__PURE__*/jsxs("button", {
                type: "button",
                className: "dark-manage-accounts-link",
                onClick: () => setMode('accounts'),
                children: [/*#__PURE__*/jsx(SwapIcon, {
                  size: 14
                }), " ", msg.manageAccounts || '管理账户']
              })]
            })]
          })]
        }), /*#__PURE__*/jsxs("div", {
          className: "dark-login-features",
          children: [/*#__PURE__*/jsxs("div", {
            className: "dark-feature",
            children: [/*#__PURE__*/jsx(ShieldIcon, {
              size: 20
            }), /*#__PURE__*/jsx("span", {
              children: msg.feature1 || '数据完全自主掌控'
            })]
          }), /*#__PURE__*/jsxs("div", {
            className: "dark-feature",
            children: [/*#__PURE__*/jsx(WalletIcon, {
              size: 20
            }), /*#__PURE__*/jsx("span", {
              children: msg.feature2 || 'W3C 标准身份'
            })]
          })]
        })]
      })]
    })
  }), document.body);
}

/**
 * QuantaBit Auth SDK - 全页面登录组件 (LoginPage)
 * 
 * 独立的全页登录/注册页面，支持：
 * - 钱包登录（MetaMask, WalletConnect 等）
 * - DID 密码登录
 * - 标签页切换登录方式
 * - 精美的深色背景动画
 */

function LoginPage({
  onSuccess,
  onNavigateToRegister,
  onNavigateToHome,
  apiBaseUrl = '/api/v1',
  appName = 'QuantaBit',
  appLogo,
  language = 'zh',
  redirectTo = '/dashboard'
}) {
  const [loginType, setLoginType] = useState('wallet'); // wallet, did
  const [step, setStep] = useState('connect'); // connect, connecting, signing, success
  const [error, setError] = useState(null);
  const [walletAddress, setWalletAddress] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    did: '',
    password: ''
  });
  const msg = getMessages$1(language);

  // 配置 API
  useEffect(() => {
    if (apiBaseUrl) {
      authApi.configure({
        apiBaseUrl
      });
    }
  }, [apiBaseUrl]);

  // 钱包登录
  const connectWallet = useCallback(async () => {
    setError(null);
    if (!hasQBitWallet()) {
      setError(msg.noWallet);
      return;
    }
    try {
      setStep('connecting');
      const {
        address,
        response
      } = await loginWithBrowserWallet(authApi, 'quantabit');
      setWalletAddress(address);
      setStep('signing');
      const {
        access_token,
        refresh_token,
        did,
        user
      } = response;
      authApi.setTokens(access_token, refresh_token);
      setStep('success');
      setTimeout(() => {
        onSuccess && onSuccess({
          accessToken: access_token,
          refreshToken: refresh_token,
          did,
          user,
          redirectTo
        });
      }, 1500);
    } catch (err) {
      console.error('Login error:', err);
      if (err.code === 4001) {
        setError(msg.rejected);
      } else {
        setError(err.message || msg.errorLoginFailed);
      }
      setStep('connect');
    }
  }, [msg, onSuccess, redirectTo]);

  // DID 登录
  const handleDIDLogin = useCallback(async e => {
    e.preventDefault();
    setError(null);
    try {
      setStep('signing');
      const authRes = await authApi.didLogin(formData.did, formData.password);
      const {
        access_token,
        refresh_token,
        did,
        user
      } = authRes.data || authRes;
      authApi.setTokens(access_token, refresh_token);
      setStep('success');
      setTimeout(() => {
        onSuccess && onSuccess({
          accessToken: access_token,
          refreshToken: refresh_token,
          did,
          user,
          redirectTo
        });
      }, 1500);
    } catch (err) {
      console.error('DID Login error:', err);
      setError(err.message || msg.errorLoginFailed);
      setStep('connect');
    }
  }, [formData, msg, onSuccess, redirectTo]);

  // 默认 Logo
  const defaultLogo = /*#__PURE__*/jsxs("svg", {
    viewBox: "0 0 40 40",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [/*#__PURE__*/jsx("rect", {
      width: "40",
      height: "40",
      rx: "10",
      fill: "url(#login-grad)"
    }), /*#__PURE__*/jsx("path", {
      d: "M12 20C12 15.5817 15.5817 12 20 12C24.4183 12 28 15.5817 28 20C28 24.4183 24.4183 28 20 28",
      stroke: "white",
      strokeWidth: "2.5",
      strokeLinecap: "round"
    }), /*#__PURE__*/jsx("circle", {
      cx: "20",
      cy: "20",
      r: "4",
      fill: "white"
    }), /*#__PURE__*/jsx("defs", {
      children: /*#__PURE__*/jsxs("linearGradient", {
        id: "login-grad",
        x1: "0",
        y1: "0",
        x2: "40",
        y2: "40",
        gradientUnits: "userSpaceOnUse",
        children: [/*#__PURE__*/jsx("stop", {
          stopColor: "#6366f1"
        }), /*#__PURE__*/jsx("stop", {
          offset: "1",
          stopColor: "#a855f7"
        })]
      })
    })]
  });
  return /*#__PURE__*/jsxs("div", {
    className: "did-login-page",
    children: [/*#__PURE__*/jsxs("div", {
      className: "did-login-bg",
      children: [/*#__PURE__*/jsx("div", {
        className: "did-login-orb did-login-orb-1"
      }), /*#__PURE__*/jsx("div", {
        className: "did-login-orb did-login-orb-2"
      }), /*#__PURE__*/jsx("div", {
        className: "did-login-grid"
      })]
    }), /*#__PURE__*/jsxs("div", {
      className: "did-login-container",
      children: [/*#__PURE__*/jsxs("a", {
        href: "/",
        className: "did-login-logo-link",
        onClick: e => {
          e.preventDefault();
          onNavigateToHome && onNavigateToHome();
        },
        children: [/*#__PURE__*/jsx("div", {
          className: "did-login-logo-icon",
          children: appLogo ? /*#__PURE__*/jsx("img", {
            src: appLogo,
            alt: appName
          }) : defaultLogo
        }), /*#__PURE__*/jsx("span", {
          children: appName
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "did-page-login-card",
        children: [/*#__PURE__*/jsxs("div", {
          className: "did-page-login-header",
          children: [/*#__PURE__*/jsx("h1", {
            children: language === 'zh' ? '登录' : 'Login'
          }), /*#__PURE__*/jsx("p", {
            children: msg.subtitle
          })]
        }), /*#__PURE__*/jsxs("div", {
          className: "did-page-login-tabs",
          children: [/*#__PURE__*/jsxs("button", {
            className: `did-page-login-tab ${loginType === 'wallet' ? 'active' : ''}`,
            onClick: () => {
              setLoginType('wallet');
              setError(null);
              setStep('connect');
            },
            children: [/*#__PURE__*/jsx(WalletIcon, {
              size: 18
            }), msg.walletSection]
          }), /*#__PURE__*/jsxs("button", {
            className: `did-page-login-tab ${loginType === 'did' ? 'active' : ''}`,
            onClick: () => {
              setLoginType('did');
              setError(null);
              setStep('connect');
            },
            children: [/*#__PURE__*/jsx(MailIcon, {
              size: 18
            }), msg.didSection]
          })]
        }), /*#__PURE__*/jsxs("div", {
          className: "did-page-login-content",
          children: [loginType === 'wallet' && /*#__PURE__*/jsxs(Fragment, {
            children: [step === 'connect' && /*#__PURE__*/jsxs("div", {
              className: "did-page-login-step",
              children: [/*#__PURE__*/jsx("div", {
                className: "did-wallet-icon-wrapper",
                children: /*#__PURE__*/jsx(WalletIcon, {
                  size: 48
                })
              }), /*#__PURE__*/jsx("p", {
                className: "did-step-description",
                children: msg.connectDescription
              }), /*#__PURE__*/jsxs("button", {
                className: "did-connect-btn",
                onClick: connectWallet,
                children: [/*#__PURE__*/jsx(WalletIcon, {
                  size: 20
                }), msg.connectWallet, /*#__PURE__*/jsx(ArrowRightIcon, {
                  size: 20
                })]
              }), /*#__PURE__*/jsxs("div", {
                className: "did-supported-wallets",
                children: [/*#__PURE__*/jsx("span", {
                  children: msg.supportedWallets
                }), /*#__PURE__*/jsxs("div", {
                  className: "did-wallet-logos",
                  children: [/*#__PURE__*/jsx("img", {
                    src: "https://metamask.io/images/metamask-fox.svg",
                    alt: "MetaMask"
                  }), /*#__PURE__*/jsx("img", {
                    src: "https://avatars.githubusercontent.com/u/18060234",
                    alt: "WalletConnect"
                  }), /*#__PURE__*/jsx("img", {
                    src: "https://avatars.githubusercontent.com/u/43919302",
                    alt: "Coinbase"
                  })]
                })]
              })]
            }), step === 'connecting' && /*#__PURE__*/jsxs("div", {
              className: "did-page-login-step did-loading-step",
              children: [/*#__PURE__*/jsx(LoaderIcon, {
                size: 48
              }), /*#__PURE__*/jsx("p", {
                children: msg.connecting
              })]
            }), step === 'signing' && /*#__PURE__*/jsxs("div", {
              className: "did-page-login-step did-signing-step",
              children: [/*#__PURE__*/jsxs("div", {
                className: "did-wallet-address",
                children: [/*#__PURE__*/jsx(ShieldIcon, {
                  size: 20
                }), /*#__PURE__*/jsx("code", {
                  children: formatAddress(walletAddress)
                })]
              }), /*#__PURE__*/jsx(LoaderIcon, {
                size: 32
              }), /*#__PURE__*/jsx("p", {
                children: msg.signing
              }), /*#__PURE__*/jsx("span", {
                className: "did-signing-hint",
                children: msg.securityNote
              })]
            })]
          }), loginType === 'did' && step === 'connect' && /*#__PURE__*/jsx("div", {
            className: "did-page-login-step",
            children: /*#__PURE__*/jsxs("form", {
              onSubmit: handleDIDLogin,
              className: "did-page-login-form",
              children: [/*#__PURE__*/jsxs("div", {
                className: "did-form-group",
                children: [/*#__PURE__*/jsx("label", {
                  htmlFor: "did",
                  children: language === 'zh' ? 'DID 标识符' : 'DID Identifier'
                }), /*#__PURE__*/jsx("input", {
                  type: "text",
                  id: "did",
                  value: formData.did,
                  onChange: e => setFormData({
                    ...formData,
                    did: e.target.value
                  }),
                  placeholder: "did:qbit:0x...",
                  required: true
                })]
              }), /*#__PURE__*/jsxs("div", {
                className: "did-form-group",
                children: [/*#__PURE__*/jsx("label", {
                  htmlFor: "password",
                  children: language === 'zh' ? '密码' : 'Password'
                }), /*#__PURE__*/jsxs("div", {
                  className: "did-password-input",
                  children: [/*#__PURE__*/jsx("input", {
                    type: showPassword ? 'text' : 'password',
                    id: "password",
                    value: formData.password,
                    onChange: e => setFormData({
                      ...formData,
                      password: e.target.value
                    }),
                    placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022",
                    required: true
                  }), /*#__PURE__*/jsx("button", {
                    type: "button",
                    className: "did-toggle-password",
                    onClick: () => setShowPassword(!showPassword),
                    children: showPassword ? /*#__PURE__*/jsx(EyeOffIcon, {
                      size: 18
                    }) : /*#__PURE__*/jsx(EyeIcon, {
                      size: 18
                    })
                  })]
                })]
              }), /*#__PURE__*/jsxs("button", {
                type: "submit",
                className: "did-connect-btn",
                children: [/*#__PURE__*/jsx(LockIcon, {
                  size: 20
                }), msg.loginButton, /*#__PURE__*/jsx(ArrowRightIcon, {
                  size: 20
                })]
              })]
            })
          }), loginType === 'did' && step === 'signing' && /*#__PURE__*/jsxs("div", {
            className: "did-page-login-step did-loading-step",
            children: [/*#__PURE__*/jsx(LoaderIcon, {
              size: 48
            }), /*#__PURE__*/jsx("p", {
              children: msg.verifying
            })]
          }), step === 'success' && /*#__PURE__*/jsxs("div", {
            className: "did-page-login-step did-success-step",
            children: [/*#__PURE__*/jsx("div", {
              className: "did-success-icon",
              children: /*#__PURE__*/jsx(CheckIcon, {
                size: 32
              })
            }), /*#__PURE__*/jsx("p", {
              children: msg.success
            }), /*#__PURE__*/jsx("span", {
              children: msg.redirecting
            })]
          }), error && /*#__PURE__*/jsxs("div", {
            className: "did-page-login-error",
            children: [/*#__PURE__*/jsx(AlertIcon, {
              size: 18
            }), /*#__PURE__*/jsx("span", {
              children: error
            })]
          })]
        }), /*#__PURE__*/jsxs("div", {
          className: "did-page-login-footer",
          children: [/*#__PURE__*/jsxs("p", {
            children: [msg.noAccountYet, /*#__PURE__*/jsx("a", {
              href: "/register",
              onClick: e => {
                e.preventDefault();
                onNavigateToRegister && onNavigateToRegister();
              },
              children: msg.register
            })]
          }), /*#__PURE__*/jsxs("p", {
            children: [msg.noWalletYet, /*#__PURE__*/jsx("a", {
              href: "https://metamask.io",
              target: "_blank",
              rel: "noopener noreferrer",
              children: msg.getWallet
            })]
          })]
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "did-page-login-features",
        children: [/*#__PURE__*/jsxs("div", {
          className: "did-feature",
          children: [/*#__PURE__*/jsx(ShieldIcon, {
            size: 20
          }), /*#__PURE__*/jsx("span", {
            children: msg.feature1
          })]
        }), /*#__PURE__*/jsxs("div", {
          className: "did-feature",
          children: [/*#__PURE__*/jsx(WalletIcon, {
            size: 20
          }), /*#__PURE__*/jsx("span", {
            children: msg.feature2
          })]
        })]
      })]
    })]
  });
}

/**
 * QuantaBit Auth SDK - 全页面注册组件 (RegisterPage)
 * 
 * 独立的全页注册页面，支持：
 * - 钱包注册（自动创建 DID）
 * - 邮箱注册
 * - 精美的深色背景动画
 */

function RegisterPage({
  onSuccess,
  onNavigateToLogin,
  onNavigateToHome,
  apiBaseUrl = '/api/v1',
  appName = 'QuantaBit',
  appLogo,
  language = 'zh',
  redirectTo = '/dashboard'
}) {
  const [registerMethod, setRegisterMethod] = useState('wallet'); // 'wallet' or 'email'
  const [step, setStep] = useState('connect'); // connect, connecting, creating, success
  const [walletAddress, setWalletAddress] = useState(null);
  const [emailForm, setEmailForm] = useState({
    nickname: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createdDID, setCreatedDID] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const msg = getMessages$1(language);

  // 配置 API
  useEffect(() => {
    if (apiBaseUrl) {
      authApi.configure({
        apiBaseUrl
      });
    }
  }, [apiBaseUrl]);

  // 钱包注册
  const connectAndRegister = useCallback(async () => {
    setError(null);
    if (!hasQBitWallet()) {
      setError(msg.noWallet);
      return;
    }
    try {
      setStep('connecting');
      const {
        address,
        response
      } = await loginWithBrowserWallet(authApi, 'quantabit');
      setWalletAddress(address);
      setStep('creating');
      const {
        access_token,
        refresh_token,
        did,
        user
      } = response;
      authApi.setTokens(access_token, refresh_token);
      setCreatedDID(did);
      setIsSuccess(true);
      setTimeout(() => {
        onSuccess && onSuccess({
          accessToken: access_token,
          refreshToken: refresh_token,
          did,
          user,
          isNewUser: true,
          redirectTo
        });
      }, 2500);
    } catch (err) {
      console.error('Register error:', err);
      if (err.code === 4001) {
        setError(msg.rejected);
      } else {
        setError(err.message || msg.errorRegisterFailed);
      }
      setStep('connect');
    }
  }, [msg, onSuccess, redirectTo]);

  // 邮箱注册
  const handleEmailRegister = useCallback(async e => {
    e.preventDefault();
    if (emailForm.password !== emailForm.confirmPassword) {
      setError(msg.errorPasswordMismatch);
      return;
    }
    setError(null);
    setEmailLoading(true);
    try {
      const authRes = await authApi.register({
        displayName: emailForm.nickname,
        email: emailForm.email,
        password: emailForm.password
      });
      const {
        access_token,
        refresh_token,
        did,
        user
      } = authRes.data || authRes;
      authApi.setTokens(access_token, refresh_token);
      setCreatedDID(did);
      setIsSuccess(true);
      setTimeout(() => {
        onSuccess && onSuccess({
          accessToken: access_token,
          refreshToken: refresh_token,
          did,
          user,
          isNewUser: true,
          redirectTo
        });
      }, 2500);
    } catch (err) {
      console.error('Email register error:', err);
      setError(err.message || msg.errorRegisterFailed);
      setEmailLoading(false);
    }
  }, [emailForm, msg, onSuccess, redirectTo]);

  // 默认 Logo
  const defaultLogo = /*#__PURE__*/jsxs("svg", {
    viewBox: "0 0 40 40",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [/*#__PURE__*/jsx("rect", {
      width: "40",
      height: "40",
      rx: "10",
      fill: "url(#register-grad)"
    }), /*#__PURE__*/jsx("path", {
      d: "M12 20C12 15.5817 15.5817 12 20 12C24.4183 12 28 15.5817 28 20C28 24.4183 24.4183 28 20 28",
      stroke: "white",
      strokeWidth: "2.5",
      strokeLinecap: "round"
    }), /*#__PURE__*/jsx("circle", {
      cx: "20",
      cy: "20",
      r: "4",
      fill: "white"
    }), /*#__PURE__*/jsx("defs", {
      children: /*#__PURE__*/jsxs("linearGradient", {
        id: "register-grad",
        x1: "0",
        y1: "0",
        x2: "40",
        y2: "40",
        gradientUnits: "userSpaceOnUse",
        children: [/*#__PURE__*/jsx("stop", {
          stopColor: "#6366f1"
        }), /*#__PURE__*/jsx("stop", {
          offset: "1",
          stopColor: "#a855f7"
        })]
      })
    })]
  });
  return /*#__PURE__*/jsxs("div", {
    className: "did-login-page",
    children: [/*#__PURE__*/jsxs("div", {
      className: "did-login-bg",
      children: [/*#__PURE__*/jsx("div", {
        className: "did-login-orb did-login-orb-1"
      }), /*#__PURE__*/jsx("div", {
        className: "did-login-orb did-login-orb-2"
      }), /*#__PURE__*/jsx("div", {
        className: "did-login-grid"
      })]
    }), /*#__PURE__*/jsxs("div", {
      className: "did-login-container",
      children: [/*#__PURE__*/jsxs("a", {
        href: "/",
        className: "did-login-logo-link",
        onClick: e => {
          e.preventDefault();
          onNavigateToHome && onNavigateToHome();
        },
        children: [/*#__PURE__*/jsx("div", {
          className: "did-login-logo-icon",
          children: appLogo ? /*#__PURE__*/jsx("img", {
            src: appLogo,
            alt: appName
          }) : defaultLogo
        }), /*#__PURE__*/jsx("span", {
          children: appName
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "did-page-login-card",
        children: [/*#__PURE__*/jsxs("div", {
          className: "did-page-login-header",
          children: [/*#__PURE__*/jsx("h1", {
            children: msg.createIdentity
          }), /*#__PURE__*/jsx("p", {
            children: msg.chooseMethod
          })]
        }), !isSuccess ? /*#__PURE__*/jsxs(Fragment, {
          children: [/*#__PURE__*/jsxs("div", {
            className: "did-page-login-tabs",
            children: [/*#__PURE__*/jsxs("button", {
              className: `did-page-login-tab ${registerMethod === 'wallet' ? 'active' : ''}`,
              onClick: () => {
                setRegisterMethod('wallet');
                setError(null);
              },
              children: [/*#__PURE__*/jsx(WalletIcon, {
                size: 18
              }), msg.walletMethod]
            }), /*#__PURE__*/jsxs("button", {
              className: `did-page-login-tab ${registerMethod === 'email' ? 'active' : ''}`,
              onClick: () => {
                setRegisterMethod('email');
                setError(null);
              },
              children: [/*#__PURE__*/jsx(MailIcon, {
                size: 18
              }), msg.emailMethod]
            })]
          }), /*#__PURE__*/jsxs("div", {
            className: "did-page-login-content",
            children: [registerMethod === 'wallet' && /*#__PURE__*/jsxs("div", {
              className: "did-page-login-step",
              children: [step === 'connect' && /*#__PURE__*/jsxs(Fragment, {
                children: [/*#__PURE__*/jsx("div", {
                  className: "did-wallet-icon-wrapper",
                  children: /*#__PURE__*/jsx(FingerprintIcon, {
                    size: 48
                  })
                }), /*#__PURE__*/jsx("p", {
                  className: "did-step-description",
                  children: msg.walletDesc
                }), /*#__PURE__*/jsxs("button", {
                  className: "did-connect-btn",
                  onClick: connectAndRegister,
                  children: [/*#__PURE__*/jsx(WalletIcon, {
                    size: 20
                  }), msg.connectWallet, /*#__PURE__*/jsx(ArrowRightIcon, {
                    size: 20
                  })]
                })]
              }), step === 'connecting' && /*#__PURE__*/jsxs("div", {
                className: "did-loading-step",
                children: [/*#__PURE__*/jsx(LoaderIcon, {
                  size: 48
                }), /*#__PURE__*/jsx("p", {
                  children: msg.connecting
                })]
              }), step === 'creating' && /*#__PURE__*/jsxs("div", {
                className: "did-signing-step",
                children: [/*#__PURE__*/jsxs("div", {
                  className: "did-wallet-address",
                  children: [/*#__PURE__*/jsx(WalletIcon, {
                    size: 20
                  }), /*#__PURE__*/jsx("code", {
                    children: formatAddress(walletAddress)
                  })]
                }), /*#__PURE__*/jsx(LoaderIcon, {
                  size: 32
                }), /*#__PURE__*/jsx("p", {
                  children: msg.signing
                })]
              })]
            }), registerMethod === 'email' && /*#__PURE__*/jsx("div", {
              className: "did-page-login-step",
              children: /*#__PURE__*/jsxs("form", {
                onSubmit: handleEmailRegister,
                className: "did-page-login-form",
                children: [/*#__PURE__*/jsxs("div", {
                  className: "did-form-group",
                  children: [/*#__PURE__*/jsx("label", {
                    children: msg.nickname
                  }), /*#__PURE__*/jsxs("div", {
                    className: "did-input-with-icon",
                    children: [/*#__PURE__*/jsx(UserIcon, {
                      size: 18
                    }), /*#__PURE__*/jsx("input", {
                      type: "text",
                      value: emailForm.nickname,
                      onChange: e => setEmailForm({
                        ...emailForm,
                        nickname: e.target.value
                      }),
                      placeholder: language === 'zh' ? "您的显示名称" : "Your display name",
                      required: true
                    })]
                  })]
                }), /*#__PURE__*/jsxs("div", {
                  className: "did-form-group",
                  children: [/*#__PURE__*/jsx("label", {
                    children: msg.email
                  }), /*#__PURE__*/jsxs("div", {
                    className: "did-input-with-icon",
                    children: [/*#__PURE__*/jsx(MailIcon, {
                      size: 18
                    }), /*#__PURE__*/jsx("input", {
                      type: "email",
                      value: emailForm.email,
                      onChange: e => setEmailForm({
                        ...emailForm,
                        email: e.target.value
                      }),
                      placeholder: "name@example.com",
                      required: true
                    })]
                  })]
                }), /*#__PURE__*/jsxs("div", {
                  className: "did-form-group",
                  children: [/*#__PURE__*/jsx("label", {
                    children: msg.password
                  }), /*#__PURE__*/jsxs("div", {
                    className: "did-password-input",
                    children: [/*#__PURE__*/jsx("input", {
                      type: showPassword ? 'text' : 'password',
                      value: emailForm.password,
                      onChange: e => setEmailForm({
                        ...emailForm,
                        password: e.target.value
                      }),
                      placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022",
                      required: true,
                      minLength: 6
                    }), /*#__PURE__*/jsx("button", {
                      type: "button",
                      className: "did-toggle-password",
                      onClick: () => setShowPassword(!showPassword),
                      children: showPassword ? /*#__PURE__*/jsx(EyeOffIcon, {
                        size: 18
                      }) : /*#__PURE__*/jsx(EyeIcon, {
                        size: 18
                      })
                    })]
                  })]
                }), /*#__PURE__*/jsxs("div", {
                  className: "did-form-group",
                  children: [/*#__PURE__*/jsx("label", {
                    children: msg.confirmPassword
                  }), /*#__PURE__*/jsx("div", {
                    className: "did-password-input",
                    children: /*#__PURE__*/jsx("input", {
                      type: showPassword ? 'text' : 'password',
                      value: emailForm.confirmPassword,
                      onChange: e => setEmailForm({
                        ...emailForm,
                        confirmPassword: e.target.value
                      }),
                      placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022",
                      required: true
                    })
                  })]
                }), /*#__PURE__*/jsxs("div", {
                  className: "did-quantabit-notice",
                  children: [/*#__PURE__*/jsx(ZapIcon, {
                    size: 16
                  }), /*#__PURE__*/jsx("span", {
                    children: msg.autoWallet
                  })]
                }), /*#__PURE__*/jsxs("button", {
                  type: "submit",
                  className: "did-connect-btn",
                  disabled: emailLoading,
                  children: [emailLoading ? /*#__PURE__*/jsx(LoaderIcon, {
                    size: 20
                  }) : /*#__PURE__*/jsx(ShieldIcon, {
                    size: 20
                  }), msg.createAccount, !emailLoading && /*#__PURE__*/jsx(ArrowRightIcon, {
                    size: 20
                  })]
                })]
              })
            }), error && /*#__PURE__*/jsxs("div", {
              className: "did-page-login-error",
              children: [/*#__PURE__*/jsx(AlertIcon, {
                size: 18
              }), /*#__PURE__*/jsx("span", {
                children: error
              })]
            })]
          })]
        }) : /*#__PURE__*/jsx("div", {
          className: "did-page-login-content",
          children: /*#__PURE__*/jsxs("div", {
            className: "did-page-login-step did-success-step",
            children: [/*#__PURE__*/jsx("div", {
              className: "did-success-icon",
              children: /*#__PURE__*/jsx(CheckIcon, {
                size: 32
              })
            }), /*#__PURE__*/jsxs("h2", {
              children: [msg.welcome, " ", appName]
            }), /*#__PURE__*/jsx("p", {
              children: msg.successDesc
            }), createdDID && /*#__PURE__*/jsxs("div", {
              className: "did-created-did-card",
              children: [/*#__PURE__*/jsx("div", {
                className: "did-label",
                children: msg.didIdentifier
              }), /*#__PURE__*/jsx("code", {
                className: "did-value",
                children: createdDID
              })]
            }), registerMethod === 'email' && /*#__PURE__*/jsxs("div", {
              className: "did-wallet-created-notice",
              children: [/*#__PURE__*/jsx(ZapIcon, {
                size: 16,
                color: "#f59e0b"
              }), /*#__PURE__*/jsx("span", {
                children: msg.autoWallet
              })]
            }), /*#__PURE__*/jsxs("div", {
              className: "did-redirect-hint",
              children: [/*#__PURE__*/jsx(LoaderIcon, {
                size: 14
              }), /*#__PURE__*/jsx("span", {
                children: msg.redirecting
              })]
            })]
          })
        }), /*#__PURE__*/jsx("div", {
          className: "did-page-login-footer",
          children: /*#__PURE__*/jsxs("p", {
            children: [msg.hasAccount, /*#__PURE__*/jsx("a", {
              href: "/login",
              onClick: e => {
                e.preventDefault();
                onNavigateToLogin && onNavigateToLogin();
              },
              children: msg.login
            })]
          })
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "did-page-login-features",
        children: [/*#__PURE__*/jsxs("div", {
          className: "did-feature",
          children: [/*#__PURE__*/jsx(ShieldIcon, {
            size: 20
          }), /*#__PURE__*/jsx("span", {
            children: msg.feature1
          })]
        }), /*#__PURE__*/jsxs("div", {
          className: "did-feature",
          children: [/*#__PURE__*/jsx(FingerprintIcon, {
            size: 20
          }), /*#__PURE__*/jsx("span", {
            children: msg.feature2
          })]
        })]
      })]
    })]
  });
}

/**
 * QuantaBit Auth SDK - 扁平化暗色全页面登录组件 (FlatDarkLoginPage)
 * 
 * 现代扁平化设计风格的全页登录页面，支持：
 * - 钱包登录（MetaMask, WalletConnect 等）
 * - DID 密码登录
 * - 邮箱注册（内置）
 * - 多账户切换
 * - 语言切换
 * 
 * 特点：
 * - 深色背景配合网格和光晕效果
 * - 玻璃拟态卡片设计
 * - 流畅的动画过渡
 * - 完全响应式布局
 */

function FlatDarkLoginPage({
  onSuccess,
  onNavigateToRegister,
  onNavigateToHome,
  onLogout,
  apiBaseUrl = '/api/v1',
  appName = 'QuantaBit',
  appLogo,
  language: initialLanguage,
  redirectTo = '/dashboard',
  showRegister = true,
  showLanguageSwitch = true,
  currentUser = null,
  initialMode = 'login',
  chainType = 'quantabit',
  showSocialLogin = true,
  socialProviders = ['apple', 'google', 'microsoft', 'wechat', 'tiktok'],
  renderSocialLogin
}) {
  const [mode, setMode] = useState(initialMode); // login, register, accounts
  const [loginType, setLoginType] = useState('wallet'); // wallet, did
  const [step, setStep] = useState('connect'); // connect, connecting, signing, success
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [walletAddress, setWalletAddress] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // 语言状态
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    if (initialLanguage) return initialLanguage;
    return initLanguage();
  });

  // 表单数据
  const [formData, setFormData] = useState({
    did: '',
    password: '',
    nickname: '',
    email: '',
    confirmPassword: ''
  });

  // 账户列表
  const [savedAccounts, setSavedAccounts] = useState([]);
  const msg = getMessages$1(currentLanguage);

  // 语言切换处理
  const handleLanguageChange = useCallback(newLang => {
    setCurrentLanguage(newLang);
    setLanguage(newLang);
  }, []);

  // 配置 API
  useEffect(() => {
    if (apiBaseUrl) {
      apiClient.configure({
        apiBaseUrl
      });
    }
  }, [apiBaseUrl]);

  // 初始化账户列表
  useEffect(() => {
    setSavedAccounts(getSavedAccounts());

    // 如果已登录，显示账户管理
    if (currentUser?.did && initialMode !== 'register') {
      setMode('accounts');
    }
  }, [currentUser, initialMode]);

  // 社交登录成功回调
  const handleSocialSuccess = useCallback(result => {
    const {
      access_token,
      refresh_token,
      did,
      user: resUser,
      is_new_user
    } = result;
    apiClient.setTokens(access_token, refresh_token);
    setCurrentDID(did);
    saveAccount({
      did,
      displayName: resUser?.display_name || formatDID$1(did),
      avatar: resUser?.avatar_url || resUser?.avatar,
      verified: true
    });
    setSavedAccounts(getSavedAccounts());
    setStep('success');
    setTimeout(() => {
      onSuccess && onSuccess({
        success: true,
        accessToken: access_token,
        refreshToken: refresh_token,
        did,
        user: {
          nickname: resUser?.display_name || resUser?.name,
          avatar: resUser?.avatar_url || resUser?.avatar,
          ...resUser
        },
        isNewUser: is_new_user,
        redirectTo
      });
    }, 1200);
  }, [onSuccess, redirectTo]);
  const handleSocialError = useCallback(err => {
    setError(err?.message || '社交登录失败');
  }, []);

  // 钱包登录
  const connectWallet = useCallback(async () => {
    setError(null);
    if (!hasQBitWallet() && !hasMetaMask()) {
      setError(msg.noWallet || '未检测到钱包');
      return;
    }
    try {
      setStep('connecting');
      const {
        address,
        response: result
      } = await loginWithBrowserWallet(apiClient, chainType);
      setWalletAddress(address);
      setStep('signing');
      const {
        access_token,
        refresh_token,
        did,
        user,
        is_new_user
      } = result;
      apiClient.setTokens(access_token, refresh_token);
      setCurrentDID(did);

      // 保存账户
      saveAccount({
        did,
        displayName: user?.nickname || formatAddress(address),
        avatar: user?.avatar,
        walletAddress: address,
        verified: true
      });
      setSavedAccounts(getSavedAccounts());
      setStep('success');
      setTimeout(() => {
        onSuccess && onSuccess({
          success: true,
          accessToken: access_token,
          refreshToken: refresh_token,
          did,
          user,
          isNewUser: is_new_user,
          redirectTo
        });
      }, 1200);
    } catch (err) {
      console.error('Login error:', err);
      if (err.code === 4001) {
        setError(msg.rejected || '用户拒绝了签名');
      } else {
        setError(err.message || msg.errorLoginFailed || '登录失败');
      }
      setStep('connect');
    }
  }, [chainType, msg, onSuccess, redirectTo]);

  // DID 登录
  const handleDIDLogin = useCallback(async e => {
    e.preventDefault();
    setError(null);
    if (!formData.did.trim() || !formData.password) {
      setError(currentLanguage === 'zh' ? '请输入 DID 和密码' : 'Please enter DID and password');
      return;
    }
    try {
      setLoading(true);
      setStep('signing');
      const authRes = await apiClient.didLogin(formData.did, formData.password);
      const {
        access_token,
        refresh_token,
        did,
        user
      } = authRes.data || authRes;
      apiClient.setTokens(access_token, refresh_token);
      setCurrentDID(did);

      // 保存账户
      saveAccount({
        did,
        displayName: user?.nickname || formatDID$1(did),
        avatar: user?.avatar,
        verified: true
      });
      setSavedAccounts(getSavedAccounts());
      setStep('success');
      setTimeout(() => {
        onSuccess && onSuccess({
          success: true,
          accessToken: access_token,
          refreshToken: refresh_token,
          did,
          user,
          isNewUser: false,
          redirectTo
        });
      }, 1200);
    } catch (err) {
      console.error('DID Login error:', err);
      setError(err.message || msg.errorLoginFailed || '登录失败');
      setStep('connect');
    } finally {
      setLoading(false);
    }
  }, [formData, currentLanguage, msg, onSuccess, redirectTo]);

  // 注册
  const handleRegister = useCallback(async e => {
    e.preventDefault();
    setError(null);

    // 验证
    if (!formData.nickname.trim()) {
      setError(currentLanguage === 'zh' ? '请输入昵称' : 'Please enter nickname');
      return;
    }
    if (!formData.password) {
      setError(currentLanguage === 'zh' ? '请输入密码' : 'Please enter password');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError(msg.errorPasswordMismatch || '两次密码不一致');
      return;
    }
    if (formData.password.length < 6) {
      setError(currentLanguage === 'zh' ? '密码至少6位' : 'Password must be at least 6 characters');
      return;
    }
    try {
      setLoading(true);
      const authRes = await apiClient.register({
        displayName: formData.nickname.trim(),
        email: formData.email || undefined,
        password: formData.password
      });
      const {
        access_token,
        refresh_token,
        did,
        user
      } = authRes.data || authRes;
      apiClient.setTokens(access_token, refresh_token);
      setCurrentDID(did);

      // 保存账户
      saveAccount({
        did,
        displayName: formData.nickname.trim(),
        verified: true
      });
      setSavedAccounts(getSavedAccounts());
      setSuccess(currentLanguage === 'zh' ? '注册成功！' : 'Registration successful!');
      setTimeout(() => {
        onSuccess && onSuccess({
          success: true,
          accessToken: access_token,
          refreshToken: refresh_token,
          did,
          user,
          isNewUser: true,
          redirectTo
        });
      }, 1200);
    } catch (err) {
      console.error('Register error:', err);
      setError(err.message || msg.errorRegisterFailed || '注册失败');
    } finally {
      setLoading(false);
    }
  }, [formData, currentLanguage, msg, onSuccess, redirectTo]);

  // 快速登录
  const handleQuickLogin = useCallback(account => {
    setFormData(prev => ({
      ...prev,
      did: account.did
    }));
    setMode('login');
    setLoginType('did');
    setTimeout(() => {
      document.getElementById('flat-password')?.focus();
    }, 100);
  }, []);

  // 移除账户
  const handleRemoveAccount = useCallback((did, e) => {
    e.stopPropagation();
    if (window.confirm(msg.removeConfirm || '确定移除此账户？')) {
      removeAccount(did);
      setSavedAccounts(getSavedAccounts());
    }
  }, [msg]);

  // 登出
  const handleLogout = useCallback(() => {
    clearCurrentSession();
    setMode('login');
    onLogout && onLogout();
  }, [onLogout]);

  // 切换到注册
  const goToRegister = useCallback(() => {
    if (onNavigateToRegister) {
      onNavigateToRegister();
    } else {
      setMode('register');
      setError(null);
      setFormData({
        did: '',
        password: '',
        nickname: '',
        email: '',
        confirmPassword: ''
      });
    }
  }, [onNavigateToRegister]);

  // 默认 Logo
  const defaultLogo = /*#__PURE__*/jsxs("svg", {
    viewBox: "0 0 40 40",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [/*#__PURE__*/jsx("rect", {
      width: "40",
      height: "40",
      rx: "10",
      fill: "url(#flat-page-grad)"
    }), /*#__PURE__*/jsx("path", {
      d: "M12 20C12 15.5817 15.5817 12 20 12C24.4183 12 28 15.5817 28 20C28 24.4183 24.4183 28 20 28",
      stroke: "white",
      strokeWidth: "2.5",
      strokeLinecap: "round"
    }), /*#__PURE__*/jsx("circle", {
      cx: "20",
      cy: "20",
      r: "4",
      fill: "white"
    }), /*#__PURE__*/jsx("defs", {
      children: /*#__PURE__*/jsxs("linearGradient", {
        id: "flat-page-grad",
        x1: "0",
        y1: "0",
        x2: "40",
        y2: "40",
        gradientUnits: "userSpaceOnUse",
        children: [/*#__PURE__*/jsx("stop", {
          stopColor: "#6366f1"
        }), /*#__PURE__*/jsx("stop", {
          offset: "1",
          stopColor: "#a855f7"
        })]
      })
    })]
  });
  const otherAccounts = savedAccounts.filter(a => a.did !== currentUser?.did);

  // 渲染账户管理视图
  const renderAccountsView = () => /*#__PURE__*/jsxs("div", {
    className: "flat-accounts-view",
    children: [/*#__PURE__*/jsxs("div", {
      className: "flat-accounts-header",
      children: [/*#__PURE__*/jsxs("button", {
        className: "flat-back-btn",
        onClick: () => setMode('login'),
        children: ["\u2190 ", msg.back || '返回']
      }), /*#__PURE__*/jsxs("h3", {
        children: [/*#__PURE__*/jsx(SwapIcon, {
          size: 18
        }), " ", msg.accountsSection || '账户管理']
      })]
    }), currentUser?.did && /*#__PURE__*/jsxs("div", {
      className: "flat-current-account",
      children: [/*#__PURE__*/jsx("p", {
        className: "flat-section-label",
        children: msg.currentAccount || '当前账户'
      }), /*#__PURE__*/jsxs("div", {
        className: "flat-account-card current",
        children: [/*#__PURE__*/jsx("div", {
          className: "flat-account-avatar",
          children: currentUser.avatar ? /*#__PURE__*/jsx("img", {
            src: currentUser.avatar,
            alt: ""
          }) : /*#__PURE__*/jsx("span", {
            children: "\uD83D\uDD10"
          })
        }), /*#__PURE__*/jsxs("div", {
          className: "flat-account-info",
          children: [/*#__PURE__*/jsxs("span", {
            className: "flat-account-name",
            children: [currentUser.nickname || currentUser.displayName || formatDID$1(currentUser.did), /*#__PURE__*/jsx(VerifiedIcon, {
              size: 14
            })]
          }), /*#__PURE__*/jsx("span", {
            className: "flat-account-did",
            children: formatDID$1(currentUser.did)
          })]
        }), /*#__PURE__*/jsx("button", {
          className: "flat-logout-btn",
          onClick: handleLogout,
          title: msg.logout,
          children: /*#__PURE__*/jsx(LogoutIcon, {
            size: 18
          })
        })]
      })]
    }), /*#__PURE__*/jsxs("button", {
      className: "flat-add-account-btn",
      onClick: () => setMode('login'),
      children: ["+ ", msg.addNewAccount || '添加新账户']
    }), otherAccounts.length > 0 && /*#__PURE__*/jsxs("div", {
      className: "flat-other-accounts",
      children: [/*#__PURE__*/jsx("p", {
        className: "flat-section-label",
        children: msg.otherAccounts || '其他账户'
      }), otherAccounts.map(account => /*#__PURE__*/jsxs("div", {
        className: "flat-account-card",
        onClick: () => handleQuickLogin(account),
        children: [/*#__PURE__*/jsx("div", {
          className: "flat-account-avatar",
          children: account.avatar ? /*#__PURE__*/jsx("img", {
            src: account.avatar,
            alt: ""
          }) : /*#__PURE__*/jsx("span", {
            children: "\uD83D\uDD10"
          })
        }), /*#__PURE__*/jsxs("div", {
          className: "flat-account-info",
          children: [/*#__PURE__*/jsxs("span", {
            className: "flat-account-name",
            children: [account.displayName || formatDID$1(account.did), account.verified && /*#__PURE__*/jsx(VerifiedIcon, {
              size: 14
            })]
          }), /*#__PURE__*/jsx("span", {
            className: "flat-account-did",
            children: formatDID$1(account.did)
          })]
        }), /*#__PURE__*/jsx("button", {
          className: "flat-switch-btn",
          children: msg.switchAccount || '切换'
        }), /*#__PURE__*/jsx("button", {
          className: "flat-remove-btn",
          onClick: e => handleRemoveAccount(account.did, e),
          title: msg.removeAccount || '移除',
          children: /*#__PURE__*/jsx(TrashIcon, {
            size: 16
          })
        })]
      }, account.did))]
    }), otherAccounts.length === 0 && !currentUser?.did && /*#__PURE__*/jsx("p", {
      className: "flat-no-accounts",
      children: msg.noOtherAccounts || '没有其他账户'
    })]
  });

  // 渲染注册视图
  const renderRegisterView = () => /*#__PURE__*/jsx("div", {
    className: "flat-page-login-step",
    children: /*#__PURE__*/jsxs("form", {
      onSubmit: handleRegister,
      className: "flat-page-login-form",
      children: [/*#__PURE__*/jsxs("div", {
        className: "flat-form-group",
        children: [/*#__PURE__*/jsxs("label", {
          htmlFor: "flat-nickname",
          children: [msg.nickname || '昵称', " *"]
        }), /*#__PURE__*/jsx("input", {
          type: "text",
          id: "flat-nickname",
          value: formData.nickname,
          onChange: e => setFormData({
            ...formData,
            nickname: e.target.value
          }),
          placeholder: currentLanguage === 'zh' ? '请输入昵称' : 'Enter nickname',
          required: true,
          disabled: loading
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "flat-form-group",
        children: [/*#__PURE__*/jsxs("label", {
          htmlFor: "flat-email",
          children: [msg.email || '邮箱', " (", currentLanguage === 'zh' ? '可选' : 'optional', ")"]
        }), /*#__PURE__*/jsx("input", {
          type: "email",
          id: "flat-email",
          value: formData.email,
          onChange: e => setFormData({
            ...formData,
            email: e.target.value
          }),
          placeholder: "user@example.com",
          disabled: loading
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "flat-form-group",
        children: [/*#__PURE__*/jsxs("label", {
          htmlFor: "flat-reg-password",
          children: [msg.password || '密码', " *"]
        }), /*#__PURE__*/jsxs("div", {
          className: "flat-password-input",
          children: [/*#__PURE__*/jsx("input", {
            type: showPassword ? 'text' : 'password',
            id: "flat-reg-password",
            value: formData.password,
            onChange: e => setFormData({
              ...formData,
              password: e.target.value
            }),
            placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022",
            required: true,
            disabled: loading
          }), /*#__PURE__*/jsx("button", {
            type: "button",
            className: "flat-toggle-password",
            onClick: () => setShowPassword(!showPassword),
            children: showPassword ? /*#__PURE__*/jsx(EyeOffIcon, {
              size: 18
            }) : /*#__PURE__*/jsx(EyeIcon, {
              size: 18
            })
          })]
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "flat-form-group",
        children: [/*#__PURE__*/jsxs("label", {
          htmlFor: "flat-confirm-password",
          children: [msg.confirmPassword || '确认密码', " *"]
        }), /*#__PURE__*/jsxs("div", {
          className: "flat-password-input",
          children: [/*#__PURE__*/jsx("input", {
            type: showConfirmPassword ? 'text' : 'password',
            id: "flat-confirm-password",
            value: formData.confirmPassword,
            onChange: e => setFormData({
              ...formData,
              confirmPassword: e.target.value
            }),
            placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022",
            required: true,
            disabled: loading
          }), /*#__PURE__*/jsx("button", {
            type: "button",
            className: "flat-toggle-password",
            onClick: () => setShowConfirmPassword(!showConfirmPassword),
            children: showConfirmPassword ? /*#__PURE__*/jsx(EyeOffIcon, {
              size: 18
            }) : /*#__PURE__*/jsx(EyeIcon, {
              size: 18
            })
          })]
        })]
      }), /*#__PURE__*/jsxs("button", {
        type: "submit",
        className: "flat-connect-btn",
        disabled: loading,
        children: [loading ? /*#__PURE__*/jsx(LoaderIcon, {
          size: 20
        }) : /*#__PURE__*/jsx(ShieldIcon, {
          size: 20
        }), loading ? msg.registering || '注册中...' : msg.registerButton || '创建身份', !loading && /*#__PURE__*/jsx(ArrowRightIcon, {
          size: 20
        })]
      })]
    })
  });
  return /*#__PURE__*/jsxs("div", {
    className: "flat-dark-login-page",
    children: [/*#__PURE__*/jsxs("div", {
      className: "flat-login-bg",
      children: [/*#__PURE__*/jsx("div", {
        className: "flat-login-orb flat-login-orb-1"
      }), /*#__PURE__*/jsx("div", {
        className: "flat-login-orb flat-login-orb-2"
      }), /*#__PURE__*/jsx("div", {
        className: "flat-login-grid"
      })]
    }), showLanguageSwitch && /*#__PURE__*/jsx("div", {
      className: "flat-login-language",
      children: /*#__PURE__*/jsx(LanguageSwitcher, {
        currentLanguage: currentLanguage,
        onLanguageChange: handleLanguageChange,
        variant: "dark",
        showFlag: true,
        showName: false
      })
    }), /*#__PURE__*/jsxs("div", {
      className: "flat-login-container",
      children: [/*#__PURE__*/jsxs("a", {
        href: "/",
        className: "flat-login-logo-link",
        onClick: e => {
          e.preventDefault();
          onNavigateToHome && onNavigateToHome();
        },
        children: [/*#__PURE__*/jsx("div", {
          className: "flat-login-logo-icon",
          children: appLogo ? /*#__PURE__*/jsx("img", {
            src: appLogo,
            alt: appName
          }) : defaultLogo
        }), /*#__PURE__*/jsx("span", {
          children: appName
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "flat-page-login-card",
        children: [/*#__PURE__*/jsxs("div", {
          className: "flat-page-login-header",
          children: [/*#__PURE__*/jsx("h1", {
            children: mode === 'register' ? msg.registerSection || '创建数字身份' : mode === 'accounts' ? msg.accountsSection || '账户管理' : currentLanguage === 'zh' ? '登录' : 'Login'
          }), /*#__PURE__*/jsx("p", {
            children: mode === 'register' ? msg.chooseMethod || '选择您的注册方式，开启去中心化之旅' : msg.subtitle
          })]
        }), mode === 'accounts' ? /*#__PURE__*/jsx("div", {
          className: "flat-page-login-content",
          children: renderAccountsView()
        }) : mode === 'register' ? /*#__PURE__*/jsxs(Fragment, {
          children: [/*#__PURE__*/jsxs("div", {
            className: "flat-page-login-content",
            children: [success ? /*#__PURE__*/jsxs("div", {
              className: "flat-page-login-step flat-success-step",
              children: [/*#__PURE__*/jsx("div", {
                className: "flat-success-icon",
                children: /*#__PURE__*/jsx(CheckIcon, {
                  size: 32
                })
              }), /*#__PURE__*/jsx("p", {
                children: success
              }), /*#__PURE__*/jsx("span", {
                children: msg.redirecting || '正在跳转...'
              })]
            }) : renderRegisterView(), error && /*#__PURE__*/jsxs("div", {
              className: "flat-page-login-error",
              children: [/*#__PURE__*/jsx(AlertIcon, {
                size: 18
              }), /*#__PURE__*/jsx("span", {
                children: error
              })]
            })]
          }), /*#__PURE__*/jsx("div", {
            className: "flat-page-login-footer",
            children: /*#__PURE__*/jsxs("p", {
              children: [msg.alreadyHaveDID || '已有账号？', /*#__PURE__*/jsx("button", {
                onClick: () => {
                  setMode('login');
                  setError(null);
                },
                children: msg.goLogin || '去登录'
              })]
            })
          })]
        }) : /*#__PURE__*/jsxs(Fragment, {
          children: [/*#__PURE__*/jsxs("div", {
            className: "flat-page-login-tabs",
            children: [/*#__PURE__*/jsxs("button", {
              className: `flat-page-login-tab ${loginType === 'wallet' ? 'active' : ''}`,
              onClick: () => {
                setLoginType('wallet');
                setError(null);
                setStep('connect');
              },
              children: [/*#__PURE__*/jsx(WalletIcon, {
                size: 18
              }), msg.walletSection || '钱包登录']
            }), /*#__PURE__*/jsxs("button", {
              className: `flat-page-login-tab ${loginType === 'did' ? 'active' : ''}`,
              onClick: () => {
                setLoginType('did');
                setError(null);
                setStep('connect');
              },
              children: [/*#__PURE__*/jsx(MailIcon, {
                size: 18
              }), msg.didSection || 'DID 登录']
            })]
          }), /*#__PURE__*/jsxs("div", {
            className: "flat-page-login-content",
            children: [loginType === 'wallet' && /*#__PURE__*/jsxs(Fragment, {
              children: [step === 'connect' && /*#__PURE__*/jsxs("div", {
                className: "flat-page-login-step",
                children: [/*#__PURE__*/jsx("div", {
                  className: "flat-wallet-icon-wrapper",
                  children: /*#__PURE__*/jsx(WalletIcon, {
                    size: 48
                  })
                }), /*#__PURE__*/jsx("p", {
                  className: "flat-step-description",
                  children: msg.connectDescription || '连接您的 Web3 钱包，安全验证您的去中心化身份'
                }), /*#__PURE__*/jsxs("button", {
                  className: "flat-connect-btn",
                  onClick: connectWallet,
                  children: [/*#__PURE__*/jsx(WalletIcon, {
                    size: 20
                  }), msg.connectWallet || '连接钱包', /*#__PURE__*/jsx(ArrowRightIcon, {
                    size: 20
                  })]
                }), /*#__PURE__*/jsxs("div", {
                  className: "flat-supported-wallets",
                  children: [/*#__PURE__*/jsx("span", {
                    children: msg.supportedWallets || '支持的钱包'
                  }), /*#__PURE__*/jsxs("div", {
                    className: "flat-wallet-logos",
                    children: [/*#__PURE__*/jsx("img", {
                      src: "https://metamask.io/images/metamask-fox.svg",
                      alt: "MetaMask"
                    }), /*#__PURE__*/jsx("img", {
                      src: "https://avatars.githubusercontent.com/u/18060234",
                      alt: "WalletConnect"
                    }), /*#__PURE__*/jsx("img", {
                      src: "https://avatars.githubusercontent.com/u/43919302",
                      alt: "Coinbase"
                    })]
                  })]
                })]
              }), step === 'connecting' && /*#__PURE__*/jsxs("div", {
                className: "flat-page-login-step flat-loading-step",
                children: [/*#__PURE__*/jsx(LoaderIcon, {
                  size: 48
                }), /*#__PURE__*/jsx("p", {
                  children: msg.connecting || '正在连接...'
                })]
              }), step === 'signing' && /*#__PURE__*/jsxs("div", {
                className: "flat-page-login-step flat-signing-step",
                children: [/*#__PURE__*/jsxs("div", {
                  className: "flat-wallet-address",
                  children: [/*#__PURE__*/jsx(ShieldIcon, {
                    size: 20
                  }), /*#__PURE__*/jsx("code", {
                    children: formatAddress(walletAddress)
                  })]
                }), /*#__PURE__*/jsx(LoaderIcon, {
                  size: 32
                }), /*#__PURE__*/jsx("p", {
                  children: msg.signing || '请在钱包中签名...'
                }), /*#__PURE__*/jsx("span", {
                  className: "flat-signing-hint",
                  children: msg.securityNote || '请确认在您的钱包中签名'
                })]
              })]
            }), loginType === 'did' && step === 'connect' && /*#__PURE__*/jsxs("div", {
              className: "flat-page-login-step",
              children: [savedAccounts.length > 0 && /*#__PURE__*/jsxs("div", {
                className: "flat-saved-accounts",
                children: [/*#__PURE__*/jsx("p", {
                  className: "flat-saved-label",
                  children: msg.savedAccounts || '已保存账户'
                }), savedAccounts.slice(0, 3).map(account => /*#__PURE__*/jsxs("button", {
                  className: "flat-saved-account",
                  onClick: () => handleQuickLogin(account),
                  children: [/*#__PURE__*/jsx("div", {
                    className: "flat-saved-avatar",
                    children: account.avatar ? /*#__PURE__*/jsx("img", {
                      src: account.avatar,
                      alt: ""
                    }) : /*#__PURE__*/jsx("span", {
                      children: "\uD83D\uDD10"
                    })
                  }), /*#__PURE__*/jsxs("div", {
                    className: "flat-saved-info",
                    children: [/*#__PURE__*/jsx("span", {
                      className: "flat-saved-name",
                      children: account.displayName || formatDID$1(account.did)
                    }), /*#__PURE__*/jsx("span", {
                      className: "flat-saved-did",
                      children: formatDID$1(account.did)
                    })]
                  }), /*#__PURE__*/jsx("span", {
                    className: "flat-quick-label",
                    children: msg.quickLogin || '快速登录'
                  })]
                }, account.did))]
              }), /*#__PURE__*/jsxs("form", {
                onSubmit: handleDIDLogin,
                className: "flat-page-login-form",
                children: [/*#__PURE__*/jsxs("div", {
                  className: "flat-form-group",
                  children: [/*#__PURE__*/jsx("label", {
                    htmlFor: "flat-did",
                    children: currentLanguage === 'zh' ? 'DID 标识符' : 'DID Identifier'
                  }), /*#__PURE__*/jsx("input", {
                    type: "text",
                    id: "flat-did",
                    value: formData.did,
                    onChange: e => setFormData({
                      ...formData,
                      did: e.target.value
                    }),
                    placeholder: "did:qbit:0x...",
                    required: true,
                    disabled: loading
                  })]
                }), /*#__PURE__*/jsxs("div", {
                  className: "flat-form-group",
                  children: [/*#__PURE__*/jsx("label", {
                    htmlFor: "flat-password",
                    children: currentLanguage === 'zh' ? '密码' : 'Password'
                  }), /*#__PURE__*/jsxs("div", {
                    className: "flat-password-input",
                    children: [/*#__PURE__*/jsx("input", {
                      type: showPassword ? 'text' : 'password',
                      id: "flat-password",
                      value: formData.password,
                      onChange: e => setFormData({
                        ...formData,
                        password: e.target.value
                      }),
                      placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022",
                      required: true,
                      disabled: loading
                    }), /*#__PURE__*/jsx("button", {
                      type: "button",
                      className: "flat-toggle-password",
                      onClick: () => setShowPassword(!showPassword),
                      children: showPassword ? /*#__PURE__*/jsx(EyeOffIcon, {
                        size: 18
                      }) : /*#__PURE__*/jsx(EyeIcon, {
                        size: 18
                      })
                    })]
                  })]
                }), /*#__PURE__*/jsxs("button", {
                  type: "submit",
                  className: "flat-connect-btn",
                  disabled: loading,
                  children: [loading ? /*#__PURE__*/jsx(LoaderIcon, {
                    size: 20
                  }) : /*#__PURE__*/jsx(LockIcon, {
                    size: 20
                  }), loading ? msg.loggingIn || '登录中...' : msg.loginButton || '安全登录', !loading && /*#__PURE__*/jsx(ArrowRightIcon, {
                    size: 20
                  })]
                })]
              })]
            }), loginType === 'did' && step === 'signing' && /*#__PURE__*/jsxs("div", {
              className: "flat-page-login-step flat-loading-step",
              children: [/*#__PURE__*/jsx(LoaderIcon, {
                size: 48
              }), /*#__PURE__*/jsx("p", {
                children: msg.verifying || '验证中...'
              })]
            }), step === 'success' && /*#__PURE__*/jsxs("div", {
              className: "flat-page-login-step flat-success-step",
              children: [/*#__PURE__*/jsx("div", {
                className: "flat-success-icon",
                children: /*#__PURE__*/jsx(CheckIcon, {
                  size: 32
                })
              }), /*#__PURE__*/jsx("p", {
                children: msg.success || '登录成功'
              }), /*#__PURE__*/jsx("span", {
                children: msg.redirecting || '正在跳转...'
              })]
            }), error && /*#__PURE__*/jsxs("div", {
              className: "flat-page-login-error",
              children: [/*#__PURE__*/jsx(AlertIcon, {
                size: 18
              }), /*#__PURE__*/jsx("span", {
                children: error
              })]
            })]
          }), showSocialLogin && /*#__PURE__*/jsxs("div", {
            className: "flat-page-social-login",
            style: {
              margin: '15px 0',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%'
            },
            children: [/*#__PURE__*/jsxs("div", {
              className: "flat-social-divider",
              style: {
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                margin: '10px 0 15px 0',
                color: '#6b7280',
                fontSize: '13px'
              },
              children: [/*#__PURE__*/jsx("div", {
                style: {
                  flex: 1,
                  height: '1px',
                  backgroundColor: 'rgba(255,255,255,0.08)'
                }
              }), /*#__PURE__*/jsx("span", {
                style: {
                  padding: '0 10px'
                },
                children: msg.orDivider || '或'
              }), /*#__PURE__*/jsx("div", {
                style: {
                  flex: 1,
                  height: '1px',
                  backgroundColor: 'rgba(255,255,255,0.08)'
                }
              })]
            }), renderSocialLogin ? typeof renderSocialLogin === 'function' ? renderSocialLogin() : renderSocialLogin : /*#__PURE__*/jsx(OAuthIconGroup, {
              providers: socialProviders,
              onSuccess: handleSocialSuccess,
              onError: handleSocialError
            })]
          }), /*#__PURE__*/jsxs("div", {
            className: "flat-page-login-footer",
            children: [showRegister && /*#__PURE__*/jsxs("p", {
              children: [msg.noAccountYet || '还没有账号？', /*#__PURE__*/jsx("button", {
                onClick: goToRegister,
                children: msg.register || '注册'
              })]
            }), /*#__PURE__*/jsxs("p", {
              children: [msg.noWalletYet || '还没有钱包？', /*#__PURE__*/jsx("a", {
                href: "https://metamask.io",
                target: "_blank",
                rel: "noopener noreferrer",
                children: msg.getWallet || '获取一个'
              })]
            })]
          })]
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "flat-page-login-features",
        children: [/*#__PURE__*/jsxs("div", {
          className: "flat-feature",
          children: [/*#__PURE__*/jsx(ShieldIcon, {
            size: 20
          }), /*#__PURE__*/jsx("span", {
            children: msg.feature1 || '数据完全自主掌控'
          })]
        }), /*#__PURE__*/jsxs("div", {
          className: "flat-feature",
          children: [/*#__PURE__*/jsx(ZapIcon, {
            size: 20
          }), /*#__PURE__*/jsx("span", {
            children: msg.feature2 || 'W3C 标准身份'
          })]
        })]
      })]
    })]
  });
}

/**
 * QuantaBit Auth SDK - 扁平化暗色全页面注册组件 (FlatDarkRegisterPage)
 * 
 * 现代扁平化设计风格的全页注册页面，支持：
 * - 钱包连接注册
 * - 邮箱/密码注册
 * - 语言切换
 */

function FlatDarkRegisterPage({
  onSuccess,
  onNavigateToLogin,
  onNavigateToHome,
  apiBaseUrl = '/api/v1',
  appName = 'QuantaBit',
  appLogo,
  language: initialLanguage,
  redirectTo = '/dashboard',
  showLanguageSwitch = true,
  showSocialLogin = true,
  socialProviders = ['apple', 'google', 'microsoft', 'wechat', 'tiktok'],
  renderSocialLogin
}) {
  const [registerType, setRegisterType] = useState('wallet'); // wallet, email
  const [step, setStep] = useState('input'); // input, connecting, creating, success
  const [error, setError] = useState(null);
  const [walletAddress, setWalletAddress] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // 语言状态
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    if (initialLanguage) return initialLanguage;
    return initLanguage();
  });

  // 表单数据
  const [formData, setFormData] = useState({
    nickname: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const msg = getMessages$1(currentLanguage);

  // 语言切换处理
  const handleLanguageChange = useCallback(newLang => {
    setCurrentLanguage(newLang);
    setLanguage(newLang);
  }, []);

  // 配置 API
  useEffect(() => {
    if (apiBaseUrl) {
      apiClient.configure({
        apiBaseUrl
      });
    }
  }, [apiBaseUrl]);

  // 社交登录成功回调
  const handleSocialSuccess = useCallback(result => {
    const {
      access_token,
      refresh_token,
      did,
      user: resUser,
      is_new_user
    } = result;
    apiClient.setTokens(access_token, refresh_token);
    setCurrentDID(did);
    saveAccount({
      did,
      displayName: resUser?.display_name || formatDID(did),
      avatar: resUser?.avatar_url || resUser?.avatar,
      verified: true
    });
    setStep('success');
    setTimeout(() => {
      onSuccess && onSuccess({
        success: true,
        accessToken: access_token,
        refreshToken: refresh_token,
        did,
        user: {
          nickname: resUser?.display_name || resUser?.name,
          avatar: resUser?.avatar_url || resUser?.avatar,
          ...resUser
        },
        isNewUser: is_new_user,
        redirectTo
      });
    }, 1200);
  }, [onSuccess, redirectTo]);
  const handleSocialError = useCallback(err => {
    setError(err?.message || '社交登录失败');
  }, []);

  // 钱包注册
  const connectWallet = useCallback(async () => {
    setError(null);
    if (!hasQBitWallet()) {
      setError(msg.noWallet || '未检测到钱包');
      return;
    }
    try {
      setStep('connecting');
      const {
        address,
        response
      } = await loginWithBrowserWallet(apiClient, 'quantabit');
      setWalletAddress(address);
      setStep('creating');
      const {
        access_token,
        refresh_token,
        did,
        user,
        is_new_user
      } = response;
      apiClient.setTokens(access_token, refresh_token);
      setCurrentDID(did);

      // 保存账户
      saveAccount({
        did,
        displayName: user?.nickname || formatAddress(address),
        avatar: user?.avatar,
        walletAddress: address,
        verified: true
      });
      setStep('success');
      setTimeout(() => {
        onSuccess && onSuccess({
          success: true,
          accessToken: access_token,
          refreshToken: refresh_token,
          did,
          user,
          isNewUser: is_new_user,
          redirectTo
        });
      }, 1200);
    } catch (err) {
      console.error('Register error:', err);
      if (err.code === 4001) {
        setError(msg.rejected || '用户拒绝了签名');
      } else {
        setError(err.message || msg.errorRegisterFailed || '注册失败');
      }
      setStep('input');
    }
  }, [msg, onSuccess, redirectTo]);

  // 邮箱注册
  const handleEmailRegister = useCallback(async e => {
    e.preventDefault();
    setError(null);

    // 验证
    if (!formData.nickname.trim()) {
      setError(currentLanguage === 'zh' ? '请输入昵称' : 'Please enter nickname');
      return;
    }
    if (!formData.password) {
      setError(currentLanguage === 'zh' ? '请输入密码' : 'Please enter password');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError(msg.errorPasswordMismatch || '两次密码不一致');
      return;
    }
    if (formData.password.length < 6) {
      setError(currentLanguage === 'zh' ? '密码至少6位' : 'Password must be at least 6 characters');
      return;
    }
    try {
      setLoading(true);
      setStep('creating');
      const authRes = await apiClient.register({
        displayName: formData.nickname.trim(),
        email: formData.email || undefined,
        password: formData.password
      });
      const {
        access_token,
        refresh_token,
        did,
        user
      } = authRes.data || authRes;
      apiClient.setTokens(access_token, refresh_token);
      setCurrentDID(did);

      // 保存账户
      saveAccount({
        did,
        displayName: formData.nickname.trim(),
        verified: true
      });
      setStep('success');
      setTimeout(() => {
        onSuccess && onSuccess({
          success: true,
          accessToken: access_token,
          refreshToken: refresh_token,
          did,
          user,
          isNewUser: true,
          redirectTo
        });
      }, 1200);
    } catch (err) {
      console.error('Register error:', err);
      setError(err.message || msg.errorRegisterFailed || '注册失败');
      setStep('input');
    } finally {
      setLoading(false);
    }
  }, [formData, currentLanguage, msg, onSuccess, redirectTo]);

  // 默认 Logo
  const defaultLogo = /*#__PURE__*/jsxs("svg", {
    viewBox: "0 0 40 40",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [/*#__PURE__*/jsx("rect", {
      width: "40",
      height: "40",
      rx: "10",
      fill: "url(#flat-reg-grad)"
    }), /*#__PURE__*/jsx("path", {
      d: "M12 20C12 15.5817 15.5817 12 20 12C24.4183 12 28 15.5817 28 20C28 24.4183 24.4183 28 20 28",
      stroke: "white",
      strokeWidth: "2.5",
      strokeLinecap: "round"
    }), /*#__PURE__*/jsx("circle", {
      cx: "20",
      cy: "20",
      r: "4",
      fill: "white"
    }), /*#__PURE__*/jsx("defs", {
      children: /*#__PURE__*/jsxs("linearGradient", {
        id: "flat-reg-grad",
        x1: "0",
        y1: "0",
        x2: "40",
        y2: "40",
        gradientUnits: "userSpaceOnUse",
        children: [/*#__PURE__*/jsx("stop", {
          stopColor: "#6366f1"
        }), /*#__PURE__*/jsx("stop", {
          offset: "1",
          stopColor: "#a855f7"
        })]
      })
    })]
  });
  return /*#__PURE__*/jsxs("div", {
    className: "flat-dark-login-page",
    children: [/*#__PURE__*/jsxs("div", {
      className: "flat-login-bg",
      children: [/*#__PURE__*/jsx("div", {
        className: "flat-login-orb flat-login-orb-1"
      }), /*#__PURE__*/jsx("div", {
        className: "flat-login-orb flat-login-orb-2"
      }), /*#__PURE__*/jsx("div", {
        className: "flat-login-grid"
      })]
    }), showLanguageSwitch && /*#__PURE__*/jsx("div", {
      className: "flat-login-language",
      children: /*#__PURE__*/jsx(LanguageSwitcher, {
        currentLanguage: currentLanguage,
        onLanguageChange: handleLanguageChange,
        variant: "dark",
        showFlag: true,
        showName: false
      })
    }), /*#__PURE__*/jsxs("div", {
      className: "flat-login-container",
      children: [/*#__PURE__*/jsxs("a", {
        href: "/",
        className: "flat-login-logo-link",
        onClick: e => {
          e.preventDefault();
          onNavigateToHome && onNavigateToHome();
        },
        children: [/*#__PURE__*/jsx("div", {
          className: "flat-login-logo-icon",
          children: appLogo ? /*#__PURE__*/jsx("img", {
            src: appLogo,
            alt: appName
          }) : defaultLogo
        }), /*#__PURE__*/jsx("span", {
          children: appName
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "flat-page-login-card",
        children: [/*#__PURE__*/jsxs("div", {
          className: "flat-page-login-header",
          children: [/*#__PURE__*/jsx("h1", {
            children: currentLanguage === 'zh' ? '创建数字身份' : 'Create Digital Identity'
          }), /*#__PURE__*/jsx("p", {
            children: msg.chooseMethod || '选择您的注册方式，开启去中心化之旅'
          })]
        }), /*#__PURE__*/jsxs("div", {
          className: "flat-page-login-tabs",
          children: [/*#__PURE__*/jsxs("button", {
            className: `flat-page-login-tab ${registerType === 'wallet' ? 'active' : ''}`,
            onClick: () => {
              setRegisterType('wallet');
              setError(null);
              setStep('input');
            },
            children: [/*#__PURE__*/jsx(WalletIcon, {
              size: 18
            }), msg.walletSection || '钱包连接']
          }), /*#__PURE__*/jsxs("button", {
            className: `flat-page-login-tab ${registerType === 'email' ? 'active' : ''}`,
            onClick: () => {
              setRegisterType('email');
              setError(null);
              setStep('input');
            },
            children: [/*#__PURE__*/jsx(MailIcon, {
              size: 18
            }), msg.emailSection || '邮箱注册']
          })]
        }), /*#__PURE__*/jsxs("div", {
          className: "flat-page-login-content",
          children: [registerType === 'wallet' && /*#__PURE__*/jsxs(Fragment, {
            children: [step === 'input' && /*#__PURE__*/jsxs("div", {
              className: "flat-page-login-step",
              children: [/*#__PURE__*/jsx("div", {
                className: "flat-wallet-icon-wrapper",
                children: /*#__PURE__*/jsx(WalletIcon, {
                  size: 48
                })
              }), /*#__PURE__*/jsx("p", {
                className: "flat-step-description",
                children: currentLanguage === 'zh' ? '连接您的 Web3 钱包，我们将为您创建一个与钱包地址绑定的 DID。' : 'Connect your Web3 wallet, we will create a DID bound to your wallet address.'
              }), /*#__PURE__*/jsxs("button", {
                className: "flat-connect-btn",
                onClick: connectWallet,
                children: [/*#__PURE__*/jsx(WalletIcon, {
                  size: 20
                }), msg.connectWallet || '连接钱包', /*#__PURE__*/jsx(ArrowRightIcon, {
                  size: 20
                })]
              }), /*#__PURE__*/jsxs("div", {
                className: "flat-supported-wallets",
                children: [/*#__PURE__*/jsx("span", {
                  children: msg.supportedWallets || '支持的钱包'
                }), /*#__PURE__*/jsxs("div", {
                  className: "flat-wallet-logos",
                  children: [/*#__PURE__*/jsx("img", {
                    src: "https://metamask.io/images/metamask-fox.svg",
                    alt: "MetaMask"
                  }), /*#__PURE__*/jsx("img", {
                    src: "https://avatars.githubusercontent.com/u/18060234",
                    alt: "WalletConnect"
                  }), /*#__PURE__*/jsx("img", {
                    src: "https://avatars.githubusercontent.com/u/43919302",
                    alt: "Coinbase"
                  })]
                })]
              })]
            }), step === 'connecting' && /*#__PURE__*/jsxs("div", {
              className: "flat-page-login-step flat-loading-step",
              children: [/*#__PURE__*/jsx(LoaderIcon, {
                size: 48
              }), /*#__PURE__*/jsx("p", {
                children: msg.connecting || '正在连接...'
              })]
            }), step === 'creating' && /*#__PURE__*/jsxs("div", {
              className: "flat-page-login-step flat-signing-step",
              children: [walletAddress && /*#__PURE__*/jsxs("div", {
                className: "flat-wallet-address",
                children: [/*#__PURE__*/jsx(ShieldIcon, {
                  size: 20
                }), /*#__PURE__*/jsx("code", {
                  children: formatAddress(walletAddress)
                })]
              }), /*#__PURE__*/jsx(LoaderIcon, {
                size: 32
              }), /*#__PURE__*/jsx("p", {
                children: currentLanguage === 'zh' ? '正在创建身份...' : 'Creating identity...'
              }), /*#__PURE__*/jsx("span", {
                className: "flat-signing-hint",
                children: msg.securityNote || '请确认在您的钱包中签名'
              })]
            })]
          }), registerType === 'email' && step === 'input' && /*#__PURE__*/jsx("div", {
            className: "flat-page-login-step",
            children: /*#__PURE__*/jsxs("form", {
              onSubmit: handleEmailRegister,
              className: "flat-page-login-form",
              children: [/*#__PURE__*/jsxs("div", {
                className: "flat-form-group",
                children: [/*#__PURE__*/jsxs("label", {
                  htmlFor: "flat-reg-nickname",
                  children: [msg.nickname || '昵称', " *"]
                }), /*#__PURE__*/jsx("input", {
                  type: "text",
                  id: "flat-reg-nickname",
                  value: formData.nickname,
                  onChange: e => setFormData({
                    ...formData,
                    nickname: e.target.value
                  }),
                  placeholder: currentLanguage === 'zh' ? '请输入昵称' : 'Enter nickname',
                  required: true,
                  disabled: loading
                })]
              }), /*#__PURE__*/jsxs("div", {
                className: "flat-form-group",
                children: [/*#__PURE__*/jsxs("label", {
                  htmlFor: "flat-reg-email",
                  children: [msg.email || '邮箱', " (", currentLanguage === 'zh' ? '可选' : 'optional', ")"]
                }), /*#__PURE__*/jsx("input", {
                  type: "email",
                  id: "flat-reg-email",
                  value: formData.email,
                  onChange: e => setFormData({
                    ...formData,
                    email: e.target.value
                  }),
                  placeholder: "user@example.com",
                  disabled: loading
                })]
              }), /*#__PURE__*/jsxs("div", {
                className: "flat-form-group",
                children: [/*#__PURE__*/jsxs("label", {
                  htmlFor: "flat-new-password",
                  children: [msg.password || '密码', " *"]
                }), /*#__PURE__*/jsxs("div", {
                  className: "flat-password-input",
                  children: [/*#__PURE__*/jsx("input", {
                    type: showPassword ? 'text' : 'password',
                    id: "flat-new-password",
                    value: formData.password,
                    onChange: e => setFormData({
                      ...formData,
                      password: e.target.value
                    }),
                    placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022",
                    required: true,
                    disabled: loading
                  }), /*#__PURE__*/jsx("button", {
                    type: "button",
                    className: "flat-toggle-password",
                    onClick: () => setShowPassword(!showPassword),
                    children: showPassword ? /*#__PURE__*/jsx(EyeOffIcon, {
                      size: 18
                    }) : /*#__PURE__*/jsx(EyeIcon, {
                      size: 18
                    })
                  })]
                })]
              }), /*#__PURE__*/jsxs("div", {
                className: "flat-form-group",
                children: [/*#__PURE__*/jsxs("label", {
                  htmlFor: "flat-confirm-pwd",
                  children: [msg.confirmPassword || '确认密码', " *"]
                }), /*#__PURE__*/jsxs("div", {
                  className: "flat-password-input",
                  children: [/*#__PURE__*/jsx("input", {
                    type: showConfirmPassword ? 'text' : 'password',
                    id: "flat-confirm-pwd",
                    value: formData.confirmPassword,
                    onChange: e => setFormData({
                      ...formData,
                      confirmPassword: e.target.value
                    }),
                    placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022",
                    required: true,
                    disabled: loading
                  }), /*#__PURE__*/jsx("button", {
                    type: "button",
                    className: "flat-toggle-password",
                    onClick: () => setShowConfirmPassword(!showConfirmPassword),
                    children: showConfirmPassword ? /*#__PURE__*/jsx(EyeOffIcon, {
                      size: 18
                    }) : /*#__PURE__*/jsx(EyeIcon, {
                      size: 18
                    })
                  })]
                })]
              }), /*#__PURE__*/jsxs("button", {
                type: "submit",
                className: "flat-connect-btn",
                disabled: loading,
                children: [loading ? /*#__PURE__*/jsx(LoaderIcon, {
                  size: 20
                }) : /*#__PURE__*/jsx(ShieldIcon, {
                  size: 20
                }), loading ? msg.registering || '注册中...' : msg.registerButton || '创建身份', !loading && /*#__PURE__*/jsx(ArrowRightIcon, {
                  size: 20
                })]
              })]
            })
          }), registerType === 'email' && step === 'creating' && /*#__PURE__*/jsxs("div", {
            className: "flat-page-login-step flat-loading-step",
            children: [/*#__PURE__*/jsx(LoaderIcon, {
              size: 48
            }), /*#__PURE__*/jsx("p", {
              children: currentLanguage === 'zh' ? '正在创建身份...' : 'Creating identity...'
            })]
          }), step === 'success' && /*#__PURE__*/jsxs("div", {
            className: "flat-page-login-step flat-success-step",
            children: [/*#__PURE__*/jsx("div", {
              className: "flat-success-icon",
              children: /*#__PURE__*/jsx(CheckIcon, {
                size: 32
              })
            }), /*#__PURE__*/jsx("p", {
              children: currentLanguage === 'zh' ? '注册成功！' : 'Registration successful!'
            }), /*#__PURE__*/jsx("span", {
              children: msg.redirecting || '正在跳转...'
            })]
          }), error && /*#__PURE__*/jsxs("div", {
            className: "flat-page-login-error",
            children: [/*#__PURE__*/jsx(AlertIcon, {
              size: 18
            }), /*#__PURE__*/jsx("span", {
              children: error
            })]
          })]
        }), showSocialLogin && /*#__PURE__*/jsxs("div", {
          className: "flat-page-social-login",
          style: {
            margin: '15px 0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%'
          },
          children: [/*#__PURE__*/jsxs("div", {
            className: "flat-social-divider",
            style: {
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              margin: '10px 0 15px 0',
              color: '#6b7280',
              fontSize: '13px'
            },
            children: [/*#__PURE__*/jsx("div", {
              style: {
                flex: 1,
                height: '1px',
                backgroundColor: 'rgba(255,255,255,0.08)'
              }
            }), /*#__PURE__*/jsx("span", {
              style: {
                padding: '0 10px'
              },
              children: msg.orDivider || '或'
            }), /*#__PURE__*/jsx("div", {
              style: {
                flex: 1,
                height: '1px',
                backgroundColor: 'rgba(255,255,255,0.08)'
              }
            })]
          }), renderSocialLogin ? typeof renderSocialLogin === 'function' ? renderSocialLogin() : renderSocialLogin : /*#__PURE__*/jsx(OAuthIconGroup, {
            providers: socialProviders,
            onSuccess: handleSocialSuccess,
            onError: handleSocialError
          })]
        }), /*#__PURE__*/jsx("div", {
          className: "flat-page-login-footer",
          children: /*#__PURE__*/jsxs("p", {
            children: [msg.alreadyHaveDID || '已有账号？', /*#__PURE__*/jsx("button", {
              onClick: () => onNavigateToLogin && onNavigateToLogin(),
              children: msg.goLogin || '登录'
            })]
          })
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "flat-page-login-features",
        children: [/*#__PURE__*/jsxs("div", {
          className: "flat-feature",
          children: [/*#__PURE__*/jsx(ShieldIcon, {
            size: 20
          }), /*#__PURE__*/jsx("span", {
            children: msg.feature1 || '数据完全自主掌控'
          })]
        }), /*#__PURE__*/jsxs("div", {
          className: "flat-feature",
          children: [/*#__PURE__*/jsx(ZapIcon, {
            size: 20
          }), /*#__PURE__*/jsx("span", {
            children: msg.feature2 || 'W3C 标准身份'
          })]
        })]
      })]
    })]
  });
}

/**
 * QuantaBit Auth SDK - useDIDChain Hook
 * 
 * DID 链上锚定与注册流程 Hook：
 * - 将 DID 注册到链上 (生成 Payload -> 外部钱包签名/广播 -> 提交 tx_hash)
 * - 更新链上 DID 文档 (生成 Payload -> 外部钱包签名/广播 -> 提交 tx_hash)
 * - 验证链上 DID 状态 (已确认/待确认/哈希校验)
 * - 从主网解析 DID 文档
 * - 支持轮询确认 (waitForChainConfirmation)
 */

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * useDIDChain Hook
 * 
 * @param {Object} options
 * @param {string} options.did - 当前用户的 DID
 * @param {string} options.apiBaseUrl - API 基础 URL
 * @returns {Object} DID 链上注册与验证方法和状态
 */
function useDIDChain({
  did,
  apiBaseUrl
} = {}) {
  const [chainDocument, setChainDocument] = useState(null);
  const [chainStatus, setChainStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 配置 API
  useEffect(() => {
    if (apiBaseUrl) {
      apiClient.configure({
        apiBaseUrl
      });
    }
  }, [apiBaseUrl]);

  /**
   * 从真实链解析 DID
   */
  const resolveDIDFromChain = useCallback(async didString => {
    setLoading(true);
    setError(null);
    try {
      const targetDid = didString || did;
      if (!targetDid) {
        throw new Error('缺少 DID，无法从链上解析');
      }
      const result = await apiClient.resolveDIDFromChain(targetDid);
      const data = result.data || result;
      setChainDocument(data);
      return data;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [did]);

  /**
   * 将当前 DID 注册到链上
   */
  const registerOnChain = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.registerDIDOnChain();
      const data = result.data || result;
      setChainStatus(data);
      return {
        success: true,
        data
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 提交 DID 注册后的链上 tx_hash
   */
  const submitRegisterTxHash = useCallback(async txHash => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.submitDIDRegisterTxHash(txHash);
      const data = result.data || result;
      setChainStatus(data);
      return {
        success: true,
        data
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 通过外部签名器完成 DID 注册广播并自动提交 tx_hash
   */
  const registerOnChainWithSigner = useCallback(async broadcaster => {
    const initial = await registerOnChain();
    if (!initial?.success) {
      return initial;
    }
    try {
      const completed = await completeExternalChainOperation$1(initial.data, broadcaster, submitRegisterTxHash);
      return {
        success: true,
        data: completed.submit?.data || completed.submit || initial.data,
        broadcast: completed.broadcast,
        txHash: completed.txHash,
        requiresExternalSigner: true
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err,
        data: initial.data
      };
    }
  }, [registerOnChain, submitRegisterTxHash]);

  /**
   * 使用 QBit Memo 锚定广播完成 DID 注册闭环
   */
  const registerOnChainWithQBitWallet = useCallback(async (options = {}) => {
    const broadcaster = createQBitMemoAnchorBroadcaster$1(options);
    return registerOnChainWithSigner(broadcaster);
  }, [registerOnChainWithSigner]);

  /**
   * 更新链上 DID 文档
   */
  const updateOnChain = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.updateDIDOnChain();
      const data = result.data || result;
      setChainStatus(data);
      return {
        success: true,
        data
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 提交 DID 更新后的链上 tx_hash
   */
  const submitUpdateTxHash = useCallback(async txHash => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.submitDIDUpdateTxHash(txHash);
      const data = result.data || result;
      setChainStatus(data);
      return {
        success: true,
        data
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 通过外部签名器完成 DID 更新广播并自动提交 tx_hash
   */
  const updateOnChainWithSigner = useCallback(async broadcaster => {
    const initial = await updateOnChain();
    if (!initial?.success) {
      return initial;
    }
    try {
      const completed = await completeExternalChainOperation$1(initial.data, broadcaster, submitUpdateTxHash);
      return {
        success: true,
        data: completed.submit?.data || completed.submit || initial.data,
        broadcast: completed.broadcast,
        txHash: completed.txHash,
        requiresExternalSigner: true
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err,
        data: initial.data
      };
    }
  }, [submitUpdateTxHash, updateOnChain]);

  /**
   * 使用 QBit Memo 锚定广播完成 DID 更新闭环
   */
  const updateOnChainWithQBitWallet = useCallback(async (options = {}) => {
    const broadcaster = createQBitMemoAnchorBroadcaster$1(options);
    return updateOnChainWithSigner(broadcaster);
  }, [updateOnChainWithSigner]);

  /**
   * 一次性完成 DID 链上注册（后端操作 → 钱包广播 → 确认/验证 → 提交 tx_hash）
   * 直接委托 apiClient 的高级编排，并同步 loading/error/chainStatus 状态。
   *
   * @param {Object} provider - QBit 钱包 provider
   * @param {Object} options - 编排选项（network/address/waitForConfirmation/verify...）
   */
  const registerWithWallet = useCallback(async (provider, options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.registerDIDWithWallet(provider, options);
      setChainStatus(result.submit?.data || result.submit || result.operation);
      return {
        success: true,
        ...result
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 一次性完成 DID 链上更新
   * @param {Object} provider - QBit 钱包 provider
   * @param {Object} options - 编排选项
   */
  const updateWithWallet = useCallback(async (provider, options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.updateDIDWithWallet(provider, options);
      setChainStatus(result.submit?.data || result.submit || result.operation);
      return {
        success: true,
        ...result
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 一次性完成凭证链上锚定
   * @param {string} credentialId - 凭证 ID
   * @param {Object} provider - QBit 钱包 provider
   * @param {Object} options - 编排选项
   */
  const anchorCredentialWithWallet = useCallback(async (credentialId, provider, options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.anchorCredentialWithWallet(credentialId, provider, options);
      setChainStatus(result.submit?.data || result.submit || result.operation);
      return {
        success: true,
        ...result
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 一次性完成凭证链上吊销
   * @param {string} credentialId - 凭证 ID
   * @param {Object} provider - QBit 钱包 provider
   * @param {Object} options - 编排选项（reason 通过 options.reason 传入）
   */
  const revokeCredentialWithWallet = useCallback(async (credentialId, provider, options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.revokeCredentialWithWallet(credentialId, provider, options);
      setChainStatus(result.submit?.data || result.submit || result.operation);
      return {
        success: true,
        ...result
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 校验 DID 链上状态
   */
  const verifyOnChain = useCallback(async (didString = did) => {
    const targetDid = didString || did;
    if (!targetDid) return null;
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.verifyDIDOnChain(targetDid);
      const data = result.data || result;
      setChainStatus(data);
      return data;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [did]);

  /**
   * 轮询 DID 链上状态，直到确认、锚定无效或超时
   */
  const waitForChainConfirmation = useCallback(async (didString = did, {
    intervalMs = 3000,
    timeoutMs = 60000
  } = {}) => {
    const targetDid = didString || did;
    if (!targetDid) {
      const missingDidError = new Error('缺少 DID');
      setError(missingDidError);
      return {
        success: false,
        error: missingDidError
      };
    }
    const startedAt = Date.now();
    while (Date.now() - startedAt <= timeoutMs) {
      const result = await verifyOnChain(targetDid);
      if (!result) {
        return {
          success: false,
          confirmed: false,
          timedOut: false,
          error: error || new Error('链上状态查询失败')
        };
      }
      if (result.invalid_anchor || result.confirmed && result.memo_hash_match === false) {
        return {
          success: false,
          confirmed: false,
          invalidAnchor: true,
          timedOut: false,
          data: result
        };
      }
      if (result.confirmed && result.hash_match) {
        return {
          success: true,
          confirmed: true,
          timedOut: false,
          data: result
        };
      }
      await sleep(intervalMs);
    }
    const latest = await verifyOnChain(targetDid);
    return {
      success: false,
      confirmed: false,
      timedOut: true,
      data: latest
    };
  }, [did, error, verifyOnChain]);

  /**
   * 提交 DID 注册交易后等待链上确认
   */
  const submitRegisterTxHashAndWait = useCallback(async (txHash, options = {}) => {
    const submitted = await submitRegisterTxHash(txHash);
    if (!submitted?.success) {
      return submitted;
    }
    const targetDid = did || submitted.data?.did;
    const waited = await waitForChainConfirmation(targetDid, options);
    return {
      ...waited,
      submitted: submitted.data,
      txHash
    };
  }, [did, submitRegisterTxHash, waitForChainConfirmation]);

  /**
   * 提交 DID 更新交易后等待链上确认
   */
  const submitUpdateTxHashAndWait = useCallback(async (txHash, options = {}) => {
    const submitted = await submitUpdateTxHash(txHash);
    if (!submitted?.success) {
      return submitted;
    }
    const targetDid = did || submitted.data?.did;
    const waited = await waitForChainConfirmation(targetDid, options);
    return {
      ...waited,
      submitted: submitted.data,
      txHash
    };
  }, [did, submitUpdateTxHash, waitForChainConfirmation]);
  return {
    chainDocument,
    chainStatus,
    loading,
    error,
    resolveDIDFromChain,
    registerOnChain,
    submitRegisterTxHash,
    registerOnChainWithSigner,
    registerOnChainWithQBitWallet,
    updateOnChain,
    submitUpdateTxHash,
    updateOnChainWithSigner,
    updateOnChainWithQBitWallet,
    registerWithWallet,
    updateWithWallet,
    anchorCredentialWithWallet,
    revokeCredentialWithWallet,
    verifyOnChain,
    waitForChainConfirmation,
    submitRegisterTxHashAndWait,
    submitUpdateTxHashAndWait
  };
}

/**
 * QuantaBit Auth SDK - useDID Hook
 * 
 * DID 管理 Hook：
 * - 获取当前 DID
 * - 解析 DID 文档
 * - 创建 DID
 * - 更新 DID 文档
 * - 委托 DID 链上注册与管理 (useDIDChain)
 */


/**
 * useDID Hook
 * 
 * @param {Object} options
 * @param {string} options.apiBaseUrl - API 基础 URL
 * @param {boolean} options.autoLoad - 是否自动加载（默认 true）
 * @returns {Object} DID 管理方法和状态
 */
function useDID({
  apiBaseUrl,
  autoLoad = true
} = {}) {
  const [did, setDID] = useState(null);
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 配置 API
  useEffect(() => {
    if (apiBaseUrl) {
      apiClient.configure({
        apiBaseUrl
      });
    }
  }, [apiBaseUrl]);

  // 引入并委托链上操作 Hook
  const {
    chainDocument,
    chainStatus,
    loading: chainLoading,
    error: chainError,
    resolveDIDFromChain,
    registerOnChain,
    submitRegisterTxHash,
    registerOnChainWithSigner,
    registerOnChainWithQBitWallet,
    updateOnChain,
    submitUpdateTxHash,
    updateOnChainWithSigner,
    updateOnChainWithQBitWallet,
    verifyOnChain,
    waitForChainConfirmation,
    submitRegisterTxHashAndWait,
    submitUpdateTxHashAndWait
  } = useDIDChain({
    did,
    apiBaseUrl
  });

  // 组合 loading 和 error 状态
  const finalLoading = loading || chainLoading;
  const finalError = error || chainError;

  /**
   * 获取当前用户 DID
   */
  const fetchMyDID = useCallback(async () => {
    if (!apiClient.isAuthenticated()) {
      return null;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.getMyDID();
      const data = result.data || result;
      setDID(data.did || data);
      setDocument(data.document);
      return data;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 解析 DID 文档
   */
  const resolveDID = useCallback(async didString => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.resolveDID(didString);
      const data = result.data || result;
      setDocument(data.document || data.did_document || data);
      return data;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 创建新 DID
   */
  const createDID = useCallback(async (options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.createDID(options);
      const data = result.data || result;
      setDID(data.did);
      setDocument(data.document);
      return {
        success: true,
        data
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 更新 DID 文档
   */
  const updateDocument = useCallback(async operation => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.updateDIDDocument(operation);
      const data = result.data || result;
      setDocument(data.document || data);
      return {
        success: true,
        data
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 添加密钥
   */
  const addKey = useCallback(async (keyType, publicKeyHex, signature) => {
    return updateDocument({
      operation: 'add_key',
      payload: {
        key_type: keyType,
        public_key_hex: publicKeyHex
      },
      signature
    });
  }, [updateDocument]);

  /**
   * 移除密钥
   */
  const removeKey = useCallback(async (keyId, signature) => {
    return updateDocument({
      operation: 'remove_key',
      payload: {
        key_id: keyId
      },
      signature
    });
  }, [updateDocument]);

  /**
   * 添加服务端点
   */
  const addService = useCallback(async (serviceId, type, endpoint, signature) => {
    return updateDocument({
      operation: 'add_service',
      payload: {
        id: serviceId,
        type,
        service_endpoint: endpoint
      },
      signature
    });
  }, [updateDocument]);

  /**
   * 移除服务端点
   */
  const removeService = useCallback(async (serviceId, signature) => {
    return updateDocument({
      operation: 'remove_service',
      payload: {
        service_id: serviceId
      },
      signature
    });
  }, [updateDocument]);

  // 自动加载
  useEffect(() => {
    if (autoLoad && apiClient.isAuthenticated()) {
      fetchMyDID();
    }
  }, [autoLoad, fetchMyDID]);
  return {
    did,
    document,
    chainDocument,
    chainStatus,
    loading: finalLoading,
    error: finalError,
    fetchMyDID,
    resolveDID,
    resolveDIDFromChain,
    createDID,
    updateDocument,
    registerOnChain,
    submitRegisterTxHash,
    submitRegisterTxHashAndWait,
    registerOnChainWithSigner,
    registerOnChainWithQBitWallet,
    updateOnChain,
    submitUpdateTxHash,
    submitUpdateTxHashAndWait,
    updateOnChainWithSigner,
    updateOnChainWithQBitWallet,
    verifyOnChain,
    waitForChainConfirmation,
    addKey,
    removeKey,
    addService,
    removeService,
    // 兼容别名
    registerDIDOnChain: registerOnChain,
    submitDIDRegisterTxHash: submitRegisterTxHash,
    updateDIDOnChain: updateOnChain,
    submitDIDUpdateTxHash: submitUpdateTxHash,
    // 修复: 之前错误地自引用
    verifyDIDOnChain: verifyOnChain,
    // 便捷属性
    isActive: document?.status === 'active',
    verificationMethods: document?.verificationMethod || [],
    services: document?.service || []
  };
}

function getMessages(language = 'zh') {
  const isZh = String(language).toLowerCase().startsWith('zh');
  if (isZh) {
    return {
      title: 'DID 链上状态',
      subtitle: 'QBit 主网 Memo 锚定校验',
      refresh: '刷新状态',
      waiting: '等待确认',
      verified: '锚定有效',
      invalid: '锚定无效',
      localOnly: '仅本地',
      unknown: '未检测',
      confirmed: '已确认',
      unconfirmed: '未确认',
      memoMatched: 'Memo 哈希匹配',
      memoMismatch: 'Memo 哈希不匹配',
      did: 'DID',
      txHash: '交易哈希',
      localHash: '本地哈希',
      chainHash: '链上哈希',
      expectedHash: '期望哈希',
      status: '状态',
      confirmation: '确认状态',
      noDid: '当前还没有可展示的 DID'
    };
  }
  return {
    title: 'DID Chain Status',
    subtitle: 'QBit mainnet memo anchor verification',
    refresh: 'Refresh',
    waiting: 'Pending confirmation',
    verified: 'Anchor verified',
    invalid: 'Invalid anchor',
    localOnly: 'Local only',
    unknown: 'Unknown',
    confirmed: 'Confirmed',
    unconfirmed: 'Unconfirmed',
    memoMatched: 'Memo hash matched',
    memoMismatch: 'Memo hash mismatched',
    did: 'DID',
    txHash: 'Tx Hash',
    localHash: 'Local Hash',
    chainHash: 'On-chain Hash',
    expectedHash: 'Expected Hash',
    status: 'Status',
    confirmation: 'Confirmation',
    noDid: 'No DID available yet'
  };
}
function DIDChainStatusCard({
  did: didProp,
  status,
  loading: loadingProp,
  onRefresh,
  apiBaseUrl,
  autoLoad = false,
  language = 'zh',
  className = ''
}) {
  const didHook = useDID({
    apiBaseUrl,
    autoLoad
  });
  const did = didProp || didHook.did;
  const chainStatus = status || didHook.chainStatus;
  const loading = loadingProp ?? didHook.loading;
  const msg = getMessages(language);
  const view = useMemo(() => {
    if (!chainStatus) {
      return {
        tone: 'neutral',
        label: msg.unknown,
        detail: msg.noDid
      };
    }
    if (chainStatus.invalid_anchor || chainStatus.confirmed && chainStatus.memo_hash_match === false) {
      return {
        tone: 'danger',
        label: msg.invalid,
        detail: msg.memoMismatch
      };
    }
    if (chainStatus.hash_match || chainStatus.is_registered_on_chain || chainStatus.confirmed && chainStatus.memo_hash_match) {
      return {
        tone: 'success',
        label: msg.verified,
        detail: msg.memoMatched
      };
    }
    if (chainStatus.local_only || chainStatus.on_chain === false) {
      return {
        tone: 'muted',
        label: msg.localOnly,
        detail: msg.unconfirmed
      };
    }
    if (chainStatus.pending_confirmation || chainStatus.confirmed === false) {
      return {
        tone: 'warning',
        label: msg.waiting,
        detail: msg.unconfirmed
      };
    }
    return {
      tone: 'neutral',
      label: msg.unknown,
      detail: chainStatus.confirmation_status || msg.unknown
    };
  }, [chainStatus, msg]);
  const refresh = onRefresh || (() => did && didHook.verifyOnChain(did));
  return /*#__PURE__*/jsxs("section", {
    className: `did-chain-card did-chain-card--${view.tone} ${className}`.trim(),
    children: [/*#__PURE__*/jsxs("div", {
      className: "did-chain-card__header",
      children: [/*#__PURE__*/jsxs("div", {
        children: [/*#__PURE__*/jsx("div", {
          className: "did-chain-card__eyebrow",
          children: msg.subtitle
        }), /*#__PURE__*/jsx("h3", {
          className: "did-chain-card__title",
          children: msg.title
        })]
      }), /*#__PURE__*/jsxs("button", {
        type: "button",
        className: "did-chain-card__refresh",
        onClick: refresh,
        disabled: loading || !did,
        children: [loading ? /*#__PURE__*/jsx(LoaderIcon, {
          size: 14
        }) : /*#__PURE__*/jsx(ShieldIcon, {
          size: 14
        }), /*#__PURE__*/jsx("span", {
          children: msg.refresh
        })]
      })]
    }), /*#__PURE__*/jsxs("div", {
      className: "did-chain-card__summary",
      children: [/*#__PURE__*/jsxs("div", {
        className: `did-chain-card__badge did-chain-card__badge--${view.tone}`,
        children: [view.tone === 'success' && /*#__PURE__*/jsx(CheckIcon, {
          size: 14
        }), view.tone !== 'success' && view.tone !== 'warning' && /*#__PURE__*/jsx(AlertIcon, {
          size: 14
        }), view.tone === 'warning' && /*#__PURE__*/jsx(LoaderIcon, {
          size: 14
        }), /*#__PURE__*/jsx("span", {
          children: view.label
        })]
      }), /*#__PURE__*/jsx("p", {
        className: "did-chain-card__detail",
        children: view.detail
      })]
    }), /*#__PURE__*/jsxs("div", {
      className: "did-chain-card__grid",
      children: [/*#__PURE__*/jsxs("div", {
        className: "did-chain-card__item",
        children: [/*#__PURE__*/jsx("span", {
          children: msg.did
        }), /*#__PURE__*/jsx("code", {
          children: did ? formatDID$1(did) : '--'
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "did-chain-card__item",
        children: [/*#__PURE__*/jsx("span", {
          children: msg.status
        }), /*#__PURE__*/jsx("strong", {
          children: chainStatus?.status || view.label
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "did-chain-card__item",
        children: [/*#__PURE__*/jsx("span", {
          children: msg.confirmation
        }), /*#__PURE__*/jsx("strong", {
          children: chainStatus?.confirmation_status || msg.unknown
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "did-chain-card__item",
        children: [/*#__PURE__*/jsx("span", {
          children: msg.txHash
        }), /*#__PURE__*/jsx("code", {
          children: chainStatus?.tx_hash ? formatAddress(chainStatus.tx_hash) : '--'
        })]
      })]
    }), /*#__PURE__*/jsxs("div", {
      className: "did-chain-card__hashes",
      children: [/*#__PURE__*/jsxs("div", {
        className: "did-chain-card__hash",
        children: [/*#__PURE__*/jsx("span", {
          children: msg.localHash
        }), /*#__PURE__*/jsx("code", {
          children: chainStatus?.local_document_hash || '--'
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "did-chain-card__hash",
        children: [/*#__PURE__*/jsx("span", {
          children: msg.chainHash
        }), /*#__PURE__*/jsx("code", {
          children: chainStatus?.on_chain_document_hash || chainStatus?.document_hash || '--'
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "did-chain-card__hash",
        children: [/*#__PURE__*/jsx("span", {
          children: msg.expectedHash
        }), /*#__PURE__*/jsx("code", {
          children: chainStatus?.expected_document_hash || chainStatus?.expected_hash || '--'
        })]
      })]
    })]
  });
}

/**
 * QuantaBit Auth SDK - DID Profile Panel
 * 
 * 通用 DID 个人中心面板组件
 * 生态内所有应用可共用，展示用户基础 DID 身份信息、钱包、链上状态
 * 
 * 使用场景：
 * - 用户点击头像/昵称 → 展开 DID 个人中心
 * - 可嵌入到任意生态应用的侧边栏、弹窗、页面中
 */

const panelMessages = {
  zh: {
    title: '身份中心',
    didLabel: 'DID 标识',
    walletLabel: '关联钱包',
    chainStatus: '链上状态',
    reputation: '声誉积分',
    credentials: '我的凭证',
    profile: '个人资料',
    wallets: '钱包管理',
    security: '安全设置',
    settings: '账户设置',
    switchAccount: '切换账户',
    logout: '退出登录',
    copied: '已复制',
    copy: '复制',
    noWallet: '未绑定钱包',
    viewAll: '查看全部',
    level: '等级',
    score: '分',
    verified: '已验证',
    unverified: '未验证'
  },
  en: {
    title: 'Identity Center',
    didLabel: 'DID Identifier',
    walletLabel: 'Linked Wallet',
    chainStatus: 'On-Chain Status',
    reputation: 'Reputation',
    credentials: 'Credentials',
    profile: 'Profile',
    wallets: 'Wallets',
    security: 'Security',
    settings: 'Settings',
    switchAccount: 'Switch Account',
    logout: 'Logout',
    copied: 'Copied',
    copy: 'Copy',
    noWallet: 'No wallet linked',
    viewAll: 'View All',
    level: 'Level',
    score: 'pts',
    verified: 'Verified',
    unverified: 'Unverified'
  },
  ja: {
    title: 'アイデンティティセンター',
    didLabel: 'DID 識別子',
    walletLabel: 'リンクウォレット',
    chainStatus: 'オンチェーン状態',
    reputation: '評判スコア',
    credentials: '認証情報',
    profile: 'プロフィール',
    wallets: 'ウォレット',
    security: 'セキュリティ',
    settings: '設定',
    switchAccount: 'アカウント切替',
    logout: 'ログアウト',
    copied: 'コピー済み',
    copy: 'コピー',
    noWallet: 'ウォレット未接続',
    viewAll: 'すべて表示',
    level: 'レベル',
    score: 'ポイント',
    verified: '認証済み',
    unverified: '未認証'
  },
  ko: {
    title: '신원 센터',
    didLabel: 'DID 식별자',
    walletLabel: '연결된 지갑',
    chainStatus: '온체인 상태',
    reputation: '평판 점수',
    credentials: '인증서',
    profile: '프로필',
    wallets: '지갑 관리',
    security: '보안 설정',
    settings: '계정 설정',
    switchAccount: '계정 전환',
    logout: '로그아웃',
    copied: '복사됨',
    copy: '복사',
    noWallet: '연결된 지갑 없음',
    viewAll: '전체 보기',
    level: '레벨',
    score: '점',
    verified: '인증됨',
    unverified: '미인증'
  }
};

/**
 * 获取用户显示名称 - 昵称优先
 * 统一的显示名获取逻辑，所有生态应用共用
 */
function getUserDisplayName(user) {
  if (!user) return '';
  // 昵称优先级链：nickname > displayName > display_name > username > 截断DID
  return user.nickname || user.displayName || user.display_name || user.username || user.name || formatDID$1(user.did, 12);
}

/**
 * 获取用户头像首字母
 */
function getAvatarInitial(user) {
  const name = getUserDisplayName(user);
  if (!name) return 'U';
  // 支持中文首字、英文首字母
  return name[0].toUpperCase();
}

/**
 * DIDProfilePanel - 通用 DID 个人中心面板
 * 
 * @param {Object} props
 * @param {Object} props.user - 用户信息 { did, nickname, avatar, wallets, reputation, ... }
 * @param {Function} props.onNavigate - 导航回调 (path) => void
 * @param {Function} props.onLogout - 登出回调
 * @param {Function} props.onSwitchAccount - 切换账户回调
 * @param {Function} props.onClose - 关闭面板回调
 * @param {string} props.apiBaseUrl - API 基础 URL
 * @param {string} props.language - 语言
 * @param {string} props.mode - 显示模式 (panel/drawer/inline)
 * @param {boolean} props.showChainStatus - 是否显示链上状态卡片 (default: true)
 * @param {boolean} props.showQuickActions - 是否显示快捷操作 (default: true)
 * @param {Object} props.chainStatus - 外部传入的链上状态（可选，不传则内部查询）
 * @param {Function} props.onVerifyChain - 触发链上验证的回调（可选）
 * @param {Array} props.extraMenuItems - 扩展菜单项 [{ key, label, icon, onClick }]
 * @param {Array} props.quickActions - 完全替换快捷操作列表（高级自定义）
 * @param {string} props.className - 自定义 CSS 类名
 */
function DIDProfilePanel({
  user,
  onNavigate,
  onLogout,
  onSwitchAccount,
  onClose,
  apiBaseUrl = '/api/v1',
  language: initialLanguage = 'zh',
  mode = 'panel',
  showChainStatus = true,
  showQuickActions = true,
  // 外部直接传入链上状态（避免重复 hook 实例化）
  chainStatus: externalChainStatus = null,
  onVerifyChain = null,
  // 扩展定制
  extraMenuItems = [],
  quickActions: customQuickActions = null,
  className = ''
}) {
  const [copiedField, setCopiedField] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(() => normalizeLanguage(initialLanguage));
  // 内部链上状态（仅当外部未传入 chainStatus 时才自查）
  const [internalChainStatus, setInternalChainStatus] = useState(null);

  // 优先使用外部传入的链上状态
  const chainStatus = externalChainStatus ?? internalChainStatus;
  const [chainLoading, setChainLoading] = useState(false);

  // 触发链上验证（优先用外部回调，否则用内部实现）
  const handleVerifyChain = useCallback(async didString => {
    if (onVerifyChain) {
      onVerifyChain(didString);
      return;
    }
    // 无外部回调时降级：内部调用（动态 import 避免循环依赖）
    try {
      setChainLoading(true);
      const {
        apiClient
      } = await Promise.resolve().then(function () { return apiClient$1; });
      if (apiBaseUrl) apiClient.configure({
        apiBaseUrl
      });
      const result = await apiClient.verifyDIDOnChain(didString);
      setInternalChainStatus(result?.data || result);
    } catch (e) {
      console.warn('[DIDProfilePanel] verifyOnChain error:', e);
    } finally {
      setChainLoading(false);
    }
  }, [onVerifyChain, apiBaseUrl]);

  // 语言同步
  useEffect(() => {
    setCurrentLanguage(normalizeLanguage(initialLanguage));
  }, [initialLanguage]);

  // 订阅全局语言变更
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = e => {
      const newLang = e.detail?.language;
      if (newLang) setCurrentLanguage(normalizeLanguage(newLang));
    };
    window.addEventListener('qbit-did:language-change', handler);
    return () => window.removeEventListener('qbit-did:language-change', handler);
  }, []);

  // 加载链上状态
  useEffect(() => {
    if (user?.did && showChainStatus) {
      handleVerifyChain(user.did);
    }
  }, [user?.did, showChainStatus, handleVerifyChain]);
  const msg = panelMessages[currentLanguage] || panelMessages.zh;

  // 复制到剪贴板
  const handleCopy = useCallback(async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (e) {
      // 降级方案
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  }, []);
  const handleNavigate = useCallback(path => {
    onNavigate?.(path);
  }, [onNavigate]);
  if (!user) return null;
  const displayName = getUserDisplayName(user);
  const walletAddress = user.wallet_address || user.walletAddress || user.wallets?.[0]?.address;
  const reputationScore = user.reputation_score || user.reputationScore || user.reputation || 0;
  const reputationLevel = user.reputation_level || user.reputationLevel || user.level || 1;
  const credentialCount = user.credential_count || user.credentialCount || user.credentials?.length || 0;

  // 快捷操作菜单（允许外部完全替换）
  const quickActions = customQuickActions || [{
    key: 'profile',
    label: msg.profile,
    icon: /*#__PURE__*/jsx(IdCardIcon, {
      size: 18
    }),
    onClick: () => handleNavigate('/dashboard/profile')
  }, {
    key: 'credentials',
    label: msg.credentials,
    icon: /*#__PURE__*/jsx(VerifiedIcon, {
      size: 18
    }),
    onClick: () => handleNavigate('/dashboard/credentials'),
    badge: credentialCount > 0 ? credentialCount : null
  }, {
    key: 'wallets',
    label: msg.wallets,
    icon: /*#__PURE__*/jsx(WalletIcon, {
      size: 18
    }),
    onClick: () => handleNavigate('/dashboard/wallets')
  }, {
    key: 'security',
    label: msg.security,
    icon: /*#__PURE__*/jsx(KeyIcon, {
      size: 18
    }),
    onClick: () => handleNavigate('/dashboard/security')
  }, {
    key: 'settings',
    label: msg.settings,
    icon: /*#__PURE__*/jsx(SettingsIcon, {
      size: 18
    }),
    onClick: () => handleNavigate('/dashboard/settings')
  }, ...extraMenuItems];
  return /*#__PURE__*/jsxs("div", {
    className: `did-profile-panel did-profile-${mode} ${className}`,
    children: [/*#__PURE__*/jsxs("div", {
      className: "did-profile-header",
      children: [onClose && /*#__PURE__*/jsx("button", {
        className: "did-profile-close",
        onClick: onClose,
        children: /*#__PURE__*/jsx(CloseIcon, {
          size: 18
        })
      }), /*#__PURE__*/jsxs("div", {
        className: "did-profile-identity",
        children: [/*#__PURE__*/jsxs("div", {
          className: "did-profile-avatar",
          children: [user.avatar || user.avatar_url ? /*#__PURE__*/jsx("img", {
            src: user.avatar || user.avatar_url,
            alt: displayName
          }) : /*#__PURE__*/jsx("span", {
            className: "did-profile-avatar-initial",
            children: getAvatarInitial(user)
          }), user.verified && /*#__PURE__*/jsx("span", {
            className: "did-profile-verified-badge",
            children: /*#__PURE__*/jsx(VerifiedIcon, {
              size: 12
            })
          })]
        }), /*#__PURE__*/jsxs("div", {
          className: "did-profile-info",
          children: [/*#__PURE__*/jsxs("h3", {
            className: "did-profile-name",
            children: [displayName, user.verified && /*#__PURE__*/jsx(VerifiedIcon, {
              size: 14
            })]
          }), user.did && /*#__PURE__*/jsxs("div", {
            className: "did-profile-did-row",
            children: [/*#__PURE__*/jsx("span", {
              className: "did-profile-did-label",
              children: msg.didLabel
            }), /*#__PURE__*/jsx("span", {
              className: "did-profile-did-value",
              children: formatDID$1(user.did, 16)
            }), /*#__PURE__*/jsx("button", {
              className: "did-profile-copy-btn",
              onClick: () => handleCopy(user.did, 'did'),
              title: copiedField === 'did' ? msg.copied : msg.copy,
              children: copiedField === 'did' ? '✓' : '📋'
            })]
          }), walletAddress && /*#__PURE__*/jsxs("div", {
            className: "did-profile-wallet-row",
            children: [/*#__PURE__*/jsx(WalletIcon, {
              size: 12
            }), /*#__PURE__*/jsx("span", {
              className: "did-profile-wallet-value",
              children: formatAddress(walletAddress)
            }), /*#__PURE__*/jsx("button", {
              className: "did-profile-copy-btn",
              onClick: () => handleCopy(walletAddress, 'wallet'),
              title: copiedField === 'wallet' ? msg.copied : msg.copy,
              children: copiedField === 'wallet' ? '✓' : '📋'
            })]
          })]
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: "did-profile-stats",
        children: [/*#__PURE__*/jsxs("div", {
          className: "did-profile-stat",
          children: [/*#__PURE__*/jsx(StarIcon, {
            size: 16
          }), /*#__PURE__*/jsx("span", {
            className: "did-profile-stat-value",
            children: reputationScore
          }), /*#__PURE__*/jsx("span", {
            className: "did-profile-stat-label",
            children: msg.reputation
          })]
        }), /*#__PURE__*/jsx("div", {
          className: "did-profile-stat-divider"
        }), /*#__PURE__*/jsxs("div", {
          className: "did-profile-stat",
          children: [/*#__PURE__*/jsx(ShieldIcon, {
            size: 16
          }), /*#__PURE__*/jsxs("span", {
            className: "did-profile-stat-value",
            children: ["Lv.", reputationLevel]
          }), /*#__PURE__*/jsx("span", {
            className: "did-profile-stat-label",
            children: msg.level
          })]
        }), /*#__PURE__*/jsx("div", {
          className: "did-profile-stat-divider"
        }), /*#__PURE__*/jsxs("div", {
          className: "did-profile-stat",
          children: [/*#__PURE__*/jsx(VerifiedIcon, {
            size: 16
          }), /*#__PURE__*/jsx("span", {
            className: "did-profile-stat-value",
            children: credentialCount
          }), /*#__PURE__*/jsx("span", {
            className: "did-profile-stat-label",
            children: msg.credentials
          })]
        })]
      })]
    }), showChainStatus && user.did && /*#__PURE__*/jsx("div", {
      className: "did-profile-chain-section",
      children: /*#__PURE__*/jsx(DIDChainStatusCard, {
        did: user.did,
        status: chainStatus,
        loading: chainLoading,
        onRefresh: () => handleVerifyChain(user.did),
        apiBaseUrl: apiBaseUrl,
        autoLoad: false,
        language: currentLanguage
      })
    }), showQuickActions && /*#__PURE__*/jsx("div", {
      className: "did-profile-actions",
      children: quickActions.map(action => /*#__PURE__*/jsxs("button", {
        className: `did-profile-action-item ${action.danger ? 'danger' : ''}`,
        onClick: action.onClick,
        children: [action.icon, /*#__PURE__*/jsx("span", {
          className: "did-profile-action-label",
          children: action.label
        }), action.badge != null && /*#__PURE__*/jsx("span", {
          className: "did-profile-action-badge",
          children: action.badge
        }), /*#__PURE__*/jsx(ArrowRightIcon, {
          size: 14
        })]
      }, action.key))
    }), /*#__PURE__*/jsxs("div", {
      className: "did-profile-footer",
      children: [onSwitchAccount && /*#__PURE__*/jsxs("button", {
        className: "did-profile-footer-btn",
        onClick: onSwitchAccount,
        children: [/*#__PURE__*/jsx(SwapIcon, {
          size: 16
        }), /*#__PURE__*/jsx("span", {
          children: msg.switchAccount
        })]
      }), onLogout && /*#__PURE__*/jsxs("button", {
        className: "did-profile-footer-btn danger",
        onClick: onLogout,
        children: [/*#__PURE__*/jsx(LogoutIcon, {
          size: 16
        }), /*#__PURE__*/jsx("span", {
          children: msg.logout
        })]
      })]
    })]
  });
}

/**
 * QuantaBit Auth SDK - useLogin Hook
 * 
 * 提供命令式的登录方法，便于在代码中调用
 */


/**
 * useLogin Hook
 * 
 * @param {Object} options
 * @param {string} options.apiBaseUrl - API 基础 URL
 * @param {Function} options.onSuccess - 登录成功回调
 * @param {Function} options.onError - 错误回调
 */
function useLogin({
  apiBaseUrl,
  onSuccess,
  onError
} = {}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 配置 API
  if (apiBaseUrl) {
    apiClient.configure({
      apiBaseUrl
    });
  }

  /**
   * 钱包登录
   */
  const loginWithWallet = useCallback(async (chainType = 'quantabit') => {
    setLoading(true);
    setError(null);
    try {
      const {
        address: walletAddress,
        response: result
      } = await loginWithBrowserWallet(apiClient, chainType);

      // 保存 session
      apiClient.setTokens(result.access_token, result.refresh_token);
      setCurrentDID(result.did);
      saveAccount({
        did: result.did,
        displayName: result.display_name || result.user?.nickname,
        avatar: result.avatar || result.user?.avatar,
        walletAddress,
        verified: true
      });
      const successResult = {
        success: true,
        accessToken: result.access_token,
        refreshToken: result.refresh_token,
        did: result.did,
        isNewUser: result.is_new_user,
        user: result.user
      };
      onSuccess && onSuccess(successResult);
      setLoading(false);
      return successResult;
    } catch (err) {
      setError(err);
      onError && onError(err);
      setLoading(false);
      return {
        success: false,
        error: err
      };
    }
  }, [onSuccess, onError]);

  /**
   * DID 密码登录
   */
  const loginWithDID = useCallback(async (did, password, pin = null, totpCode = null) => {
    setLoading(true);
    setError(null);
    let actualPin = pin;
    let actualTotp = totpCode;

    // Smart parsing for backwards compatibility (did, password, totpCode)
    if (pin !== null && totpCode === null) {
      const pinStr = String(pin).trim();
      if (pinStr.length === 6 && !isNaN(pinStr)) {
        actualPin = null;
        actualTotp = pinStr;
      }
    }
    try {
      const loginRes = await apiClient.didLogin(did, password, actualPin, actualTotp);
      const result = loginRes.data || loginRes;

      // 保存 session
      apiClient.setTokens(result.access_token, result.refresh_token);
      setCurrentDID(result.did);
      saveAccount({
        did: result.did,
        displayName: result.display_name || result.user?.nickname,
        avatar: result.avatar || result.user?.avatar,
        verified: true
      });
      const successResult = {
        success: true,
        accessToken: result.access_token,
        refreshToken: result.refresh_token,
        did: result.did,
        isNewUser: false,
        user: result.user
      };
      onSuccess && onSuccess(successResult);
      setLoading(false);
      return successResult;
    } catch (err) {
      setError(err);
      onError && onError(err);
      setLoading(false);
      return {
        success: false,
        error: err
      };
    }
  }, [onSuccess, onError]);

  /**
   * 注册新账户
   */
  const register = useCallback(async ({
    nickname,
    email,
    password
  }) => {
    setLoading(true);
    setError(null);
    try {
      const registerRes = await apiClient.register({
        displayName: nickname,
        email,
        password
      });
      const result = registerRes.data || registerRes;

      // 保存 session
      apiClient.setTokens(result.access_token, result.refresh_token);
      setCurrentDID(result.did);
      saveAccount({
        did: result.did,
        displayName: nickname,
        verified: true
      });
      const successResult = {
        success: true,
        accessToken: result.access_token,
        refreshToken: result.refresh_token,
        did: result.did,
        isNewUser: true,
        user: result.user
      };
      onSuccess && onSuccess(successResult);
      setLoading(false);
      return successResult;
    } catch (err) {
      setError(err);
      onError && onError(err);
      setLoading(false);
      return {
        success: false,
        error: err
      };
    }
  }, [onSuccess, onError]);

  /**
   * 登出
   */
  const logout = useCallback(async () => {
    try {
      await apiClient.logout();
      return {
        success: true
      };
    } catch (err) {
      return {
        success: false,
        error: err
      };
    }
  }, []);

  /**
   * 检查是否已登录
   */
  const isAuthenticated = useCallback(() => {
    return apiClient.isAuthenticated();
  }, []);

  /**
   * 获取当前用户信息
   */
  const getCurrentUser = useCallback(async () => {
    if (!apiClient.isAuthenticated()) {
      return null;
    }
    try {
      const res = await apiClient.getMe();
      return res.data || res;
    } catch (err) {
      return null;
    }
  }, []);
  return {
    loading,
    error,
    loginWithWallet,
    loginWithDID,
    register,
    logout,
    isAuthenticated,
    getCurrentUser
  };
}

/**
 * QuantaBit Auth SDK - Quick Connect Button
 * 
 * 一个简单的快速连接按钮，集成了完整的登录功能
 * 登录后显示用户中心下拉菜单
 */

function QuickConnectButton({
  onSuccess,
  onError,
  onLogout,
  onNavigate,
  onOpenFullscreen,
  user = null,
  apiBaseUrl = '/api/v1',
  appName = 'QuantaBit',
  appLogo,
  language: initialLanguage = 'zh',
  buttonText,
  variant = 'primary',
  shape = 'default',
  size = 'md',
  colorScheme = 'indigo',
  iconOnly = false,
  className = '',
  style = {},
  modalMode = 'compact',
  // compact | full
  onModeChange,
  theme = 'dark',
  onThemeChange,
  showToolbar = true,
  menuItems = null,
  defaultLoginType = 'did',
  // wallet | did
  triggerMode = 'modal',
  // modal | wallet
  chainType = 'quantabit'
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(theme);
  const [currentModalMode, setCurrentModalMode] = useState(modalMode);
  const dropdownRef = useRef(null);
  const handleSuccess = useCallback(result => {
    setIsModalOpen(false);
    onSuccess && onSuccess(result);
  }, [onSuccess]);
  const {
    loginWithWallet,
    loading: loginLoading
  } = useLogin({
    apiBaseUrl,
    onSuccess: handleSuccess,
    onError
  });

  // 语言状态 - 支持动态同步（自动归一化）
  const [currentLanguage, setCurrentLanguage] = useState(() => normalizeLanguage(initialLanguage));
  const prevLanguageRef = useRef(initialLanguage);

  // 同步外部 props.language 变化 - 无条件更新（归一化）
  useEffect(() => {
    if (initialLanguage !== prevLanguageRef.current) {
      prevLanguageRef.current = initialLanguage;
      setCurrentLanguage(normalizeLanguage(initialLanguage));
    }
  }, [initialLanguage]);

  // 订阅全局语言变更
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const LANGUAGE_CHANGE_EVENT = 'qbit-did:language-change';
    const handler = e => {
      const newLang = e.detail?.language;
      if (newLang) {
        const normalized = normalizeLanguage(newLang);
        prevLanguageRef.current = normalized;
        setCurrentLanguage(normalized);
      }
    };
    window.addEventListener(LANGUAGE_CHANGE_EVENT, handler);
    console.log('[QuickConnectButton] 已订阅语言变更事件');
    return () => {
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, handler);
    };
  }, []);

  // 同步外部 theme prop
  useEffect(() => {
    setCurrentTheme(theme);
  }, [theme]);

  // 同步外部 modalMode prop
  useEffect(() => {
    setCurrentModalMode(modalMode);
  }, [modalMode]);

  // 内部主题切换
  const handleThemeChange = useCallback(newTheme => {
    setCurrentTheme(newTheme);
    onThemeChange && onThemeChange(newTheme);
  }, [onThemeChange]);

  // 内部模式切换
  const handleModeChange = useCallback(newMode => {
    setCurrentModalMode(newMode);
    onModeChange && onModeChange(newMode);
  }, [onModeChange]);

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);
  const handleClick = async () => {
    if (triggerMode === 'wallet') {
      await loginWithWallet(chainType);
    } else {
      setIsModalOpen(true);
    }
  };
  const handleLogout = useCallback(() => {
    setIsDropdownOpen(false);
    onLogout && onLogout();
  }, [onLogout]);
  const handleNavigate = useCallback(path => {
    setIsDropdownOpen(false);
    onNavigate && onNavigate(path);
  }, [onNavigate]);

  // 自定义扩展菜单项（供宿主应用扩展使用）
  const extraMenuItems = menuItems || [];

  // 渲染 Modal 组件
  const renderModal = () => {
    // 使用 key 确保模式切换时组件重新创建，避免状态残留
    // compact 模式使用简易的 DarkLoginModal
    if (currentModalMode === 'compact') {
      return /*#__PURE__*/jsx(DarkLoginModal, {
        isOpen: isModalOpen,
        onClose: () => setIsModalOpen(false),
        onSuccess: handleSuccess,
        onError: onError,
        onLogout: handleLogout,
        onOpenFullscreen: onOpenFullscreen,
        onThemeChange: handleThemeChange,
        onModeChange: handleModeChange,
        theme: currentTheme,
        showToolbar: showToolbar,
        apiBaseUrl: apiBaseUrl,
        appName: appName,
        appLogo: appLogo,
        language: currentLanguage,
        showRegister: true,
        currentUser: user,
        defaultLoginType: defaultLoginType
      }, `dark-modal-${currentLanguage}`);
    }

    // full 模式使用双栏的 LoginModal
    return /*#__PURE__*/jsx(LoginModal, {
      isOpen: isModalOpen,
      onClose: () => setIsModalOpen(false),
      onSuccess: handleSuccess,
      onError: onError,
      onLogout: handleLogout,
      onModeChange: handleModeChange,
      apiBaseUrl: apiBaseUrl,
      appName: appName,
      appLogo: appLogo,
      language: currentLanguage,
      currentUser: user,
      showRegister: true
    }, `login-modal-${currentLanguage}`);
  };

  // 已登录状态 - 显示昵称优先的用户中心
  if (user) {
    // 使用统一的 getUserDisplayName：昵称优先，DID 仅作次要信息
    const displayName = getUserDisplayName(user);
    const avatarInitial = displayName ? displayName[0].toUpperCase() : 'U';
    return /*#__PURE__*/jsxs("div", {
      className: `did-user-center did-size-${size}`,
      ref: dropdownRef,
      children: [/*#__PURE__*/jsxs("button", {
        className: "did-user-trigger",
        onClick: () => setIsDropdownOpen(!isDropdownOpen),
        children: [/*#__PURE__*/jsx("div", {
          className: "did-user-avatar",
          children: user.avatar || user.avatar_url ? /*#__PURE__*/jsx("img", {
            src: user.avatar || user.avatar_url,
            alt: displayName
          }) : /*#__PURE__*/jsx("span", {
            className: "did-user-avatar-text",
            children: avatarInitial
          })
        }), /*#__PURE__*/jsx("span", {
          className: "did-user-name",
          children: displayName
        }), /*#__PURE__*/jsx(ChevronDownIcon, {
          size: 16,
          className: isDropdownOpen ? 'rotate' : ''
        })]
      }), isDropdownOpen && /*#__PURE__*/jsx("div", {
        className: "did-user-dropdown",
        children: /*#__PURE__*/jsx(DIDProfilePanel, {
          user: user,
          onNavigate: path => {
            setIsDropdownOpen(false);
            handleNavigate(path);
          },
          onLogout: handleLogout,
          onSwitchAccount: () => {
            setIsDropdownOpen(false);
            setIsModalOpen(true);
          },
          onClose: () => setIsDropdownOpen(false),
          apiBaseUrl: apiBaseUrl,
          language: currentLanguage,
          mode: "inline",
          showChainStatus: true,
          showQuickActions: true,
          extraMenuItems: extraMenuItems
        })
      }), renderModal()]
    });
  }

  // 未登录状态
  const defaultText = currentLanguage === 'zh' ? '连接身份' : 'Connect Identity';

  // 根据 size 获取图标大小
  const getIconSize = () => {
    switch (size) {
      case 'xs':
        return 14;
      case 'sm':
        return 16;
      case 'lg':
        return 24;
      case 'xl':
        return 28;
      default:
        return 20;
    }
  };

  // 构建按钮 class
  const buttonClasses = ['did-quick-connect-btn', `did-variant-${variant}`, `did-size-${size}`, `did-shape-${shape}`, `did-color-${colorScheme}`, iconOnly && 'did-icon-only', className].filter(Boolean).join(' ');
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx("button", {
      className: buttonClasses,
      style: style,
      onClick: handleClick,
      disabled: loginLoading,
      "aria-label": buttonText || defaultText,
      children: loginLoading ? /*#__PURE__*/jsxs(Fragment, {
        children: [/*#__PURE__*/jsx(LoaderIcon, {
          className: "spin",
          size: getIconSize()
        }), !iconOnly && /*#__PURE__*/jsx("span", {
          children: "\u7531\u4E8E\u8FDE\u63A5\u4E2D..."
        })]
      }) : iconOnly || shape === 'circle' ? /*#__PURE__*/jsx(WalletIcon, {
        size: getIconSize()
      }) : variant === 'minimal' ? /*#__PURE__*/jsx(WalletIcon, {
        size: getIconSize()
      }) : /*#__PURE__*/jsxs(Fragment, {
        children: [/*#__PURE__*/jsx(ShieldIcon, {
          size: getIconSize()
        }), /*#__PURE__*/jsx("span", {
          children: buttonText || defaultText
        })]
      })
    }), renderModal()]
  });
}

function DidWalletConnectButton({
  buttonText = '使用 DID 登录',
  className = '',
  // 预设默认值，但允许覆盖
  variant = 'gradient',
  shape = 'pill',
  size = 'md',
  colorScheme = 'indigo',
  modalMode = 'full',
  theme = 'dark',
  showToolbar = true,
  ...props
}) {
  return /*#__PURE__*/jsx(QuickConnectButton, {
    variant: variant,
    shape: shape,
    size: size,
    colorScheme: colorScheme,
    modalMode: modalMode,
    theme: theme,
    showToolbar: showToolbar,
    buttonText: buttonText,
    className: `did-wallet-connect-btn ${className}`,
    ...props
  });
}

/**
 * QuantaBit Auth SDK - AccountSwitcher 组件
 * 
 * 多账户快速切换组件，提供：
 * - 当前账户显示
 * - 快速切换下拉
 * - 添加新账户
 * - 账户管理入口
 */

function AccountSwitcher({
  currentUser,
  onSwitch,
  onAddNew,
  onLogout,
  onManage,
  language = 'zh',
  position = 'bottom-right',
  compact = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const dropdownRef = useRef(null);
  const messages = {
    zh: {
      switchAccount: '切换账户',
      addAccount: '添加账户',
      manageAccounts: '管理账户',
      logout: '退出登录',
      noOtherAccounts: '没有其他账户',
      verified: '已验证',
      current: '当前'
    },
    en: {
      switchAccount: 'Switch Account',
      addAccount: 'Add Account',
      manageAccounts: 'Manage Accounts',
      logout: 'Logout',
      noOtherAccounts: 'No other accounts',
      verified: 'Verified',
      current: 'Current'
    }
  };
  const msg = messages[language] || messages.zh;

  // 加载账户列表
  useEffect(() => {
    setAccounts(getSavedAccounts());
  }, [isOpen]);

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const otherAccounts = accounts.filter(a => a.did !== currentUser?.did);
  const handleSwitch = account => {
    setIsOpen(false);
    onSwitch?.(account);
  };
  const handleAddNew = () => {
    setIsOpen(false);
    onAddNew?.();
  };
  const handleLogout = () => {
    setIsOpen(false);
    clearCurrentSession();
    onLogout?.();
  };
  const handleManage = () => {
    setIsOpen(false);
    onManage?.();
  };
  const handleRemove = (did, e) => {
    e.stopPropagation();
    removeAccount(did);
    setAccounts(getSavedAccounts());
  };
  if (!currentUser) {
    return null;
  }
  return /*#__PURE__*/jsxs("div", {
    className: "did-account-switcher",
    ref: dropdownRef,
    children: [/*#__PURE__*/jsxs("button", {
      className: `did-switcher-trigger ${compact ? 'compact' : ''}`,
      onClick: () => setIsOpen(!isOpen),
      children: [/*#__PURE__*/jsx("div", {
        className: "did-switcher-avatar",
        children: currentUser.avatar ? /*#__PURE__*/jsx("img", {
          src: currentUser.avatar,
          alt: ""
        }) : /*#__PURE__*/jsx("span", {
          className: "did-avatar-placeholder",
          children: (currentUser.nickname || currentUser.displayName || 'U')[0].toUpperCase()
        })
      }), !compact && /*#__PURE__*/jsxs("div", {
        className: "did-switcher-info",
        children: [/*#__PURE__*/jsx("span", {
          className: "did-switcher-name",
          children: currentUser.nickname || currentUser.displayName || formatDID$1(currentUser.did)
        }), /*#__PURE__*/jsx("span", {
          className: "did-switcher-did",
          children: formatDID$1(currentUser.did)
        })]
      }), /*#__PURE__*/jsx(SwapIcon, {})]
    }), isOpen && /*#__PURE__*/jsxs("div", {
      className: `did-switcher-dropdown ${position}`,
      children: [/*#__PURE__*/jsxs("div", {
        className: "did-dropdown-current",
        children: [/*#__PURE__*/jsx("div", {
          className: "did-dropdown-avatar",
          children: currentUser.avatar ? /*#__PURE__*/jsx("img", {
            src: currentUser.avatar,
            alt: ""
          }) : /*#__PURE__*/jsx("span", {
            className: "did-avatar-placeholder",
            children: (currentUser.nickname || currentUser.displayName || 'U')[0].toUpperCase()
          })
        }), /*#__PURE__*/jsxs("div", {
          className: "did-dropdown-info",
          children: [/*#__PURE__*/jsxs("span", {
            className: "did-dropdown-name",
            children: [currentUser.nickname || currentUser.displayName || formatDID$1(currentUser.did), /*#__PURE__*/jsx(VerifiedIcon, {})]
          }), /*#__PURE__*/jsx("span", {
            className: "did-dropdown-did",
            children: formatDID$1(currentUser.did)
          })]
        }), /*#__PURE__*/jsx("span", {
          className: "did-current-badge",
          children: msg.current
        })]
      }), /*#__PURE__*/jsx("div", {
        className: "did-dropdown-divider"
      }), otherAccounts.length > 0 ? /*#__PURE__*/jsx("div", {
        className: "did-dropdown-accounts",
        children: otherAccounts.map(account => /*#__PURE__*/jsxs("div", {
          className: "did-dropdown-account",
          onClick: () => handleSwitch(account),
          children: [/*#__PURE__*/jsx("div", {
            className: "did-dropdown-avatar small",
            children: account.avatar ? /*#__PURE__*/jsx("img", {
              src: account.avatar,
              alt: ""
            }) : /*#__PURE__*/jsx("span", {
              className: "did-avatar-placeholder",
              children: (account.displayName || 'U')[0].toUpperCase()
            })
          }), /*#__PURE__*/jsxs("div", {
            className: "did-dropdown-info",
            children: [/*#__PURE__*/jsxs("span", {
              className: "did-dropdown-name",
              children: [account.displayName || formatDID$1(account.did), account.verified && /*#__PURE__*/jsx(VerifiedIcon, {})]
            }), /*#__PURE__*/jsx("span", {
              className: "did-dropdown-did",
              children: formatDID$1(account.did)
            })]
          }), /*#__PURE__*/jsx("button", {
            className: "did-dropdown-remove",
            onClick: e => handleRemove(account.did, e),
            title: "Remove",
            children: /*#__PURE__*/jsx(TrashIcon, {})
          })]
        }, account.did))
      }) : /*#__PURE__*/jsx("div", {
        className: "did-dropdown-empty",
        children: msg.noOtherAccounts
      }), /*#__PURE__*/jsx("div", {
        className: "did-dropdown-divider"
      }), /*#__PURE__*/jsxs("div", {
        className: "did-dropdown-actions",
        children: [/*#__PURE__*/jsxs("button", {
          className: "did-dropdown-action",
          onClick: handleAddNew,
          children: [/*#__PURE__*/jsx(PlusIcon, {}), /*#__PURE__*/jsx("span", {
            children: msg.addAccount
          })]
        }), onManage && /*#__PURE__*/jsxs("button", {
          className: "did-dropdown-action",
          onClick: handleManage,
          children: [/*#__PURE__*/jsx(SwapIcon, {}), /*#__PURE__*/jsx("span", {
            children: msg.manageAccounts
          })]
        }), /*#__PURE__*/jsxs("button", {
          className: "did-dropdown-action danger",
          onClick: handleLogout,
          children: [/*#__PURE__*/jsx(LogoutIcon, {}), /*#__PURE__*/jsx("span", {
            children: msg.logout
          })]
        })]
      })]
    }), /*#__PURE__*/jsx("style", {
      children: `
        .did-account-switcher {
          position: relative;
          display: inline-block;
        }

        .did-switcher-trigger {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 12px;
          background: linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.1));
          border: 1px solid rgba(99, 102, 241, 0.2);
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .did-switcher-trigger:hover {
          background: linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(139, 92, 246, 0.15));
          border-color: rgba(99, 102, 241, 0.3);
        }

        .did-switcher-trigger.compact {
          padding: 6px;
          gap: 6px;
        }

        .did-switcher-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          overflow: hidden;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
        }

        .did-switcher-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .did-avatar-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 14px;
        }

        .did-switcher-info {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 2px;
        }

        .did-switcher-name {
          font-size: 14px;
          font-weight: 500;
          color: #333;
        }

        .did-switcher-did {
          font-size: 11px;
          color: #888;
          font-family: monospace;
        }

        .did-switcher-dropdown {
          position: absolute;
          top: 100%;
          width: 280px;
          margin-top: 8px;
          background: white;
          border-radius: 14px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
          border: 1px solid rgba(0, 0, 0, 0.08);
          z-index: 1000;
          overflow: hidden;
          animation: slideDown 0.2s ease-out;
        }

        .did-switcher-dropdown.bottom-right {
          right: 0;
        }

        .did-switcher-dropdown.bottom-left {
          left: 0;
        }

        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .did-dropdown-current {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px;
          background: linear-gradient(135deg, rgba(99, 102, 241, 0.05), rgba(139, 92, 246, 0.05));
        }

        .did-dropdown-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          overflow: hidden;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
        }

        .did-dropdown-avatar.small {
          width: 32px;
          height: 32px;
        }

        .did-dropdown-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .did-dropdown-info {
          flex: 1;
          min-width: 0;
        }

        .did-dropdown-name {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 14px;
          font-weight: 500;
          color: #333;
        }

        .did-dropdown-name svg {
          color: #22c55e;
          width: 14px;
          height: 14px;
        }

        .did-dropdown-did {
          font-size: 11px;
          color: #888;
          font-family: monospace;
        }

        .did-current-badge {
          padding: 3px 8px;
          background: #dcfce7;
          color: #16a34a;
          font-size: 11px;
          font-weight: 500;
          border-radius: 10px;
        }

        .did-dropdown-divider {
          height: 1px;
          background: #f0f0f0;
          margin: 0;
        }

        .did-dropdown-accounts {
          max-height: 200px;
          overflow-y: auto;
        }

        .did-dropdown-account {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          cursor: pointer;
          transition: background 0.15s;
        }

        .did-dropdown-account:hover {
          background: rgba(99, 102, 241, 0.05);
        }

        .did-dropdown-remove {
          opacity: 0;
          padding: 4px;
          background: none;
          border: none;
          cursor: pointer;
          color: #dc2626;
          transition: opacity 0.15s;
        }

        .did-dropdown-account:hover .did-dropdown-remove {
          opacity: 1;
        }

        .did-dropdown-empty {
          padding: 16px;
          text-align: center;
          color: #888;
          font-size: 13px;
        }

        .did-dropdown-actions {
          padding: 8px;
        }

        .did-dropdown-action {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 10px 12px;
          background: none;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 13px;
          color: #555;
          transition: all 0.15s;
        }

        .did-dropdown-action:hover {
          background: #f5f5f5;
        }

        .did-dropdown-action.danger {
          color: #dc2626;
        }

        .did-dropdown-action.danger:hover {
          background: #fef2f2;
        }
      `
    })]
  });
}

/**
 * QuantaBit Auth Context
 * 
 * 提供认证状态管理的 React Context
 */

const AuthContext = /*#__PURE__*/createContext(null);

/**
 * Auth Provider 组件
 * 
 * @param {Object} props
 * @param {string} props.apiBaseUrl - API 基础 URL
 * @param {Function} props.onLoginSuccess - 登录成功回调
 * @param {Function} props.onLogout - 登出回调
 * @param {React.ReactNode} props.children
 */
function AuthProvider({
  children,
  apiBaseUrl,
  onLoginSuccess,
  onLogout: onLogoutCallback
}) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 配置 API
  useEffect(() => {
    if (apiBaseUrl) {
      authApi.configure({
        apiBaseUrl
      });
    }
  }, [apiBaseUrl]);

  // 初始化时检查认证状态
  useEffect(() => {
    checkAuth();
  }, []);

  /**
   * 检查认证状态
   */
  const checkAuth = useCallback(async () => {
    if (!authApi.isAuthenticated()) {
      setLoading(false);
      return;
    }
    try {
      const response = await authApi.getMe();
      const userData = response.data || response;
      setUser(userData);
      setError(null);
    } catch (err) {
      console.error('[AuthProvider] Auth check failed:', err);
      setUser(null);
      if (err.message?.includes('401') || err.message?.includes('Unauthorized')) {
        authApi.clearTokens();
      }
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 登录处理
   */
  const login = useCallback(async (accessToken, refreshToken, userData = null) => {
    authApi.setTokens(accessToken, refreshToken);
    if (userData) {
      setUser(userData);
    } else {
      await checkAuth();
    }
    onLoginSuccess && onLoginSuccess({
      accessToken,
      refreshToken,
      user: userData || user
    });
  }, [checkAuth, onLoginSuccess, user]);

  /**
   * 登出处理
   */
  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (e) {
      // Ignore errors
    }
    setUser(null);
    onLogoutCallback && onLogoutCallback();
  }, [onLogoutCallback]);

  /**
   * 更新用户信息
   */
  const updateUser = useCallback(updates => {
    setUser(prev => prev ? {
      ...prev,
      ...updates
    } : null);
  }, []);
  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    logout,
    checkAuth,
    updateUser
  };
  return /*#__PURE__*/jsx(AuthContext.Provider, {
    value: value,
    children: children
  });
}

/**
 * useAuth Hook
 */
function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * QuantaBit Auth SDK - useAccounts Hook
 * 
 * 多账户管理 Hook，提供：
 * - 账户列表管理
 * - 账户切换
 * - 快速登录
 * - 账户移除
 * - 当前会话管理
 */

const MAX_ACCOUNTS = 5;

/**
 * useAccounts Hook
 * 
 * @param {Object} options
 * @param {string} options.apiBaseUrl - API 基础 URL
 * @param {Function} options.onSwitchSuccess - 切换成功回调
 * @param {Function} options.onSwitchError - 切换错误回调
 * @param {Function} options.onLogout - 登出回调
 * @returns {Object} 账户管理方法和状态
 */
function useAccounts({
  apiBaseUrl,
  onSwitchSuccess,
  onSwitchError,
  onLogout
} = {}) {
  const [accounts, setAccounts] = useState([]);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 配置 API
  useEffect(() => {
    if (apiBaseUrl) {
      apiClient.configure({
        apiBaseUrl
      });
    }
  }, [apiBaseUrl]);

  // 初始化加载账户
  useEffect(() => {
    loadAccounts();
    loadCurrentSession();
  }, []);

  /**
   * 加载保存的账户列表
   */
  const loadAccounts = useCallback(() => {
    const savedAccounts = getSavedAccounts();
    setAccounts(savedAccounts);
    return savedAccounts;
  }, []);

  /**
   * 加载当前会话
   */
  const loadCurrentSession = useCallback(() => {
    const session = getCurrentSession();
    if (session) {
      const account = getSavedAccounts().find(a => a.did === session.did);
      setCurrentAccount(account || {
        did: session.did
      });
    } else {
      setCurrentAccount(null);
    }
    return session;
  }, []);

  /**
   * 添加账户
   */
  const addAccount = useCallback(account => {
    saveAccount({
      ...account,
      addedAt: new Date().toISOString()
    });
    loadAccounts();
  }, [loadAccounts]);

  /**
   * 删除账户
   */
  const deleteAccount = useCallback(did => {
    removeAccount(did);
    loadAccounts();

    // 如果删除的是当前账户，清除会话
    if (currentAccount?.did === did) {
      clearCurrentSession();
      setCurrentAccount(null);
    }
  }, [currentAccount, loadAccounts]);

  /**
   * 切换账户 - 需要重新登录
   */
  const switchAccount = useCallback(async (did, password) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.didLogin(did, password);
      const data = result.data || result;

      // 保存新 session
      apiClient.setTokens(data.access_token, data.refresh_token);
      setCurrentDID(data.did);

      // 更新账户信息
      saveAccount({
        did: data.did,
        displayName: data.display_name || data.user?.nickname,
        avatar: data.avatar || data.user?.avatar,
        verified: true,
        lastLoginAt: new Date().toISOString()
      });
      loadAccounts();
      loadCurrentSession();
      const successResult = {
        success: true,
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        did: data.did,
        user: data.user
      };
      onSwitchSuccess?.(successResult);
      setLoading(false);
      return successResult;
    } catch (err) {
      setError(err);
      onSwitchError?.(err);
      setLoading(false);
      return {
        success: false,
        error: err
      };
    }
  }, [loadAccounts, loadCurrentSession, onSwitchSuccess, onSwitchError]);

  /**
   * 快速切换账户 - 使用保存的 refresh token
   */
  const quickSwitch = useCallback(async did => {
    const account = accounts.find(a => a.did === did);
    if (!account) {
      const err = new Error('Account not found');
      setError(err);
      return {
        success: false,
        error: err
      };
    }

    // 如果账户有保存的 refresh token，尝试使用它
    if (account.refreshToken) {
      setLoading(true);
      setError(null);
      try {
        const result = await apiClient.refreshToken(account.refreshToken);
        const data = result.data || result;
        apiClient.setTokens(data.access_token, data.refresh_token);
        setCurrentDID(account.did);

        // 更新 refresh token
        saveAccount({
          ...account,
          refreshToken: data.refresh_token,
          lastLoginAt: new Date().toISOString()
        });
        loadAccounts();
        loadCurrentSession();
        const successResult = {
          success: true,
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
          did: account.did
        };
        onSwitchSuccess?.(successResult);
        setLoading(false);
        return successResult;
      } catch (err) {
        // Refresh token 过期，需要重新登录
        setError(err);
        setLoading(false);
        return {
          success: false,
          error: err,
          requiresPassword: true
        };
      }
    }

    // 没有 refresh token，需要密码登录
    return {
      success: false,
      requiresPassword: true
    };
  }, [accounts, loadAccounts, loadCurrentSession, onSwitchSuccess]);

  /**
   * 登出当前账户
   */
  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await apiClient.logout();
    } catch {
      // Ignore logout errors
    }
    clearCurrentSession();
    setCurrentAccount(null);
    setLoading(false);
    onLogout?.();
  }, [onLogout]);

  /**
   * 登出所有账户
   */
  const logoutAll = useCallback(async () => {
    setLoading(true);
    try {
      await apiClient.logout();
    } catch {
      // Ignore
    }
    clearCurrentSession();
    setCurrentAccount(null);

    // 清除所有保存的 refresh tokens
    const cleanedAccounts = accounts.map(a => ({
      ...a,
      refreshToken: undefined
    }));
    cleanedAccounts.forEach(a => saveAccount(a));
    loadAccounts();
    setLoading(false);
    onLogout?.();
  }, [accounts, loadAccounts, onLogout]);

  /**
   * 获取其他账户（排除当前账户）
   */
  const getOtherAccounts = useCallback(() => {
    if (!currentAccount) return accounts;
    return accounts.filter(a => a.did !== currentAccount.did);
  }, [accounts, currentAccount]);

  /**
   * 设置账户显示名称
   */
  const setAccountDisplayName = useCallback((did, displayName) => {
    const account = accounts.find(a => a.did === did);
    if (account) {
      saveAccount({
        ...account,
        displayName
      });
      loadAccounts();
    }
  }, [accounts, loadAccounts]);

  /**
   * 设置账户头像
   */
  const setAccountAvatar = useCallback((did, avatar) => {
    const account = accounts.find(a => a.did === did);
    if (account) {
      saveAccount({
        ...account,
        avatar
      });
      loadAccounts();
    }
  }, [accounts, loadAccounts]);

  /**
   * 检查账户是否是当前登录账户
   */
  const isCurrentAccount = useCallback(did => {
    return currentAccount?.did === did;
  }, [currentAccount]);

  /**
   * 获取账户数量
   */
  const getAccountCount = useCallback(() => {
    return accounts.length;
  }, [accounts]);

  /**
   * 检查是否可以添加更多账户
   */
  const canAddMore = useCallback(() => {
    return accounts.length < MAX_ACCOUNTS;
  }, [accounts]);
  return {
    // 状态
    accounts,
    currentAccount,
    loading,
    error,
    // 账户操作
    loadAccounts,
    addAccount,
    deleteAccount,
    // 切换操作
    switchAccount,
    quickSwitch,
    // 登出操作
    logout,
    logoutAll,
    // 辅助方法
    getOtherAccounts,
    setAccountDisplayName,
    setAccountAvatar,
    isCurrentAccount,
    getAccountCount,
    canAddMore,
    // 常量
    maxAccounts: MAX_ACCOUNTS
  };
}

/**
 * QuantaBit Auth SDK - useProfile Hook
 * 
 * 用户资料管理 Hook：
 * - 获取用户资料
 * - 更新资料
 * - 隐私设置
 * - 头像上传
 */


/**
 * useProfile Hook
 * 
 * @param {Object} options
 * @param {string} options.apiBaseUrl - API 基础 URL
 * @param {boolean} options.autoLoad - 是否自动加载（默认 true）
 * @returns {Object} 用户资料管理方法和状态
 */
function useProfile({
  apiBaseUrl,
  autoLoad = true
} = {}) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 配置 API
  useEffect(() => {
    if (apiBaseUrl) {
      apiClient.configure({
        apiBaseUrl
      });
    }
  }, [apiBaseUrl]);

  /**
   * 加载用户资料
   */
  const fetchProfile = useCallback(async () => {
    if (!apiClient.isAuthenticated()) {
      return null;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.getProfile();
      const data = result.data || result;
      setProfile(data);
      return data;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 更新用户资料
   */
  const updateProfile = useCallback(async updates => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.updateProfile(updates);
      const data = result.data || result;
      setProfile(prev => ({
        ...prev,
        ...data
      }));
      return {
        success: true,
        data
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 获取公开资料
   */
  const getPublicProfile = useCallback(async did => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.getPublicProfile(did);
      return result.data || result;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 更新隐私设置
   */
  const updatePrivacySettings = useCallback(async settings => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.updatePrivacySettings(settings);
      const data = result.data || result;
      setProfile(prev => ({
        ...prev,
        privacySettings: data.privacy_settings || data
      }));
      return {
        success: true,
        data
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 上传头像
   */
  const uploadAvatar = useCallback(async file => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.uploadAvatar(file);
      const data = result.data || result;
      setProfile(prev => ({
        ...prev,
        avatarUrl: data.avatar_url || data.avatarUrl
      }));
      return {
        success: true,
        avatarUrl: data.avatar_url || data.avatarUrl
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // 自动加载
  useEffect(() => {
    if (autoLoad && apiClient.isAuthenticated()) {
      fetchProfile();
    }
  }, [autoLoad, fetchProfile]);
  return {
    profile,
    loading,
    error,
    fetchProfile,
    updateProfile,
    getPublicProfile,
    updatePrivacySettings,
    uploadAvatar,
    // 便捷属性
    displayName: profile?.displayName || profile?.display_name,
    avatar: profile?.avatarUrl || profile?.avatar_url,
    email: profile?.email,
    emailVerified: profile?.emailVerified || profile?.email_verified,
    did: profile?.did
  };
}

/**
 * QuantaBit Auth SDK - useReputation Hook
 * 
 * 声誉系统 Hook：
 * - 获取声誉分数
 * - 获取徽章
 * - 查看排行榜
 * - 获取历史记录
 */


/**
 * useReputation Hook
 * 
 * @param {Object} options
 * @param {string} options.apiBaseUrl - API 基础 URL
 * @param {boolean} options.autoLoad - 是否自动加载（默认 true）
 * @returns {Object} 声誉管理方法和状态
 */
function useReputation({
  apiBaseUrl,
  autoLoad = true
} = {}) {
  const [reputation, setReputation] = useState(null);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 配置 API
  useEffect(() => {
    if (apiBaseUrl) {
      apiClient.configure({
        apiBaseUrl
      });
    }
  }, [apiBaseUrl]);

  /**
   * 获取当前用户声誉
   */
  const fetchReputation = useCallback(async () => {
    if (!apiClient.isAuthenticated()) {
      return null;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.getMyReputation();
      const data = result.data || result;
      setReputation(data);
      return data;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 获取用户声誉（其他用户）
   */
  const getReputation = useCallback(async did => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.getReputation(did);
      return result.data || result;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 获取当前用户徽章
   */
  const fetchBadges = useCallback(async () => {
    if (!apiClient.isAuthenticated()) {
      return [];
    }
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.getMyBadges();
      const data = result.data?.badges || result.badges || result;
      setBadges(Array.isArray(data) ? data : []);
      return data;
    } catch (err) {
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 获取排行榜
   */
  const getLeaderboard = useCallback(async (dimension = 'overall', limit = 20) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.getLeaderboard(dimension, limit);
      return result.data || result;
    } catch (err) {
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 获取声誉历史
   */
  const getHistory = useCallback(async (startDate, endDate) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.getReputationHistory(startDate, endDate);
      return result.data || result;
    } catch (err) {
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 刷新所有数据
   */
  const refresh = useCallback(async () => {
    await Promise.all([fetchReputation(), fetchBadges()]);
  }, [fetchReputation, fetchBadges]);

  // 自动加载
  useEffect(() => {
    if (autoLoad && apiClient.isAuthenticated()) {
      refresh();
    }
  }, [autoLoad, refresh]);
  return {
    reputation,
    badges,
    loading,
    error,
    fetchReputation,
    getReputation,
    fetchBadges,
    getLeaderboard,
    getHistory,
    refresh,
    // 便捷属性
    score: reputation?.overall_score || reputation?.overallScore || 0,
    level: reputation?.level || 'newcomer',
    rank: reputation?.rank,
    badgeCount: badges.length,
    // 分项分数
    scores: {
      governance: reputation?.governance_score || reputation?.governanceScore || 0,
      contribution: reputation?.contribution_score || reputation?.contributionScore || 0,
      verification: reputation?.verification_score || reputation?.verificationScore || 0,
      social: reputation?.social_score || reputation?.socialScore || 0
    }
  };
}

/**
 * QuantaBit Auth SDK - useCredentials Hook
 * 
 * 凭证管理 Hook：
 * - 获取凭证列表
 * - 获取凭证详情
 * - 创建可验证展示
 * - 验证展示
 * - 请求凭证
 */


/**
 * useCredentials Hook
 * 
 * @param {Object} options
 * @param {string} options.apiBaseUrl - API 基础 URL
 * @param {boolean} options.autoLoad - 是否自动加载（默认 true）
 * @returns {Object} 凭证管理方法和状态
 */
function useCredentials({
  apiBaseUrl,
  autoLoad = true
} = {}) {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 配置 API
  useEffect(() => {
    if (apiBaseUrl) {
      apiClient.configure({
        apiBaseUrl
      });
    }
  }, [apiBaseUrl]);

  /**
   * 获取凭证列表
   */
  const fetchCredentials = useCallback(async type => {
    if (!apiClient.isAuthenticated()) {
      return [];
    }
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.getCredentials(type);
      const data = result.data?.credentials || result.credentials || result;
      setCredentials(Array.isArray(data) ? data : []);
      return data;
    } catch (err) {
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 获取单个凭证详情
   */
  const getCredential = useCallback(async credentialId => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.getCredential(credentialId);
      return result.data || result;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 创建可验证展示
   */
  const createPresentation = useCallback(async (credentialIds, options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.createPresentation(credentialIds, options.disclosedFields, options.challenge, options.domain);
      return {
        success: true,
        data: result.data || result
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 验证可验证展示
   */
  const verifyPresentation = useCallback(async presentation => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.verifyPresentation(presentation);
      return {
        success: true,
        data: result.data || result
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 请求凭证
   */
  const requestCredential = useCallback(async (issuer, type, claims) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.requestCredential(issuer, type, claims);
      await fetchCredentials(); // 刷新列表
      return {
        success: true,
        data: result.data || result
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err
      };
    } finally {
      setLoading(false);
    }
  }, [fetchCredentials]);

  /**
   * 撤销凭证
   */
  const revokeCredential = useCallback(async (credentialId, reason) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.revokeCredential(credentialId, reason);
      await fetchCredentials(); // 刷新列表
      return {
        success: true,
        data: result.data || result
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err
      };
    } finally {
      setLoading(false);
    }
  }, [fetchCredentials]);

  /**
   * 按类型筛选凭证
   */
  const getByType = useCallback(type => {
    return credentials.filter(c => c.type === type);
  }, [credentials]);

  /**
   * 获取有效凭证
   */
  const getValidCredentials = useCallback(() => {
    return credentials.filter(c => c.status === 'valid');
  }, [credentials]);

  /**
   * 获取过期凭证
   */
  const getExpiredCredentials = useCallback(() => {
    return credentials.filter(c => c.status === 'expired');
  }, [credentials]);

  // 自动加载
  useEffect(() => {
    if (autoLoad && apiClient.isAuthenticated()) {
      fetchCredentials();
    }
  }, [autoLoad, fetchCredentials]);
  return {
    credentials,
    loading,
    error,
    fetchCredentials,
    getCredential,
    createPresentation,
    verifyPresentation,
    requestCredential,
    revokeCredential,
    // 辅助方法
    getByType,
    getValidCredentials,
    getExpiredCredentials,
    // 便捷属性
    count: credentials.length,
    validCount: credentials.filter(c => c.status === 'valid').length
  };
}

/**
 * QuantaBit Auth SDK - useWallets Hook
 * 
 * 钱包绑定管理 Hook：
 * - 获取钱包列表
 * - 绑定新钱包
 * - 解绑钱包
 * - 设置主钱包
 */


/**
 * useWallets Hook
 * 
 * @param {Object} options
 * @param {string} options.apiBaseUrl - API 基础 URL
 * @param {boolean} options.autoLoad - 是否自动加载（默认 true）
 * @returns {Object} 钱包管理方法和状态
 */
function useWallets({
  apiBaseUrl,
  autoLoad = true
} = {}) {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 配置 API
  useEffect(() => {
    if (apiBaseUrl) {
      apiClient.configure({
        apiBaseUrl
      });
    }
  }, [apiBaseUrl]);

  /**
   * 获取钱包列表
   */
  const fetchWallets = useCallback(async () => {
    if (!apiClient.isAuthenticated()) {
      return [];
    }
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.getWalletBindings();
      const data = result.data?.bindings || result.bindings || result;
      setWallets(Array.isArray(data) ? data : []);
      return data;
    } catch (err) {
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 绑定新钱包（手动签名）
   */
  const bindWallet = useCallback(async (walletAddress, chainType, signature, message, bindingType = 'secondary') => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.bindWallet(walletAddress, chainType, signature, message, bindingType);
      await fetchWallets(); // 刷新列表
      return {
        success: true,
        data: result.data || result
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err
      };
    } finally {
      setLoading(false);
    }
  }, [fetchWallets]);

  /**
   * 一键绑定当前钱包
   */
  const bindCurrentWallet = useCallback(async (chainType = 'quantabit', bindingType = 'secondary') => {
    setLoading(true);
    setError(null);
    try {
      const {
        address: walletAddress,
        response: result
      } = await bindBrowserWallet(apiClient, chainType, bindingType);
      await fetchWallets();
      return {
        success: true,
        data: result,
        walletAddress
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err
      };
    } finally {
      setLoading(false);
    }
  }, [fetchWallets]);

  /**
   * 解绑钱包
   */
  const unbindWallet = useCallback(async bindingId => {
    setLoading(true);
    setError(null);
    try {
      await apiClient.unbindWallet(bindingId);
      await fetchWallets();
      return {
        success: true
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err
      };
    } finally {
      setLoading(false);
    }
  }, [fetchWallets]);

  /**
   * 设置主钱包
   */
  const setPrimaryWallet = useCallback(async bindingId => {
    setLoading(true);
    setError(null);
    try {
      await apiClient.setPrimaryWallet(bindingId);
      await fetchWallets();
      return {
        success: true
      };
    } catch (err) {
      setError(err);
      return {
        success: false,
        error: err
      };
    } finally {
      setLoading(false);
    }
  }, [fetchWallets]);

  /**
   * 获取主钱包
   */
  const getPrimaryWallet = useCallback(() => {
    return wallets.find(w => w.isPrimary || w.is_primary);
  }, [wallets]);

  /**
   * 按链类型筛选
   */
  const getByChainType = useCallback(chainType => {
    return wallets.filter(w => w.chainType === chainType || w.chain_type === chainType);
  }, [wallets]);

  /**
   * 格式化钱包地址
   */
  const formatWalletAddress = useCallback(address => {
    return formatAddress(address);
  }, []);

  // 自动加载
  useEffect(() => {
    if (autoLoad && apiClient.isAuthenticated()) {
      fetchWallets();
    }
  }, [autoLoad, fetchWallets]);
  return {
    wallets,
    loading,
    error,
    fetchWallets,
    bindWallet,
    bindCurrentWallet,
    unbindWallet,
    setPrimaryWallet,
    // 辅助方法
    getPrimaryWallet,
    getByChainType,
    formatWalletAddress,
    // 便捷属性
    count: wallets.length,
    primaryWallet: wallets.find(w => w.isPrimary || w.is_primary),
    hasMetaMask: hasMetaMask(),
    hasQBitWallet: hasQBitWallet()
  };
}

/**
 * QuantaBit Auth SDK - useQBitIdentity
 *
 * 统一身份 Hook — 生态接入的单一入口
 *
 * 将 useDID / useProfile / useReputation / useCredentials / useWallets
 * 统一聚合，外部应用只需引入这一个 Hook 即可获得完整身份能力。
 *
 * 架构特性：
 * - 单例 API Client 共享（避免重复初始化）
 * - 懒加载各子能力（按需激活，节省请求）
 * - 统一 loading / error 聚合
 * - 事件总线：emits qbit-did:user-change 等生态事件
 * - 扩展钩子：onReady / onIdentityChange / onChainStatus
 */


// ─── 生态事件 ────────────────────────────────────────────────
const EVENTS = {
  USER_CHANGE: 'qbit-did:user-change',
  IDENTITY_READY: 'qbit-did:identity-ready',
  CHAIN_STATUS: 'qbit-did:chain-status',
  LOGOUT: 'qbit-did:logout',
  LANG_CHANGE: 'qbit-did:language-change'
};

/**
 * 向生态广播事件（全局 window）
 */
function emit(eventName, detail = {}) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(eventName, {
    detail
  }));
}

/**
 * useQBitIdentity — 统一身份 Hook
 *
 * @param {Object} options
 * @param {string}   options.apiBaseUrl         API 基础 URL（默认 '/api/v1'）
 * @param {Object}   options.user               外部传入的已登录用户对象（可选）
 * @param {boolean}  options.autoLoad            是否自动加载身份数据（默认 true）
 * @param {boolean}  options.loadProfile         是否加载资料（默认 true）
 * @param {boolean}  options.loadReputation      是否加载声誉（默认 false，按需）
 * @param {boolean}  options.loadCredentials     是否加载凭证（默认 false，按需）
 * @param {boolean}  options.loadWallets         是否加载钱包（默认 false，按需）
 * @param {Function} options.onReady             身份就绪回调 (identity) => void
 * @param {Function} options.onIdentityChange    身份变更回调 (identity) => void
 * @param {Function} options.onChainStatusChange 链上状态变更回调 (status) => void
 * @param {Function} options.onError             错误回调 (err) => void
 *
 * @returns {Object} 聚合身份对象
 */
function useQBitIdentity({
  apiBaseUrl = '/api/v1',
  user: externalUser = null,
  autoLoad = true,
  loadProfile = true,
  loadReputation = false,
  loadCredentials = false,
  loadWallets = false,
  onReady,
  onIdentityChange,
  onChainStatusChange,
  onError
} = {}) {
  // ─── 内部状态 ─────────────────────────────────────────────
  const [isReady, setIsReady] = useState(false);
  const [loadingState, setLoadingState] = useState({
    identity: false,
    profile: false,
    reputation: false,
    credentials: false,
    wallets: false
  });
  const prevChainStatusRef = useRef(null);
  const isAuthenticated = externalUser != null || apiClient.isAuthenticated();

  // ─── 配置 API ─────────────────────────────────────────────
  useEffect(() => {
    if (apiBaseUrl) apiClient.configure({
      apiBaseUrl
    });
  }, [apiBaseUrl]);

  // ─── 子 Hooks（懒加载受控） ───────────────────────────────
  const didHook = useDID({
    apiBaseUrl,
    autoLoad: autoLoad && isAuthenticated
  });
  const profileHook = useProfile({
    apiBaseUrl,
    autoLoad: autoLoad && loadProfile && isAuthenticated
  });
  const reputationHook = useReputation({
    apiBaseUrl,
    autoLoad: autoLoad && loadReputation && isAuthenticated
  });
  const credentialsHook = useCredentials({
    apiBaseUrl,
    autoLoad: autoLoad && loadCredentials && isAuthenticated
  });
  const walletsHook = useWallets({
    apiBaseUrl,
    autoLoad: autoLoad && loadWallets && isAuthenticated
  });

  // ─── 聚合用户对象（外部 > 内部） ──────────────────────────
  const mergedUser = useCallback(() => {
    const base = externalUser || {};
    const profile = profileHook?.profile || {};
    return {
      // DID
      did: base.did || didHook.did,
      // 资料（昵称优先链）
      nickname: base.nickname || profile.nickname,
      displayName: base.displayName || profile.display_name || profile.displayName,
      display_name: base.display_name || profile.display_name,
      username: base.username || profile.username,
      name: base.name || profile.name,
      avatar: base.avatar || profile.avatar_url || profile.avatar,
      avatar_url: base.avatar_url || profile.avatar_url,
      bio: base.bio || profile.bio,
      email: base.email || profile.email,
      verified: base.verified || profile.is_verified,
      // 钱包
      wallet_address: base.wallet_address || walletsHook?.primaryWallet?.address,
      wallets: walletsHook?.wallets || base.wallets || [],
      // 声誉
      reputation_score: base.reputation_score || reputationHook?.score,
      reputation_level: base.reputation_level || reputationHook?.level,
      // 凭证
      credential_count: credentialsHook?.credentials?.length ?? base.credential_count ?? 0,
      credentials: credentialsHook?.credentials || base.credentials || [],
      // 链上状态
      chainStatus: didHook.chainStatus
    };
  }, [externalUser, didHook.did, didHook.chainStatus, profileHook?.profile, walletsHook?.wallets, walletsHook?.primaryWallet, reputationHook?.score, reputationHook?.level, credentialsHook?.credentials]);

  // ─── 聚合 loading ─────────────────────────────────────────
  const loading = didHook.loading || loadProfile && profileHook?.loading || loadReputation && reputationHook?.loading || loadCredentials && credentialsHook?.loading || loadWallets && walletsHook?.loading;
  const error = didHook.error || profileHook?.error || reputationHook?.error || credentialsHook?.error || walletsHook?.error;

  // ─── ready 状态触发 ───────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;
    if (loading) return;

    // 当 DID 已知后视为就绪
    const user = mergedUser();
    if (user.did || externalUser) {
      if (!isReady) {
        setIsReady(true);
        emit(EVENTS.IDENTITY_READY, {
          user
        });
        onReady?.(user);
      } else {
        // 身份变更
        emit(EVENTS.USER_CHANGE, {
          user
        });
        onIdentityChange?.(user);
      }
    }
  }, [loading, isAuthenticated, didHook.did, profileHook?.profile]);

  // ─── 链上状态变化事件 ─────────────────────────────────────
  useEffect(() => {
    const currentStatus = didHook.chainStatus;
    if (currentStatus && currentStatus !== prevChainStatusRef.current) {
      prevChainStatusRef.current = currentStatus;
      emit(EVENTS.CHAIN_STATUS, {
        status: currentStatus
      });
      onChainStatusChange?.(currentStatus);
    }
  }, [didHook.chainStatus]);

  // ─── 错误上报 ─────────────────────────────────────────────
  useEffect(() => {
    if (error) onError?.(error);
  }, [error]);

  // ─── 便捷显示名（昵称优先） ───────────────────────────────
  const displayName = getUserDisplayName(mergedUser());

  // ─── 按需加载触发器 ───────────────────────────────────────
  const loadMore = useCallback(async (capabilities = []) => {
    const tasks = [];
    if (capabilities.includes('profile') && !loadProfile) tasks.push(profileHook?.fetchProfile?.());
    if (capabilities.includes('reputation') && !loadReputation) tasks.push(reputationHook?.fetchReputation?.());
    if (capabilities.includes('credentials') && !loadCredentials) tasks.push(credentialsHook?.fetchCredentials?.());
    if (capabilities.includes('wallets') && !loadWallets) tasks.push(walletsHook?.fetchWallets?.());
    await Promise.allSettled(tasks.filter(Boolean));
  }, [profileHook, reputationHook, credentialsHook, walletsHook]);

  // ─── 刷新全部 ─────────────────────────────────────────────
  const refresh = useCallback(async () => {
    await Promise.allSettled([didHook.fetchMyDID?.(), loadProfile && profileHook?.fetchProfile?.(), loadReputation && reputationHook?.fetchReputation?.(), loadCredentials && credentialsHook?.fetchCredentials?.(), loadWallets && walletsHook?.fetchWallets?.()].filter(Boolean));
  }, [didHook, profileHook, reputationHook, credentialsHook, walletsHook]);

  // ─── 返回聚合对象 ─────────────────────────────────────────
  return {
    // 状态
    isReady,
    loading,
    error,
    isAuthenticated,
    // 聚合用户对象
    user: mergedUser(),
    displayName,
    // 子 hook 直接暴露（高级用户可直接使用）
    did: didHook,
    profile: profileHook,
    reputation: reputationHook,
    credentials: credentialsHook,
    wallets: walletsHook,
    // 便捷直取
    didString: didHook.did,
    chainStatus: didHook.chainStatus,
    reputationScore: reputationHook?.score,
    credentialCount: credentialsHook?.credentials?.length ?? 0,
    primaryWallet: walletsHook?.primaryWallet,
    // 链上操作（委托 useDID）
    verifyOnChain: didHook.verifyOnChain,
    registerOnChain: didHook.registerOnChain,
    updateOnChain: didHook.updateOnChain,
    registerOnChainWithSigner: didHook.registerOnChainWithSigner,
    registerOnChainWithQBitWallet: didHook.registerOnChainWithQBitWallet,
    waitForChainConfirmation: didHook.waitForChainConfirmation,
    // DID 文档操作
    createDID: didHook.createDID,
    resolveDID: didHook.resolveDID,
    resolveDIDFromChain: didHook.resolveDIDFromChain,
    updateDocument: didHook.updateDocument,
    addKey: didHook.addKey,
    removeKey: didHook.removeKey,
    addService: didHook.addService,
    removeService: didHook.removeService,
    // 工具方法
    refresh,
    loadMore,
    // 事件常量（供宿主应用监听）
    EVENTS
  };
}

/**
 * QuantaBit Auth SDK - OAuth/SSO 服务
 * 
 * 支持：
 * - OAuth 2.0 授权流程
 * - SAML SSO
 * - 社交登录 (Google, GitHub, etc.)
 */

const DEFAULT_OAUTH_ENDPOINTS = {
  authorize: '/oauth/authorize',
  token: '/oauth/token',
  userinfo: '/oauth/userinfo',
  revoke: '/oauth/revoke'
};
const DEFAULT_SSO_ENDPOINTS = {
  saml: '/sso/saml',
  callback: '/sso/callback'
};

/**
 * OAuth 服务类
 */
class OAuthService {
  constructor(config = {}) {
    this.baseUrl = config.apiBaseUrl || '';
    this.clientId = config.clientId || '';
    const isBrowser = typeof window !== 'undefined';
    this.redirectUri = config.redirectUri || (isBrowser ? window.location.origin + '/oauth/callback' : '');
    this.endpoints = {
      ...DEFAULT_OAUTH_ENDPOINTS,
      ...config.endpoints
    };
    this.scopes = config.scopes || ['openid', 'profile', 'email'];
    this.state = null;
    this.codeVerifier = null;
  }

  /**
   * 配置 OAuth 服务
   */
  configure(config) {
    if (config.apiBaseUrl) this.baseUrl = config.apiBaseUrl;
    if (config.clientId) this.clientId = config.clientId;
    if (config.redirectUri) this.redirectUri = config.redirectUri;
    if (config.endpoints) this.endpoints = {
      ...this.endpoints,
      ...config.endpoints
    };
    if (config.scopes) this.scopes = config.scopes;
  }

  /**
   * 生成随机字符串
   */
  _generateRandomString(length = 32) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    if (typeof window !== 'undefined' && window.crypto) {
      const values = new Uint8Array(length);
      window.crypto.getRandomValues(values);
      for (let i = 0; i < length; i++) {
        result += chars[values[i] % chars.length];
      }
    } else {
      // Fallback for non-browser env
      for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
    }
    return result;
  }

  /**
   * 生成 PKCE code challenge
   */
  async _generateCodeChallenge(verifier) {
    if (typeof window === 'undefined' || !window.crypto || !window.crypto.subtle) {
      return ''; // SSR fallback
    }
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  /**
   * 开始 OAuth 授权流程
   */
  async startAuthorization(options = {}) {
    const {
      provider = 'qbit-did',
      responseType = 'code',
      usePKCE = true,
      additionalScopes = [],
      state: customState,
      prompt
    } = options;
    this.state = customState || this._generateRandomString();
    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      response_type: responseType,
      scope: [...this.scopes, ...additionalScopes].join(' '),
      state: this.state
    });
    if (provider !== 'qbit-did') {
      params.set('provider', provider);
    }
    if (prompt) {
      params.set('prompt', prompt);
    }

    // PKCE 支持
    if (usePKCE && responseType === 'code') {
      this.codeVerifier = this._generateRandomString(64);
      const codeChallenge = await this._generateCodeChallenge(this.codeVerifier);
      params.set('code_challenge', codeChallenge);
      params.set('code_challenge_method', 'S256');

      // 保存 verifier 到 localStorage
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('qbit_did_oauth_verifier', this.codeVerifier);
      }
    }

    // 保存 state 到 localStorage
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('qbit_did_oauth_state', this.state);
    }
    const authUrl = `${this.baseUrl}${this.endpoints.authorize}?${params.toString()}`;
    return authUrl;
  }

  /**
   * 重定向到授权页面
   */
  async authorize(options = {}) {
    if (typeof window === 'undefined') return;
    const authUrl = await this.startAuthorization(options);
    window.location.href = authUrl;
  }

  /**
   * 在弹窗中进行授权
   */
  async authorizePopup(options = {}) {
    if (typeof window === 'undefined') {
      return Promise.reject(new Error('Window object not available for popup authorization.'));
    }
    const authUrl = await this.startAuthorization(options);
    return new Promise((resolve, reject) => {
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(authUrl, 'oauth_popup', `width=${width},height=${height},left=${left},top=${top}`);
      if (!popup) {
        reject(new Error('无法打开弹窗，请检查是否被浏览器拦截'));
        return;
      }

      // 监听弹窗关闭和消息
      const checkPopup = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkPopup);
          window.removeEventListener('message', messageHandler);
          reject(new Error('用户取消了授权'));
        }
      }, 500);
      const messageHandler = async event => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type === 'oauth_callback') {
          clearInterval(checkPopup);
          window.removeEventListener('message', messageHandler);
          popup.close();
          try {
            const result = await this.handleCallback(event.data.params);
            resolve(result);
          } catch (error) {
            reject(error);
          }
        }
      };
      window.addEventListener('message', messageHandler);
    });
  }

  /**
   * 处理 OAuth 回调
   */
  async handleCallback(params) {
    const {
      code,
      state,
      error,
      error_description
    } = params;
    if (error) {
      throw new Error(error_description || error);
    }

    // 验证 state
    const savedState = localStorage.getItem('qbit_did_oauth_state');
    if (state !== savedState) {
      throw new Error('OAuth state mismatch');
    }

    // 获取 code verifier
    const codeVerifier = localStorage.getItem('qbit_did_oauth_verifier');

    // 交换 token
    const tokenData = await this.exchangeToken(code, codeVerifier);

    // 清理临时存储
    localStorage.removeItem('qbit_did_oauth_state');
    localStorage.removeItem('qbit_did_oauth_verifier');
    return tokenData;
  }

  /**
   * 用授权码交换 token
   */
  async exchangeToken(code, codeVerifier = null) {
    const body = {
      grant_type: 'authorization_code',
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      code
    };
    if (codeVerifier) {
      body.code_verifier = codeVerifier;
    }
    const response = await fetch(`${this.baseUrl}${this.endpoints.token}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Token exchange failed');
    }
    return response.json();
  }

  /**
   * 刷新 token
   */
  async refreshToken(refreshToken) {
    const response = await fetch(`${this.baseUrl}${this.endpoints.token}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        grant_type: 'refresh_token',
        client_id: this.clientId,
        refresh_token: refreshToken
      })
    });
    if (!response.ok) {
      throw new Error('Token refresh failed');
    }
    return response.json();
  }

  /**
   * 获取用户信息
   */
  async getUserInfo(accessToken) {
    const response = await fetch(`${this.baseUrl}${this.endpoints.userinfo}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    if (!response.ok) {
      throw new Error('Failed to get user info');
    }
    return response.json();
  }

  /**
   * 撤销 token
   */
  async revokeToken(token, tokenTypeHint = 'access_token') {
    const response = await fetch(`${this.baseUrl}${this.endpoints.revoke}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        token,
        token_type_hint: tokenTypeHint
      })
    });
    return response.ok;
  }
}

/**
 * SSO 服务类
 */
class SSOService {
  constructor(config = {}) {
    this.baseUrl = config.apiBaseUrl || '';
    this.endpoints = {
      ...DEFAULT_SSO_ENDPOINTS,
      ...config.endpoints
    };
  }

  /**
   * 配置 SSO 服务
   */
  configure(config) {
    if (config.apiBaseUrl) this.baseUrl = config.apiBaseUrl;
    if (config.endpoints) this.endpoints = {
      ...this.endpoints,
      ...config.endpoints
    };
  }

  /**
   * 发起 SAML 登录
   */
  async startSAMLLogin(idpId, returnUrl) {
    if (typeof window === 'undefined') return;
    const finalReturnUrl = returnUrl || window.location.href;
    const params = new URLSearchParams({
      idp: idpId,
      return_url: finalReturnUrl
    });
    const url = `${this.baseUrl}${this.endpoints.saml}?${params.toString()}`;
    window.location.href = url;
  }

  /**
   * 处理 SAML 回调
   */
  async handleSAMLCallback(samlResponse) {
    const response = await fetch(`${this.baseUrl}${this.endpoints.callback}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        saml_response: samlResponse
      })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'SAML authentication failed');
    }
    return response.json();
  }
}

/**
 * 社交登录配置
 */
const SOCIAL_PROVIDERS = {
  google: {
    id: 'google',
    name: 'Google',
    icon: 'google',
    scopes: ['openid', 'profile', 'email']
  },
  github: {
    id: 'github',
    name: 'GitHub',
    icon: 'github',
    scopes: ['read:user', 'user:email']
  },
  discord: {
    id: 'discord',
    name: 'Discord',
    icon: 'discord',
    scopes: ['identify', 'email']
  },
  twitter: {
    id: 'twitter',
    name: 'Twitter',
    icon: 'twitter',
    scopes: ['tweet.read', 'users.read']
  },
  wechat: {
    id: 'wechat',
    name: 'WeChat',
    icon: 'wechat',
    scopes: ['snsapi_userinfo']
  },
  tiktok: {
    id: 'tiktok',
    name: 'TikTok',
    icon: 'tiktok',
    scopes: ['user.info.basic']
  }
};

/**
 * 创建社交登录 URL
 */
function createSocialLoginUrl(provider, baseUrl, clientId, redirectUri) {
  const oauthService = new OAuthService({
    apiBaseUrl: baseUrl,
    clientId,
    redirectUri,
    scopes: SOCIAL_PROVIDERS[provider]?.scopes || ['openid', 'profile']
  });
  return oauthService.startAuthorization({
    provider
  });
}

// 创建默认实例
const oauthService = new OAuthService();
const ssoService = new SSOService();

export { AccountSwitcher, AlertIcon, ArrowRightIcon, AuthApiClient as AuthApiService, AuthProvider, CheckIcon, CloseIcon, CoinbaseIcon, DIDChainStatusCard, DIDProfilePanel, DarkLoginModal, DidWalletConnectButton, ExpandIcon, EyeIcon, EyeOffIcon, FingerprintIcon, FlatDarkLoginPage, FlatDarkRegisterPage, LANGUAGE_INFO, LanguageSwitcher, LayoutIcon, LoaderIcon, LockIcon, LoginModal, LoginPage, LogoutIcon, MailIcon, MaximizeIcon, MetaMaskIcon$1 as MetaMaskIcon, MinimizeIcon, MoonIcon, OAuthService, PaletteIcon, PlusIcon, EVENTS as QBIT_EVENTS, QBitIcon, QbitDIDApiClient, QuickConnectButton, RegisterPage, SOCIAL_PROVIDERS, SSOService, SUPPORTED_LANGUAGES, ShieldIcon, SunIcon, SwapIcon, TrashIcon, UserIcon, VerifiedIcon, WalletConnectIcon$1 as WalletConnectIcon, WalletIcon, Web3WalletPanel, ZapIcon, apiClient, authApi, buildQBitAnchorMemo$1 as buildQBitAnchorMemo, clearCurrentSession, completeExternalChainOperation$1 as completeExternalChainOperation, createQBitMemoAnchorBroadcaster$1 as createQBitMemoAnchorBroadcaster, createSocialLoginUrl, detectBrowserLanguage, formatAddress, formatDID$1 as formatDID, getCurrentSession, getEthereumProvider, getLanguage, getMessages$1 as getMessages, getSavedAccounts, getSupportedLanguages, getUserDisplayName, hasMetaMask, initLanguage, messages, oauthService, removeAccount, saveAccount, setCurrentDID, setLanguage, ssoService, t, useAccounts, useAuth, useCredentials, useDID, useLogin, useProfile, useQBitIdentity, useReputation, useWallets, verifyAnchoredMemo, waitForQBitConfirmation };
//# sourceMappingURL=index.esm.js.map
